import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../hooks/useSocket';
import api from '../../services/api';
import { getCoupons, updateCoupon } from '../../services/couponApi';
import { getDoctorAvailabilityForecast } from '../../services/doctorAvailabilityApi';
import { createPrescription, updatePrescription, deletePrescription, verifyPrescription, sendPrescriptionToLead, sendPrescriptionFormToDoctor, submitPrescription, downloadPrescriptionPDF } from '../../services/prescriptionApi';
import { getAppointmentPayouts, createAppointmentPayout, completeAppointmentPayout, sendAppointmentPayoutEmail, createManualAppointmentPayout, recordOfflinePayment } from '../../services/appointmentApi';
import toast from 'react-hot-toast';
import {
  Phone, Calendar, CalendarClock, Clock, UserPlus, Stethoscope, Building2, ArrowLeft, CheckCircle,
  XCircle, Loader2, History, FileText, RefreshCw, Trash2, ShieldCheck, HelpCircle,
  Share2, CheckCircle2, Tag, Pill, Plus, Save, AlertCircle, X, SendHorizontal, Send,
  ClipboardList, Stethoscope as StethoscopeIcon, Download, Edit, ChevronRight, Wallet, Banknote,
  ExternalLink, ArrowRight, User, Copy, Check, HeartPulse, AlertTriangle, Shield,
  UserCheck, Droplet, MapPin, Printer, Receipt, Sparkles, Key, Mail, CreditCard,
  Upload, Image, BadgeCheck, FileCheck, Heart, Paperclip, Eye, Search, ChevronLeft,
  TestTubes, Lightbulb, MessageSquare, Video, PhoneCall, PhoneMissed, PhoneOff, PhoneIncoming
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { FREQUENCY_OPTIONS, TYPE_OPTIONS, INSTRUCTIONS_OPTIONS, UNIT_OPTIONS, DURATION_UNIT_OPTIONS } from '../../constants/prescriptionConstants';
import CallHistoryPanel, { getEffectiveDisposition, getLightStatusBadge, getLightDispositionBadge } from '../leads/components/CallHistoryPanel';
import { getCallLogs, initiateCall as exotelInitiateCall, saveCallDisposition } from '../../services/exotelApi';
import EditPatientModal from '../../Components/common/EditPatientModal';


const STATUS_BADGES = {
  draft: 'bg-slate-100 text-slate-700 border-slate-200',
  pending_payment: 'bg-amber-50 text-amber-700 border-amber-200',
  confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  completed: 'bg-blue-50 text-blue-700 border-blue-200',
  cancelled: 'bg-rose-50 text-rose-700 border-rose-200',
  lost: 'bg-orange-50 text-orange-700 border-orange-200',
  overridden: 'bg-purple-50 text-purple-700 border-purple-200',
};

const APPOINTMENT_TYPES = [
  { value: 'consultation', label: 'Online Consultation', desc: 'General medical consultation' },
  { value: 'pre_surgery_consult', label: 'Pre-Surgery Consult', desc: 'Review before surgical procedure' },
  { value: 'follow_up', label: 'Follow-Up', desc: 'Post-treatment follow-up visit' },
];

const APPOINTMENT_TYPE_COLORS = {
  consultation: 'bg-blue-50 text-blue-700 border-blue-200',
  pre_surgery_consult: 'bg-amber-50 text-amber-700 border-amber-200',
  follow_up: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const COMMUNICATION_MODES = [
  { value: 'in_person', label: 'In Person', desc: 'Visit clinic/hospital' },
  { value: 'video_call', label: 'Video Call', desc: 'Online video consultation' },
  { value: 'phone_call', label: 'Phone Call', desc: 'Telephonic consultation' },
];

const COMMUNICATION_MODE_COLORS = {
  in_person: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  video_call: 'bg-purple-50 text-purple-700 border-purple-200',
  phone_call: 'bg-slate-50 text-slate-700 border-slate-200',
};

const COMMUNICATION_MODE_CONFIG = {
  in_person: { label: 'In-Person Clinic Visit', icon: Building2, color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  video_call: { label: 'Online Video Consult', icon: Video, color: 'bg-purple-50 text-purple-700 border-purple-200' },
  video: { label: 'Online Video Consult', icon: Video, color: 'bg-purple-50 text-purple-700 border-purple-200' },
  phone_call: { label: 'Telephonic Consult', icon: Phone, color: 'bg-slate-50 text-slate-700 border-slate-200' },
  phone: { label: 'Telephonic Consult', icon: Phone, color: 'bg-slate-50 text-slate-700 border-slate-200' },
  audio: { label: 'Telephonic Consult', icon: Phone, color: 'bg-slate-50 text-slate-700 border-slate-200' },
  chat: { label: 'Chat / WhatsApp Consult', icon: MessageSquare, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})}`;

const parseDateInput = (value) => {
  if (!value) return '';
  const [day, month, year] = value.split('-');
  if (day && month && year && day.length === 2 && month.length === 2 && year.length === 4) {
    return `${year}-${month}-${day}`;
  }
  return value;
};

const formatTime12Hour = (time24) => {
  if (!time24) return '';
  if (typeof time24 !== 'string') return '';
  if (time24.includes('AM') || time24.includes('PM')) return time24;
  const [hoursStr, minutesStr] = time24.split(':');
  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);
  if (isNaN(hours) || isNaN(minutes)) return time24;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${String(displayHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${period}`;
};

const getTimeParts = (timeStr) => {
  if (!timeStr) return { hour24: 10, minute: '00', hour12: 10, period: 'AM', raw24: '10:00', display12: '10:00 AM' };
  let h = 10;
  let m = 0;
  let period = 'AM';

  if (typeof timeStr === 'string') {
    if (timeStr.includes('AM') || timeStr.includes('PM')) {
      const isPM = /pm/i.test(timeStr);
      const clean = timeStr.replace(/AM|PM/i, '').trim();
      const [hStr, mStr] = clean.split(':');
      let parsedH = parseInt(hStr, 10) || 12;
      m = parseInt(mStr, 10) || 0;
      period = isPM ? 'PM' : 'AM';
      if (isPM && parsedH < 12) h = parsedH + 12;
      else if (!isPM && parsedH === 12) h = 0;
      else h = parsedH;
    } else if (timeStr.includes(':')) {
      const [hStr, mStr] = timeStr.split(':');
      h = parseInt(hStr, 10) || 0;
      m = parseInt(mStr, 10) || 0;
      period = h >= 12 ? 'PM' : 'AM';
    }
  }

  const hour12 = h % 12 || 12;
  const minuteStr = String(m).padStart(2, '0');
  const hour24Str = String(h).padStart(2, '0');

  return {
    hour24: h,
    minute: minuteStr,
    hour12,
    period,
    raw24: `${hour24Str}:${minuteStr}`,
    display12: `${String(hour12).padStart(2, '0')}:${minuteStr} ${period}`
  };
};

const parseTimeSlotTo24H = (timeSlot) => {
  if (!timeSlot || typeof timeSlot !== 'string') return { hour: '00', minute: '00' };

  // Take first part if range like "10:00 AM - 11:00 AM" or "10:00-11:00"
  const startPart = timeSlot.split('-')[0].trim();
  const isPM = /pm/i.test(startPart);
  const isAM = /am/i.test(startPart);

  // Extract clean digits for hour and minute
  const digitsMatch = startPart.match(/(\d{1,2}):(\d{1,2})/);
  if (!digitsMatch) {
    const hourMatch = startPart.match(/(\d{1,2})/);
    if (!hourMatch) return { hour: '00', minute: '00' };
    let h = parseInt(hourMatch[1], 10);
    if (isPM && h < 12) h += 12;
    if (isAM && h === 12) h = 0;
    return { hour: String(h).padStart(2, '0'), minute: '00' };
  }

  let h = parseInt(digitsMatch[1], 10);
  const m = parseInt(digitsMatch[2], 10) || 0;

  if (isPM && h < 12) h += 12;
  if (isAM && h === 12) h = 0;

  return {
    hour: String(h).padStart(2, '0'),
    minute: String(m).padStart(2, '0')
  };
};

const toValidISODate = (rawDate) => {
  if (!rawDate) return undefined;
  let yyyy, mm, dd;

  if (typeof rawDate === 'string') {
    const trimmed = rawDate.trim();
    const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      yyyy = isoMatch[1];
      mm = isoMatch[2];
      dd = isoMatch[3];
    } else {
      const dmyMatch = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})/);
      if (dmyMatch) {
        dd = dmyMatch[1];
        mm = dmyMatch[2];
        yyyy = dmyMatch[3];
      }
    }
  } else if (rawDate instanceof Date && !isNaN(rawDate.getTime())) {
    yyyy = rawDate.getUTCFullYear();
    mm = String(rawDate.getUTCMonth() + 1).padStart(2, '0');
    dd = String(rawDate.getUTCDate()).padStart(2, '0');
  }

  if (!yyyy || !mm || !dd) return undefined;
  return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}T00:00:00.000Z`;
};

const formatWithHyphens = (value) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 8)}`;
};

const calculateFromTotal = (totalInvoice, platformCharges, otherCharges, discountPercentage, taxType, taxPercentage) => {
  const total = Number(totalInvoice || 0);
  const platform = Number(platformCharges || 0);
  const other = Number(otherCharges || 0);
  const discount = Number(discountPercentage || 0);
  const taxPct = taxType !== 'none' ? Number(taxPercentage || 0) : 0;

  const discountAmount = platform > 0 && discount > 0 ? Math.round((platform * discount) / 100) : 0;
  const discountedPlatform = Math.max(0, platform - discountAmount);

  const taxAmount = Math.round((total - (total / (1 + taxPct / 100))) * 100) / 100;
  const baseAmount = Math.round((total - taxAmount) * 100) / 100;

  const consultationFee = Math.max(0, Math.round((baseAmount - discountedPlatform - other) * 100) / 100);

  return {
    doctorFee: consultationFee,
    platformFee: discountedPlatform,
    otherCharges: other,
    finalPrice: total,
    taxAmount,
    subtotal: baseAmount,
    netAmount: baseAmount
  };
};

const getPayoutMetrics = (payout) => {
  if (!payout) return { gross: 0, tds: 0, net: 0, tdsPct: 10 };
  const rawGross = payout.grossAmount !== undefined && payout.grossAmount !== null && Number(payout.grossAmount) > 0 ? Number(payout.grossAmount) : null;
  const rawTds = payout.tdsAmount !== undefined && payout.tdsAmount !== null ? Number(payout.tdsAmount) : null;
  const rawNet = payout.amount !== undefined && payout.amount !== null ? Number(payout.amount) : null;
  const tdsPct = Number(payout.tdsPercentage || 10);

  let gross = 0;
  let tds = 0;
  let net = 0;

  if (rawGross !== null) {
    gross = rawGross;
    tds = rawTds !== null ? rawTds : Math.round((gross * tdsPct) / 100);
    net = rawNet !== null ? rawNet : (gross - tds);
  } else if (rawNet !== null && rawNet > 0) {
    net = rawNet;
    gross = Math.round(net / (1 - (tdsPct / 100)));
    tds = gross - net;
  }

  return { gross, tds, net, tdsPct };
};

const VALID_PIPELINE_STAGES = ['lead', 'clinical', 'doctor_type', 'schedule', 'payment', 'confirm'];

const AppointmentDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { socket } = useSocket() || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab') || searchParams.get('step');
  const [activeTab, setActiveTabState] = useState(() => {
    return VALID_PIPELINE_STAGES.includes(urlTab) ? urlTab : 'clinical';
  });

  const setActiveTab = useCallback((newTab) => {
    setActiveTabState(newTab);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', newTab);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Synchronize when browser history / query string changes
  useEffect(() => {
    const currentTab = searchParams.get('tab') || searchParams.get('step');
    if (currentTab && VALID_PIPELINE_STAGES.includes(currentTab) && currentTab !== activeTab) {
      setActiveTabState(currentTab);
    }
  }, [searchParams, activeTab]);

  const [appointment, setAppointment] = useState(null);
  const isPaymentLinkGenerated = Boolean(
    appointment?.razorpayDetails?.paymentLinkId ||
    appointment?.razorpayDetails?.shortUrl ||
    appointment?.paymentStatus === 'paid' ||
    ['pending_payment', 'confirmed', 'completed', 'lost', 'cancelled'].includes(appointment?.status)
  );
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showLostModal, setShowLostModal] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [lostReason, setLostReason] = useState('');
  const [overrideAction, setOverrideAction] = useState('');
  const [overrideDate, setOverrideDate] = useState('');
  const [overrideTimeSlot, setOverrideTimeSlot] = useState('');
  const [overrideDoctorId, setOverrideDoctorId] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showCopied, setShowCopied] = useState(false);
  const [showPrescriptionDeleteModal, setShowPrescriptionDeleteModal] = useState(false);
  const currentAppointmentIdRef = useRef(id);

  // 📞 Call Tracking & Disposition State
  const [latestCall, setLatestCall] = useState(null);
  const [callLogs, setCallLogs] = useState([]);
  const [callHistoryRefresh, setCallHistoryRefresh] = useState(0);
  const [isCallingPatient, setIsCallingPatient] = useState(false);
  const [showQuickDispModal, setShowQuickDispModal] = useState(false);
  const [showEditPatientModal, setShowEditPatientModal] = useState(false);
  const canEditPatient = user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head' || user?.role === 'accountant';
  const [showFullCallHistory, setShowFullCallHistory] = useState(false);
  const [quickDispForm, setQuickDispForm] = useState({
    disposition: 'callback_completed',
    dispositionNotes: '',
    callReason: ''
  });
  const [isSavingQuickDisp, setIsSavingQuickDisp] = useState(false);

  const targetLeadId = appointment?.leadId?._id || (typeof appointment?.leadId === 'string' ? appointment?.leadId : null) || appointment?._id || id;

  useEffect(() => {
    if (targetLeadId) {
      getCallLogs(targetLeadId, 50)
        .then(logs => {
          setCallLogs(logs || []);
          if (logs && logs.length > 0) setLatestCall(logs[0]);
          else setLatestCall(null);
        })
        .catch(() => { });
    }
  }, [targetLeadId, callHistoryRefresh]);

  const handleExotelCall = async () => {
    const phoneToCall = appointment?.patientPhone || appointment?.leadId?.patientPhone;
    if (!phoneToCall) return toast.error('No patient phone number available.');
    if (!user?.phone) return toast.error('Your profile has no phone number. Update your profile to make calls.');
    setIsCallingPatient(true);
    try {
      const result = await exotelInitiateCall(targetLeadId, phoneToCall);
      if (result.success) {
        toast.success('📞 Your phone will ring first — then connect to the patient!', { duration: 6000 });
        setShowFullCallHistory(true);
        setTimeout(() => setCallHistoryRefresh(prev => prev + 1), 5000);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to initiate call. Check Exotel configuration.');
    } finally {
      setIsCallingPatient(false);
    }
  };

  const handleOpenQuickDispModal = (logToUpdate = null) => {
    const log = logToUpdate || latestCall;
    if (!log) return;
    const eff = getEffectiveDisposition(log);
    setQuickDispForm({
      disposition: eff === 'pending' ? 'callback_completed' : eff,
      dispositionNotes: log.dispositionNotes || log.notes || '',
      callReason: log.callReason || ''
    });
    setShowQuickDispModal(true);
  };

  const handleSaveQuickDisp = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!latestCall?._id) return;
    setIsSavingQuickDisp(true);
    try {
      const res = await saveCallDisposition(latestCall._id, {
        disposition: quickDispForm.disposition,
        dispositionNotes: quickDispForm.dispositionNotes.trim(),
        notes: quickDispForm.dispositionNotes.trim(),
        callReason: quickDispForm.callReason.trim()
      });
      const updated = res?.data || {
        ...latestCall,
        disposition: quickDispForm.disposition,
        dispositionNotes: quickDispForm.dispositionNotes.trim(),
        notes: quickDispForm.dispositionNotes.trim(),
        callReason: quickDispForm.callReason.trim(),
        dispositionUpdatedAt: new Date().toISOString()
      };
      setLatestCall(updated);
      setCallLogs(prev => prev.map(l => l._id === latestCall._id ? { ...l, ...updated } : l));
      setCallHistoryRefresh(prev => prev + 1);
      toast.success('Follow-up status updated');
      setShowQuickDispModal(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update status');
    } finally {
      setIsSavingQuickDisp(false);
    }
  };

  const hasInitializedTabRef = useRef(false);

  const isPastDate = (dateStr) => {
    if (!dateStr) return false;
    let y, m, d;
    if (typeof dateStr === 'string') {
      const match = dateStr.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        y = parseInt(match[1], 10);
        m = parseInt(match[2], 10) - 1;
        d = parseInt(match[3], 10);
      } else {
        const dmy = dateStr.trim().match(/^(\d{2})-(\d{2})-(\d{4})/);
        if (dmy) {
          d = parseInt(dmy[1], 10);
          m = parseInt(dmy[2], 10) - 1;
          y = parseInt(dmy[3], 10);
        }
      }
    }
    if (y === undefined) {
      const dateObj = new Date(dateStr);
      if (isNaN(dateObj.getTime())) return false;
      y = dateObj.getUTCFullYear();
      m = dateObj.getUTCMonth();
      d = dateObj.getUTCDate();
    }
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfTarget = new Date(y, m, d).getTime();
    return startOfTarget < startOfToday;
  };

  // 📝 Interactive Stage 2 Clinical Info Form state
  const [clinicalInfo, setClinicalInfo] = useState({
    chiefComplaints: '',
    pastMedicalHistory: '',
    comorbidities: ''
  });
  const [coverageMode, setCoverageMode] = useState('cash');
  const [idProof, setIdProof] = useState(null);
  const [ayushmanCard, setAyushmanCard] = useState(null);
  const [medicalReports, setMedicalReports] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [isDraggingReports, setIsDraggingReports] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [savingStage, setSavingStage] = useState(false);

  // 👨‍⚕️ Interactive Stage 3 Doctor Assignment & Coupon State (matching AppointmentCreate)
  const [doctorType, setDoctorType] = useState('coupon');
  const [doctorConsultationFee, setDoctorConsultationFee] = useState(0);
  const [coupons, setCoupons] = useState([]);
  const [selectedCouponId, setSelectedCouponId] = useState('');
  const [couponOriginal, setCouponOriginal] = useState(null);
  const [availabilityDate, setAvailabilityDate] = useState('');
  const [availabilityDoctorType, setAvailabilityDoctorType] = useState('');
  const [forecast, setForecast] = useState([]);
  const [availableDoctors, setAvailableDoctors] = useState([]);
  const [loadingForecast, setLoadingForecast] = useState(false);
  const availabilityDateRef = useRef(null);

  const [formData, setFormData] = useState({
    appointmentType: 'pre_surgery_consult',
    communicationMode: 'in_person',
    appointmentDate: '',
    appointmentTimeSlot: '',
    appointmentDoctorId: ''
  });

  const [pricing, setPricing] = useState({
    doctorFee: 847.46,
    platformFee: 0,
    otherCharges: 0,
    discountPercentage: 0,
    finalPrice: 1000,
    taxType: 'cgst_sgst',
    taxPercentage: 18,
    taxName: '',
    taxAmount: 152.54,
    netAmount: 847.46,
    editing: false
  });

  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);
  const [editingPrescription, setEditingPrescription] = useState(null);
  const [showAdvice, setShowAdvice] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [prescriptionForm, setPrescriptionForm] = useState({
    chiefComplaints: '',
    pastMedicalHistory: '',
    notes: '',
    advice: '',
    provisionalDiagnosis: '',
    followUp: '',
    prescriptionItems: [{ medicineName: '', strength: '', unit: 'mg', type: '', frequency: '', duration: '', durationUnit: 'days', instructions: '', isCritical: false }],
    investigations: []
  });
  const [prescriptionErrors, setPrescriptionErrors] = useState({});
  const [savingPrescription, setSavingPrescription] = useState(false);
  const [customFrequencyItems, setCustomFrequencyItems] = useState({});
  const [customTypeItems, setCustomTypeItems] = useState({});
  const [customInstructionsItems, setCustomInstructionsItems] = useState({});
  const [customUnitItems, setCustomUnitItems] = useState({});
  const [showPrescriptionActionModal, setShowPrescriptionActionModal] = useState(false);
  const [prescriptionActionType, setPrescriptionActionType] = useState('approve');
  const [prescriptionActionNotes, setPrescriptionActionNotes] = useState('');
  const [prescriptionRejectionReason, setPrescriptionRejectionReason] = useState('');
  const [prescriptionActionLoading, setPrescriptionActionLoading] = useState(false);
  const [sendingFormToDoctor, setSendingFormToDoctor] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  const [showEditAppointmentModal, setShowEditAppointmentModal] = useState(false);
  const [updatingAppointment, setUpdatingAppointment] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreNotes, setRestoreNotes] = useState('');
  const [editAppointmentForm, setEditAppointmentForm] = useState({
    appointmentDate: '',
    appointmentTimeSlot: '',
    appointmentDoctorId: '',
    notes: ''
  });

  const [appointmentPayouts, setAppointmentPayouts] = useState([]);
  const [loadingPayouts, setLoadingPayouts] = useState(false);
  const [completingPayoutId, setCompletingPayoutId] = useState(null);
  const [generatingPayoutId, setGeneratingPayoutId] = useState(null);
  const [sendingPayoutId, setSendingPayoutId] = useState(null);

  const [showManualPayoutModal, setShowManualPayoutModal] = useState(false);
  const [manualPayoutForm, setManualPayoutForm] = useState({
    grossAmount: '',
    tdsPercentage: '10',
    netAmount: '',
    transactionId: '',
    transactionDate: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [creatingManualPayout, setCreatingManualPayout] = useState(false);

  const [showPaymentLinkModal, setShowPaymentLinkModal] = useState(false);
  const [paymentLinkEmail, setPaymentLinkEmail] = useState('');
  const [generatingPaymentLink, setGeneratingPaymentLink] = useState(false);

  const openPaymentLinkModal = () => {
    setPaymentLinkEmail(appointment?.patientEmail || appointment?.leadId?.patientEmail || '');
    setShowPaymentLinkModal(true);
  };

  const handleGenerateOrResendPaymentLink = async () => {
    setGeneratingPaymentLink(true);
    try {
      await api.post(`/appointments/${id}/payment-link`, {
        patientEmail: paymentLinkEmail.trim(),
        forceRegenerate: true
      });
      toast.success('Payment link generated & sent successfully');
      setShowPaymentLinkModal(false);
      fetchAppointment();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate payment link');
    } finally {
      setGeneratingPaymentLink(false);
    }
  };

  // 💳 Offline Payment & Receipt Upload State
  const [paymentModeTab, setPaymentModeTab] = useState('online');
  const [offlinePaymentForm, setOfflinePaymentForm] = useState({
    paymentMethod: 'cash',
    transactionReference: '',
    amount: '',
    isAutoAmount: true,
    receiptUrl: '',
    receiptFilename: '',
    receiptSize: 0,
    notes: ''
  });

  // Automatically reflect and synchronize final payable amount
  useEffect(() => {
    const activeAmount = (pricing.finalPrice !== undefined && pricing.finalPrice !== null && pricing.finalPrice !== '')
      ? pricing.finalPrice
      : (appointment?.finalPrice ?? '');

    setOfflinePaymentForm(prev => {
      if (prev.amount === '' || prev.isAutoAmount || prev.amount === undefined) {
        return { ...prev, amount: String(activeAmount), isAutoAmount: true };
      }
      return prev;
    });
  }, [pricing.finalPrice, appointment?.finalPrice]);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [recordingOfflinePayment, setRecordingOfflinePayment] = useState(false);

  const handleReceiptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Receipt file size must be less than 10MB');
      return;
    }

    setUploadingReceipt(true);
    try {
      const data = new FormData();
      data.append('documents', file);
      const res = await api.post('/upload', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const uploaded = res.data?.data?.files?.[0];
      if (uploaded?.url) {
        setOfflinePaymentForm(prev => ({
          ...prev,
          receiptUrl: uploaded.url,
          receiptFilename: uploaded.filename || file.name,
          receiptSize: uploaded.size || file.size
        }));
        toast.success('Payment receipt uploaded successfully');
      } else {
        toast.error('Failed to get uploaded receipt URL');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to upload receipt file');
    } finally {
      setUploadingReceipt(false);
    }
  };

  const handleRecordOfflinePayment = async () => {
    if (!id) return;
    setRecordingOfflinePayment(true);
    try {
      await recordOfflinePayment(id, {
        paymentMethod: offlinePaymentForm.paymentMethod,
        transactionReference: offlinePaymentForm.transactionReference,
        amount: Number(offlinePaymentForm.amount) || appointment?.finalPrice || 0,
        receiptUrl: offlinePaymentForm.receiptUrl,
        receiptFilename: offlinePaymentForm.receiptFilename,
        receiptSize: offlinePaymentForm.receiptSize,
        notes: offlinePaymentForm.notes
      });
      toast.success('Offline payment recorded and appointment confirmed successfully!');
      await fetchAppointment();
      setActiveTab('confirm');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to record offline payment');
    } finally {
      setRecordingOfflinePayment(false);
    }
  };

  const TIME_SLOTS = [
    { value: '09:00-10:00', label: '09:00 AM - 10:00 AM' },
    { value: '10:00-11:00', label: '10:00 AM - 11:00 AM' },
    { value: '11:00-12:00', label: '11:00 AM - 12:00 PM' },
    { value: '12:00-13:00', label: '12:00 PM - 01:00 PM' },
    { value: '14:00-15:00', label: '02:00 PM - 03:00 PM' },
    { value: '15:00-16:00', label: '03:00 PM - 04:00 PM' },
    { value: '16:00-17:00', label: '04:00 PM - 05:00 PM' },
    { value: '17:00-18:00', label: '05:00 PM - 06:00 PM' },
  ];

  const formatTime12Hour = (time24) => {
    if (!time24 || typeof time24 !== 'string') return 'N/A';
    if (time24.includes('AM') || time24.includes('PM')) return time24;
    if (time24.includes('-')) {
      const parts = time24.split('-');
      return parts.map(p => formatTime12Hour(p.trim())).join(' - ');
    }
    const [hours, minutes] = time24.split(':').map(Number);
    if (isNaN(hours)) return time24;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const minStr = isNaN(minutes) ? '00' : String(minutes).padStart(2, '0');
    return `${String(displayHours).padStart(2, '0')}:${minStr} ${period}`;
  };

  const formatDateDisplay = (date) => {
    if (!date) return 'N/A';
    if (typeof date === 'string') {
      const trimmed = date.trim();
      if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) return trimmed;
      const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) return `${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}`;
    }
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return 'N/A';
    const day = String(d.getUTCDate()).padStart(2, '0');
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const year = d.getUTCFullYear();
    return `${day}-${month}-${year}`;
  };

  const copyBookingId = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedId(true);
    toast.success('Booking ID copied to clipboard!');
    setTimeout(() => setCopiedId(false), 2000);
  };

  const copyPhoneNumber = (phone) => {
    if (!phone) return;
    navigator.clipboard.writeText(phone);
    setCopiedPhone(true);
    toast.success('Phone number copied to clipboard!');
    setTimeout(() => setCopiedPhone(false), 2000);
  };

  useEffect(() => {
    fetchAppointment();
    fetchAppointmentPayouts();
  }, [id]);

  useEffect(() => {
    currentAppointmentIdRef.current = id;
  }, [id]);

  // ⚡ Real-time live reflection: instant refresh when doctor submits prescription or appointment changes
  useEffect(() => {
    if (!socket || !id) return;

    const handleRealTimePrescriptionOrAppointment = (data) => {
      const targetAptId = data?.appointmentId || data?.entityId || data?.id;
      const targetLeadId = data?.leadId;
      const currentLeadId = typeof appointment?.leadId === 'object' ? appointment?.leadId?._id : appointment?.leadId;

      if (
        (targetAptId && String(targetAptId) === String(id)) ||
        (targetLeadId && currentLeadId && String(targetLeadId) === String(currentLeadId)) ||
        (data?.prescription?.appointmentId && String(data.prescription.appointmentId) === String(id)) ||
        data?.action === 'prescription_form_submitted' ||
        data?.action === 'prescription_submitted'
      ) {
        console.log('[AppointmentDetail] Realtime prescription/appointment update received:', data);
        fetchAppointment();
      }
    };

    socket.on('prescription_updated', handleRealTimePrescriptionOrAppointment);
    socket.on('appointment_updated', handleRealTimePrescriptionOrAppointment);
    socket.on('lead_updated', handleRealTimePrescriptionOrAppointment);
    socket.on('system_activity', handleRealTimePrescriptionOrAppointment);

    return () => {
      socket.off('prescription_updated', handleRealTimePrescriptionOrAppointment);
      socket.off('appointment_updated', handleRealTimePrescriptionOrAppointment);
      socket.off('lead_updated', handleRealTimePrescriptionOrAppointment);
      socket.off('system_activity', handleRealTimePrescriptionOrAppointment);
    };
  }, [socket, id, appointment?.leadId]);

  // 🔄 Also refresh when tab becomes visible or receives focus
  useEffect(() => {
    const handleFocus = () => {
      if (document.visibilityState === 'visible' && id) {
        fetchAppointment();
      }
    };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleFocus);
    };
  }, [id]);

  useEffect(() => {
    fetchDoctors();
    fetchCouponsList();
  }, []);

  const fetchCouponsList = async () => {
    try {
      const res = await getCoupons({ isActive: true });
      const rawCoupons = res?.data?.coupons || res?.data || [];
      setCoupons(Array.isArray(rawCoupons) ? rawCoupons : []);
    } catch (e) {
      console.error('Failed to fetch coupons:', e);
    }
  };

  useEffect(() => {
    const fetchDoctorFee = async () => {
      if (!formData.appointmentDoctorId) return;
      try {
        const res = await api.get(`/doctors/${formData.appointmentDoctorId}`);
        const doctorData = res.data?.data;
        const fee = Number(doctorData?.financials?.consultationFee || 0);
        setDoctorConsultationFee(fee);
        if (doctorType !== 'coupon' || !selectedCouponId) {
          setPricing(prev => ({
            ...prev,
            doctorFee: fee
          }));
        }
      } catch {
        // keep existing fee
      }
    };
    fetchDoctorFee();
  }, [formData.appointmentDoctorId, doctorType, selectedCouponId]);

  useEffect(() => {
    if (!availabilityDate) {
      setAvailableDoctors([]);
      setForecast([]);
      return;
    }
    const fetchForecast = async () => {
      setLoadingForecast(true);
      try {
        const response = await getDoctorAvailabilityForecast({
          date: availabilityDate,
          doctorType: availabilityDoctorType || undefined
        });
        const forecastData = response.data || [];
        setForecast(forecastData);
        const available = forecastData.filter(item => item.isAvailableOnTargetDate && !item.needsRosterUpdate);
        setAvailableDoctors(available);
      } catch (error) {
        setForecast([]);
        setAvailableDoctors([]);
      } finally {
        setLoadingForecast(false);
      }
    };
    fetchForecast();
  }, [availabilityDate, availabilityDoctorType]);

  // 💾 Automatically persist any field selections / form entries to localStorage draft
  useEffect(() => {
    if (!id || loading) return;
    try {
      const draftData = {
        clinicalInfo,
        coverageMode,
        idProof,
        ayushmanCard,
        medicalReports,
        formData,
        doctorType,
        selectedCouponId,
        pricing,
        availabilityDate,
        availabilityDoctorType,
        activeTab,
        timestamp: Date.now()
      };
      localStorage.setItem(`doxez_apt_draft_${id}`, JSON.stringify(draftData));
    } catch (e) {
      console.warn('Failed to save appointment draft to localStorage', e);
    }
  }, [
    id,
    loading,
    clinicalInfo,
    coverageMode,
    idProof,
    ayushmanCard,
    medicalReports,
    formData,
    doctorType,
    selectedCouponId,
    pricing,
    availabilityDate,
    availabilityDoctorType,
    activeTab
  ]);

  useEffect(() => {
    if (!pricing.editing) return;
    const pricingResult = calculateFromTotal(pricing.finalPrice, pricing.platformFee, pricing.otherCharges, pricing.discountPercentage, pricing.taxType, pricing.taxPercentage);
    setPricing(prev => ({ ...prev, ...pricingResult }));
  }, [pricing.platformFee, pricing.otherCharges, pricing.discountPercentage, pricing.taxType, pricing.taxPercentage, pricing.editing]);

  useEffect(() => {
    if (appointment && (user?.role === 'admin' || user?.role === 'accountant' || user?.role === 'customer_care' || user?.role === 'cc_head')) {
      fetchAppointmentPayouts();
    }
  }, [appointment?._id, user?.role]);

  const fetchDoctors = async () => {
    try {
      const res = await api.get('/doctors?limit=1000');
      const rawDoctors = res.data?.data?.doctors || res.data?.doctors || res.data?.data || res.data || [];
      setDoctors(Array.isArray(rawDoctors) ? rawDoctors : []);
    } catch (error) {
      console.error('Failed to load doctors');
    }
  };

  const openEditAppointmentModal = () => {
    setEditAppointmentForm({
      appointmentDate: appointment.appointmentDate ? (typeof appointment.appointmentDate === 'string' ? appointment.appointmentDate.slice(0, 10) : new Date(appointment.appointmentDate).toISOString().split('T')[0]) : '',
      appointmentTimeSlot: appointment.appointmentTimeSlot || '',
      appointmentDoctorId: typeof appointment.appointmentDoctorId === 'object'
        ? (appointment.appointmentDoctorId?._id || '')
        : (appointment.appointmentDoctorId || ''),
      notes: appointment.notes || ''
    });
    setShowEditAppointmentModal(true);
  };

  const handleUpdateAppointment = async () => {
    setUpdatingAppointment(true);
    try {
      const date = editAppointmentForm.appointmentDate;
      const isoDate = toValidISODate(date);

      const payload = {
        appointmentDate: isoDate,
        appointmentTimeSlot: editAppointmentForm.appointmentTimeSlot || undefined,
        appointmentDoctorId: editAppointmentForm.appointmentDoctorId || null,
        notes: editAppointmentForm.notes || undefined
      };

      const res = await api.patch(`/appointments/${appointment._id}`, payload);
      if (res.data?.success) {
        setAppointment(res.data.data);
        toast.success('Appointment updated successfully');
        setShowEditAppointmentModal(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update appointment');
    } finally {
      setUpdatingAppointment(false);
    }
  };

  const fetchAppointment = async () => {
    try {
      const response = await api.get(`/appointments/${id}`);
      if (response.data?.success) {
        const apt = response.data.data;
        setAppointment(apt);

        // Check if there is a saved local draft for this appointment
        let draft = null;
        try {
          const rawDraft = localStorage.getItem(`doxez_apt_draft_${id}`);
          if (rawDraft) {
            draft = JSON.parse(rawDraft);
          }
        } catch (e) {
          console.warn('Failed to read appointment draft', e);
        }

        const lead = typeof apt.leadId === 'object' ? apt.leadId : null;

        // 1. Clinical Info (Prefer local draft, fallback to lead / apt)
        const defaultClinical = {
          chiefComplaints: lead?.chiefComplaints || apt.notes || '',
          pastMedicalHistory: lead?.pastMedicalHistory || '',
          comorbidities: lead?.comorbidities || ''
        };
        setClinicalInfo(draft?.clinicalInfo || defaultClinical);

        // 2. Coverage Mode
        const defaultCoverage = lead?.coverageMode || (lead?.hasAyushmanCard ? 'ayushman' : (lead?.paymentType?.includes('Insurance') ? 'insurance' : 'cash'));
        setCoverageMode(draft?.coverageMode || defaultCoverage);

        // 3. ID Proof & Ayushman Card
        const idDoc = (lead?.documents || []).find(d => d.type === 'id_proof');
        setIdProof(draft?.idProof !== undefined ? draft.idProof : (idDoc ? { url: idDoc.url, name: idDoc.name } : null));

        const aDoc = (lead?.documents || []).find(d => d.type === 'ayushman_card');
        setAyushmanCard(draft?.ayushmanCard !== undefined ? draft.ayushmanCard : (aDoc ? { url: aDoc.url, name: aDoc.name } : null));

        // 4. Medical Reports
        if (draft?.medicalReports && Array.isArray(draft.medicalReports)) {
          setMedicalReports(draft.medicalReports);
        } else {
          const seenReports = new Set();
          const uniqueReports = [];
          (lead?.documents || []).filter(d => d.type === 'medical_report' || !d.type).forEach(r => {
            const key = r.url || r.name;
            if (key && !seenReports.has(key)) {
              seenReports.add(key);
              uniqueReports.push({ url: r.url, name: r.name, type: r.type || 'medical_report' });
            }
          });
          setMedicalReports(uniqueReports);
        }

        const docId = typeof apt.appointmentDoctorId === 'object' ? apt.appointmentDoctorId?._id : (apt.appointmentDoctorId || '');
        const dateStr = apt.appointmentDate ? (typeof apt.appointmentDate === 'string' ? apt.appointmentDate.slice(0, 10) : new Date(apt.appointmentDate).toISOString().split('T')[0]) : '';

        // For unassigned draft appointments without a doctor, ensure date and time slot start clean and empty
        const isUnassignedDraft = apt.status === 'draft' && !docId && !apt.appointmentDate;
        const resolvedDate = isUnassignedDraft ? (draft?.formData?.appointmentDate && docId ? draft.formData.appointmentDate : '') : (draft?.formData?.appointmentDate || dateStr);
        const resolvedTime = isUnassignedDraft ? (draft?.formData?.appointmentTimeSlot && docId ? draft.formData.appointmentTimeSlot : '') : (draft?.formData?.appointmentTimeSlot || apt.appointmentTimeSlot || '');

        const defaultFormData = {
          appointmentType: apt.appointmentType || 'consultation',
          communicationMode: apt.communicationMode || 'in_person',
          appointmentDate: resolvedDate,
          appointmentTimeSlot: resolvedTime,
          appointmentDoctorId: draft?.formData?.appointmentDoctorId || docId
        };
        setFormData(defaultFormData);

        setAvailabilityDate(resolvedDate);
        if (draft?.availabilityDoctorType) {
          setAvailabilityDoctorType(draft.availabilityDoctorType);
        }

        const isFree = (apt.finalPrice === 0 && (!apt.doctorFee || apt.doctorFee === 0) && !apt.couponId);
        const defaultDoctorType = isFree ? 'in_house' : 'coupon';
        setDoctorType(draft?.doctorType || defaultDoctorType);

        const defaultCouponId = apt.couponId?._id || apt.couponId || '';
        setSelectedCouponId(draft?.selectedCouponId !== undefined ? draft.selectedCouponId : defaultCouponId);

        // 6. Pricing Breakdown
        if (draft?.pricing) {
          setPricing(draft.pricing);
        } else {
          const total = apt.finalPrice ?? (isFree ? 0 : 1000);
          const platform = apt.platformFee ?? 0;
          const other = apt.otherCharges ?? 0;
          const discount = apt.discountPercentage ?? 0;
          const taxType = (apt.taxType && apt.taxType !== 'none') ? apt.taxType : (isFree ? 'none' : 'cgst_sgst');
          const taxPercentage = (apt.taxPercentage !== undefined && apt.taxPercentage !== null && apt.taxPercentage > 0) ? apt.taxPercentage : (isFree ? 0 : 18);
          const pricingResult = calculateFromTotal(total, platform, other, discount, taxType, taxPercentage);

          setPricing({
            ...pricingResult,
            taxType,
            taxPercentage,
            taxName: apt.taxName || '',
            editing: true
          });
        }

        // Initialize Offline Payment Form State
        if (apt.offlinePaymentDetails) {
          setOfflinePaymentForm({
            paymentMethod: apt.offlinePaymentDetails.paymentMethod || 'cash',
            transactionReference: apt.offlinePaymentDetails.transactionReference || '',
            amount: apt.offlinePaymentDetails.amount || apt.finalPrice || '',
            isAutoAmount: !apt.offlinePaymentDetails.amount,
            receiptUrl: apt.offlinePaymentDetails.receiptUrl || '',
            receiptFilename: apt.offlinePaymentDetails.receiptFilename || '',
            receiptSize: apt.offlinePaymentDetails.receiptSize || 0,
            notes: apt.offlinePaymentDetails.notes || ''
          });
        } else {
          setOfflinePaymentForm(prev => ({
            ...prev,
            amount: apt.finalPrice || prev.amount || '',
            isAutoAmount: true
          }));
        }

        // 7. Pipeline Tab Stage (Only set on initial mount/load)
        if (!hasInitializedTabRef.current) {
          hasInitializedTabRef.current = true;
          const currentUrlTab = searchParams.get('tab') || searchParams.get('step');
          const isPaidOrConfirmed = (apt.status === 'confirmed' || apt.status === 'completed' || apt.paymentStatus === 'paid');

          if (isPaidOrConfirmed && (currentUrlTab === 'payment' || draft?.activeTab === 'payment')) {
            setActiveTab('confirm');
          } else if (currentUrlTab && VALID_PIPELINE_STAGES.includes(currentUrlTab)) {
            setActiveTab(currentUrlTab);
          } else if (draft?.activeTab && VALID_PIPELINE_STAGES.includes(draft.activeTab)) {
            setActiveTab(draft.activeTab);
          } else {
            let smartTab = 'clinical';
            if (isPaidOrConfirmed) {
              smartTab = 'confirm';
            } else if (apt.appointmentDoctorId && apt.appointmentDate && apt.appointmentTimeSlot) {
              smartTab = 'payment';
            } else if (apt.appointmentDoctorId) {
              smartTab = 'schedule';
            } else if (apt.notes || (apt.leadId && apt.leadId.chiefComplaints)) {
              smartTab = 'doctor_type';
            }
            setActiveTab(smartTab);
          }
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load appointment');
      navigate('/appointments');
    } finally {
      setLoading(false);
    }
  };

  const handleClinicalFileUpload = async (files, type) => {
    if (!files || files.length === 0) return;
    const MAX_SIZE = 50 * 1024 * 1024;
    for (const file of files) {
      if (file.size > MAX_SIZE) {
        toast.error(`File "${file.name}" exceeds 50MB limit.`);
        return;
      }
    }
    if (type === 'medical' && (medicalReports.length + files.length > 15)) {
      toast.error('Limit: 15 reports');
      return;
    }
    const fileFormData = new FormData();
    files.forEach(f => fileFormData.append('documents', f));
    try {
      setUploadingDoc(true);
      const response = await api.post('/upload', fileFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const uploadedFiles = response.data?.data?.files || response.data?.files || [];
      if (!uploadedFiles || uploadedFiles.length === 0) {
        throw new Error('No files returned from server');
      }
      if (type === 'id_proof') {
        setIdProof({ url: uploadedFiles[0].url, name: uploadedFiles[0].filename });
        toast.success('Identity Proof Attached');
      } else if (type === 'ayushman') {
        setAyushmanCard({ url: uploadedFiles[0].url, name: uploadedFiles[0].filename });
        toast.success('Ayushman Card Attached');
      } else if (type === 'medical') {
        const newReports = uploadedFiles.map(f => ({ url: f.url, name: f.filename, type: 'medical_report' }));
        setMedicalReports(prev => {
          const existingKeys = new Set(prev.map(r => r.url || r.name));
          const filtered = newReports.filter(r => !existingKeys.has(r.url || r.name));
          return [...prev, ...filtered];
        });
        toast.success(`${newReports.length} Report(s) Added`);
      }
    } catch (e) {
      toast.error('Upload failed');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleFilePreview = (file) => {
    if (!file || !file.url) return;
    const isPDF = file.url.toLowerCase().endsWith('.pdf') || file.type === 'application/pdf';
    setPreviewFile({
      url: file.url,
      name: file.name || 'Document',
      isPDF
    });
  };

  const handleSaveAndNext = async (currentStepKey) => {
    setSavingStage(true);
    try {
      if (currentStepKey === 'lead') {
        setActiveTab('clinical');
        return;
      }
      if (currentStepKey === 'clinical') {
        const leadTargetId = typeof appointment?.leadId === 'object' ? appointment.leadId._id : appointment.leadId;
        const docs = [];
        const seenDocKeys = new Set();

        if (idProof && idProof.url && !seenDocKeys.has(idProof.url)) {
          seenDocKeys.add(idProof.url);
          docs.push({ ...idProof, type: 'id_proof', source: 'CC' });
        }
        if (ayushmanCard && ayushmanCard.url && !seenDocKeys.has(ayushmanCard.url)) {
          seenDocKeys.add(ayushmanCard.url);
          docs.push({ ...ayushmanCard, type: 'ayushman_card', source: 'CC' });
        }
        if (medicalReports.length > 0) {
          medicalReports.forEach(r => {
            const key = r.url || r.name;
            if (key && !seenDocKeys.has(key)) {
              seenDocKeys.add(key);
              docs.push({ ...r, type: 'medical_report', source: 'CC' });
            }
          });
        }

        if (leadTargetId) {
          await api.patch(`/leads/${leadTargetId}`, {
            chiefComplaints: clinicalInfo.chiefComplaints,
            pastMedicalHistory: clinicalInfo.pastMedicalHistory,
            comorbidities: clinicalInfo.comorbidities,
            coverageMode,
            hasAyushmanCard: coverageMode === 'ayushman' || Boolean(ayushmanCard),
            paymentType: coverageMode === 'ayushman' ? ['Ayushman'] : (coverageMode === 'insurance' ? ['Insurance'] : ['Cash']),
            documents: docs,
            replaceDocuments: true
          });
        }
        await api.patch(`/appointments/${id}`, {
          notes: clinicalInfo.chiefComplaints || undefined
        });
        toast.success('Clinical information saved successfully');
        setActiveTab('doctor_type');
        await fetchAppointment();
      } else if (currentStepKey === 'doctor_type') {
        if (!formData.appointmentDoctorId) {
          toast.error('Please select a doctor to assign');
          setSavingStage(false);
          return;
        }

        const payload = {
          appointmentDoctorId: formData.appointmentDoctorId,
          appointmentType: formData.appointmentType,
          communicationMode: formData.communicationMode,
          doctorFee: doctorType === 'in_house' ? 0 : (pricing.doctorFee ?? 0),
          platformFee: doctorType === 'in_house' ? 0 : (pricing.platformFee ?? 0),
          otherCharges: doctorType === 'in_house' ? 0 : (pricing.otherCharges ?? 0),
          discountPercentage: doctorType === 'in_house' ? 0 : (pricing.discountPercentage ?? 0),
          taxType: doctorType === 'in_house' ? 'none' : (pricing.taxType || 'none'),
          taxPercentage: doctorType === 'in_house' ? 0 : (pricing.taxPercentage ?? 0),
          finalPrice: doctorType === 'in_house' ? 0 : (pricing.finalPrice ?? 0),
          ...(selectedCouponId ? { couponId: selectedCouponId } : {})
        };

        await api.patch(`/appointments/${id}`, payload);
        toast.success('Doctor and consultation settings updated successfully');
        setActiveTab('schedule');
        await fetchAppointment();
      } else if (currentStepKey === 'schedule') {
        const rawDate = formData.appointmentDate || availabilityDate;
        if (!rawDate) {
          toast.error('Please select an appointment date');
          setSavingStage(false);
          return;
        }
        if (isPastDate(rawDate)) {
          toast.error(`The selected appointment date (${formatDateDisplay(rawDate)}) has already passed. Please select today or a future date.`);
          setSavingStage(false);
          return;
        }
        if (!formData.appointmentTimeSlot) {
          toast.error('Please select an appointment time slot');
          setSavingStage(false);
          return;
        }
        const isoDate = toValidISODate(rawDate);

        if (formData.patientEmail && appointment?.leadId?._id) {
          try {
            await api.patch(`/leads/${appointment.leadId._id}`, {
              patientEmail: formData.patientEmail
            });
          } catch (e) { }
        }

        await api.patch(`/appointments/${id}`, {
          ...(isoDate ? { appointmentDate: isoDate } : {}),
          appointmentTimeSlot: formData.appointmentTimeSlot,
          appointmentType: formData.appointmentType,
          communicationMode: formData.communicationMode,
          ...(formData.patientEmail ? { patientEmail: formData.patientEmail } : {})
        });
        toast.success('Schedule updated successfully');
        setActiveTab('payment');
        await fetchAppointment();
      } else if (currentStepKey === 'payment') {
        setActiveTab('confirm');
      } else if (currentStepKey === 'confirm') {
        if (canConfirm) {
          await handleConfirm();
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save stage data');
    } finally {
      setSavingStage(false);
    }
  };

  const fetchAppointmentPayouts = async () => {
    if (!appointment?._id) return;
    setLoadingPayouts(true);
    try {
      const response = await getAppointmentPayouts(appointment._id);
      if (response?.success) {
        setAppointmentPayouts(response.data?.payouts || []);
      }
    } catch (error) {
      console.error('Failed to load appointment payouts:', error);
    } finally {
      setLoadingPayouts(false);
    }
  };

  const handleCompleteAppointmentPayout = async (payoutId) => {
    setCompletingPayoutId(payoutId);
    try {
      const response = await completeAppointmentPayout(payoutId);
      if (response?.success) {
        toast.success('Appointment payout marked as completed');
        fetchAppointmentPayouts();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to complete payout');
    } finally {
      setCompletingPayoutId(null);
    }
  };

  const handleGenerateAppointmentPayout = async () => {
    if (!appointment?._id) return;
    setGeneratingPayoutId(appointment._id);
    try {
      const response = await createAppointmentPayout(appointment._id);
      if (response?.success) {
        toast.success('Appointment payout generated successfully');
        fetchAppointmentPayouts();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate payout');
    } finally {
      setGeneratingPayoutId(null);
    }
  };

  const handleSendPayoutEmail = async (payoutId) => {
    setSendingPayoutId(payoutId);
    try {
      const response = await sendAppointmentPayoutEmail(payoutId);
      if (response?.success) {
        toast.success('Payout receipt emailed to doctor & appointment marked as Completed!');
        await fetchAppointment();
        await fetchAppointmentPayouts();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send payout email');
    } finally {
      setSendingPayoutId(null);
    }
  };

  const calculateManualNetAmount = (gross, tdsPercentage) => {
    const grossNum = Number(gross || 0);
    const tdsNum = Number(tdsPercentage || 0);
    return (grossNum - (grossNum * tdsNum / 100)).toFixed(2);
  };

  const handleManualPayoutFieldChange = (field, value) => {
    setManualPayoutForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'grossAmount' || field === 'tdsPercentage') {
        next.netAmount = calculateManualNetAmount(next.grossAmount, next.tdsPercentage);
      }
      return next;
    });
  };

  const handleCreateManualPayout = async () => {
    if (!appointment?._id) return;
    const grossAmount = Number(manualPayoutForm.grossAmount);
    const tdsPercentage = Number(manualPayoutForm.tdsPercentage);
    const netAmount = Number(manualPayoutForm.netAmount);

    if (Number.isNaN(grossAmount) || grossAmount < 0) {
      toast.error('Please enter a valid gross amount');
      return;
    }
    if (Number.isNaN(tdsPercentage) || tdsPercentage < 0 || tdsPercentage > 100) {
      toast.error('TDS percentage must be between 0 and 100');
      return;
    }
    if (Number.isNaN(netAmount) || netAmount < 0) {
      toast.error('Calculated net amount is invalid');
      return;
    }

    setCreatingManualPayout(true);
    try {
      const response = await createManualAppointmentPayout(appointment._id, {
        grossAmount,
        tdsPercentage,
        netAmount,
        transactionId: manualPayoutForm.transactionId || undefined,
        transactionDate: manualPayoutForm.transactionDate || undefined,
        notes: manualPayoutForm.notes || undefined
      });
      if (response?.success) {
        toast.success(appointmentPayouts.length > 0 ? 'Doctor payout updated & Appointment marked as Completed!' : 'Doctor payout disbursed & Appointment marked as Completed!');
        setShowManualPayoutModal(false);
        setManualPayoutForm({
          grossAmount: '',
          tdsPercentage: '10',
          netAmount: '',
          transactionId: '',
          transactionDate: new Date().toISOString().split('T')[0],
          notes: ''
        });
        await fetchAppointmentPayouts();
        await fetchAppointment();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create manual payout');
    } finally {
      setCreatingManualPayout(false);
    }
  };

  const handleSyncPayment = useCallback(async (isAutoPoll = false) => {
    if (!isAutoPoll) setSyncing(true);
    try {
      const res = await api.post(`/appointments/${id}/sync`);
      const resultData = res.data?.data || res.data;
      const isPaidNow = (
        resultData?.status === 'paid' ||
        resultData?.status === 'confirmed' ||
        resultData?.paymentStatus === 'paid' ||
        (resultData?.success === true && (resultData?.message?.toLowerCase()?.includes('verified') || resultData?.message?.toLowerCase()?.includes('confirmed')))
      );

      if (!isAutoPoll) {
        toast.success(isPaidNow ? 'Payment verified successfully!' : 'Payment status synced');
      }

      await fetchAppointment();

      if (isPaidNow) {
        toast.success('Payment received! Moving to Confirm stage...');
        setActiveTab('confirm');
      }
    } catch (error) {
      if (!isAutoPoll) {
        toast.error(error.response?.data?.message || 'Failed to sync payment');
      }
    } finally {
      if (!isAutoPoll) setSyncing(false);
    }
  }, [id, fetchAppointment, api, toast, setActiveTab]);

  // 🔄 Automatic Live Payment Polling when on Payment Stage
  useEffect(() => {
    if (!appointment) return;
    const isAlreadyPaid = (appointment.paymentStatus === 'paid' || appointment.status === 'confirmed' || appointment.status === 'completed');
    if (isAlreadyPaid) {
      if (activeTab === 'payment') {
        setActiveTab('confirm');
      }
      return;
    }

    if (activeTab !== 'payment' && appointment.status !== 'pending_payment') return;

    const interval = setInterval(async () => {
      if (currentAppointmentIdRef.current === id) {
        await handleSyncPayment(true);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [appointment?.status, appointment?.paymentStatus, activeTab, id, handleSyncPayment, setActiveTab]);

  const handleConfirm = async () => {
    setUpdating(true);
    try {
      await api.post(`/appointments/${id}/confirm`);
      try {
        localStorage.removeItem(`doxez_apt_draft_${id}`);
      } catch (e) { }
      toast.success('Appointment confirmed successfully');
      setShowConfirmModal(false);
      fetchAppointment();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to confirm appointment');
    } finally {
      setUpdating(false);
    }
  };

  const handleComplete = async () => {
    setUpdating(true);
    try {
      const response = await api.post(`/appointments/${id}/complete`);
      try {
        localStorage.removeItem(`doxez_apt_draft_${id}`);
      } catch (e) { }
      toast.success('Appointment marked as completed & Doctor payout settled!');
      setShowCompleteModal(false);
      if (response.data?.data?.prescriptionWarning) {
        toast.error(`Prescription issue: ${response.data.data.prescriptionWarning}`, { duration: 6000 });
      }
      await fetchAppointment();
      await fetchAppointmentPayouts();
      setActiveTab('confirm');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to complete appointment');
    } finally {
      setUpdating(false);
    }
  };

  const handleMarkLost = async () => {
    if (!lostReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    setUpdating(true);
    try {
      await api.post(`/appointments/${id}/lost`, { failureReason: lostReason });
      try {
        localStorage.removeItem(`doxez_apt_draft_${id}`);
      } catch (e) { }
      toast.success('Appointment marked as lost');
      setShowLostModal(false);
      setLostReason('');
      fetchAppointment();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to mark appointment as lost');
    } finally {
      setUpdating(false);
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    try {
      await api.post(`/appointments/${id}/restore`, { notes: restoreNotes });
      toast.success('Appointment restored successfully');
      setShowRestoreModal(false);
      setRestoreNotes('');
      fetchAppointment();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to restore appointment');
    } finally {
      setRestoring(false);
    }
  };

  const handleOverride = async () => {
    if (overrideAction === 'book_now' && (!overrideDate || !overrideTimeSlot || !overrideDoctorId)) {
      toast.error('Please fill all required fields');
      return;
    }
    if (overrideAction === 'mark_lost_permanent' && !lostReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    setUpdating(true);
    try {
      const payload = {
        action: overrideAction,
        reason: lostReason
      };
      if (overrideAction === 'book_now') {
        payload.appointmentDate = `${overrideDate}T00:00:00.000Z`;
        payload.appointmentTimeSlot = overrideTimeSlot;
        payload.appointmentDoctorId = overrideDoctorId;
      }
      await api.post(`/appointments/${id}/md-override`, payload);
      toast.success(`MD override applied: ${overrideAction}`);
      setShowOverrideModal(false);
      setOverrideAction('');
      setOverrideDate('');
      setOverrideTimeSlot('');
      setOverrideDoctorId('');
      setLostReason('');
      fetchAppointment();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to apply override');
    } finally {
      setUpdating(false);
    }
  };



  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/appointments/${id}`);
      try {
        localStorage.removeItem(`doxez_apt_draft_${id}`);
      } catch (e) { }
      toast.success('Appointment deleted successfully');
      navigate('/appointments');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete appointment');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const openCompleteModal = async () => {
    try {
      const response = await api.get(`/appointments/${id}`);
      const fresh = response.data?.data;
      setAppointment(fresh);
      if (fresh?.status === 'completed') {
        toast.error('Appointment is already marked as completed.');
        return;
      }
      setShowCompleteModal(true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load appointment');
    }
  };

  const canConfirm = ['admin', 'customer_care', 'cc_head', 'doctor'].includes(user?.role) && ['draft', 'pending_payment', 'overridden'].includes(appointment?.status);
  const canComplete = ['admin', 'customer_care', 'cc_head', 'doctor'].includes(user?.role) && appointment?.status === 'confirmed' && (!appointment?.couponId || appointment?.razorpayDetails?.status === 'paid');
  const canMarkLost = ['admin', 'customer_care', 'cc_head'].includes(user?.role);
  const canOverride = ['admin', 'cc_head', 'doctor'].includes(user?.role) && ['draft', 'pending_payment', 'confirmed', 'overridden'].includes(appointment?.status);
  const canMDOverride = canOverride;
  const canManagePrescription = ['admin', 'customer_care', 'cc_head', 'doctor'].includes(user?.role);
  const canRestore = ['admin', 'customer_care', 'cc_head'].includes(user?.role) && ['completed', 'lost'].includes(appointment?.status);

  const prescription = appointment?.prescriptionId;
  const prescriptionItems = Array.isArray(prescription?.prescriptionItems) ? prescription.prescriptionItems : [];
  const investigations = Array.isArray(prescription?.investigations) ? prescription.investigations : [];

  const openCreatePrescription = () => {
    setEditingPrescription(null);
    const lead = appointment?.leadId;
    setPrescriptionForm({
      chiefComplaints: lead?.chiefComplaints || lead?.clinicalIntake?.medicalDetails?.history?.currentIllness || '',
      pastMedicalHistory: lead?.pastMedicalHistory || lead?.clinicalIntake?.medicalDetails?.history?.pastDiseases || '',
      notes: '',
      advice: '',
      provisionalDiagnosis: '',
      followUp: '',
      prescriptionItems: [{ medicineName: '', strength: '', unit: 'mg', type: '', frequency: '', duration: '', durationUnit: 'days', instructions: '', isCritical: false }],
      investigations: []
    });
    setPrescriptionErrors({});
    setShowPrescriptionModal(true);
  };

  const openEditPrescription = () => {
    if (!prescription) return;
    navigate(`/prescriptions/${prescription._id}`, { state: { appointmentId: appointment._id } });
  };

  const handlePrescriptionItemChange = (index, field, value) => {
    const items = [...prescriptionForm.prescriptionItems];
    items[index] = { ...items[index], [field]: value };
    setPrescriptionForm((prev) => ({ ...prev, prescriptionItems: items }));
  };

  const addPrescriptionItem = () => {
    setPrescriptionForm((prev) => ({
      ...prev,
      prescriptionItems: [
        ...prev.prescriptionItems,
        { medicineName: '', strength: '', unit: 'mg', type: '', frequency: '', duration: '', durationUnit: 'days', instructions: '', isCritical: false }
      ]
    }));
  };

  const removePrescriptionItem = (index) => {
    if (prescriptionForm.prescriptionItems.length <= 1) return;
    const items = prescriptionForm.prescriptionItems.filter((_, i) => i !== index);
    setPrescriptionForm((prev) => ({ ...prev, prescriptionItems: items }));
  };

  const validatePrescriptionForm = () => {
    const newErrors = {};
    if (!editingPrescription) {
      if (!prescriptionForm.chiefComplaints?.trim()) {
        newErrors.chiefComplaints = 'Chief complaints is required';
      }
    } else {
      prescriptionForm.prescriptionItems.forEach((item, index) => {
        if (!item.medicineName?.trim()) newErrors[`item_${index}_medicineName`] = 'Medicine name is required';
        if (!item.frequency?.trim()) newErrors[`item_${index}_frequency`] = 'Frequency is required';
        if (!item.duration?.trim()) newErrors[`item_${index}_duration`] = 'Duration is required';
        if (!item.durationUnit?.trim()) newErrors[`item_${index}_durationUnit`] = 'Duration unit is required';
      });
    }
    setPrescriptionErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSavePrescription = async (e) => {
    e.preventDefault();
    if (!validatePrescriptionForm()) return;

    setSavingPrescription(true);
    try {
      const payload = {
        appointmentId: appointment._id,
        chiefComplaints: prescriptionForm.chiefComplaints,
        pastMedicalHistory: prescriptionForm.pastMedicalHistory
      };

      if (editingPrescription) {
        payload.notes = prescriptionForm.notes;
        payload.advice = prescriptionForm.advice;
        payload.provisionalDiagnosis = prescriptionForm.provisionalDiagnosis;
        payload.followUp = prescriptionForm.followUp;
        payload.prescriptionItems = prescriptionForm.prescriptionItems;
        payload.investigations = prescriptionForm.investigations;
      }

      let prescription;
      if (editingPrescription) {
        prescription = await updatePrescription(editingPrescription._id, payload);
        toast.success('Prescription updated successfully');
      } else {
        prescription = await createPrescription(payload);
        toast.success('Prescription created successfully');
      }

      setShowPrescriptionModal(false);
      fetchAppointment();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save prescription');
    } finally {
      setSavingPrescription(false);
    }
  };

  const handleDeletePrescription = async () => {
    if (!prescription) return;
    setDeleting(true);
    try {
      await deletePrescription(prescription._id);
      toast.success('Prescription deleted successfully');
      setShowPrescriptionDeleteModal(false);
      fetchAppointment();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete prescription');
    } finally {
      setDeleting(false);
    }
  };

  const handleSendFormToDoctor = async () => {
    const prescId = prescription?._id || prescription || appointment?.prescriptionId?._id || appointment?.prescriptionId || appointment?._id;
    if (!prescId) return;
    setSendingFormToDoctor(true);
    try {
      await sendPrescriptionFormToDoctor(prescId);
      toast.success(prescription?.prescriptionFormSentAt ? 'Prescription revision request resent to doctor successfully' : 'Prescription form sent to doctor successfully');
      fetchAppointment();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send prescription form to doctor');
    } finally {
      setSendingFormToDoctor(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!prescription) return;
    setDownloadingPDF(true);
    try {
      const response = await downloadPrescriptionPDF(prescription._id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Prescription_${String(prescription._id).slice(-8).toUpperCase()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Prescription PDF downloaded');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to download prescription PDF');
    } finally {
      setDownloadingPDF(false);
    }
  };

  const openPrescriptionActionModal = (type) => {
    setPrescriptionActionType(type);
    setPrescriptionActionNotes('');
    setPrescriptionRejectionReason('');
    setShowPrescriptionActionModal(true);
  };

  const handlePrescriptionAction = async () => {
    if (!prescription) return;
    if (prescriptionActionType === 'reject' && !prescriptionRejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    setPrescriptionActionLoading(true);
    try {
      if (prescriptionActionType === 'sendToLead') {
        await sendPrescriptionToLead(prescription._id, { notes: prescriptionActionNotes });
        toast.success('Prescription sent to lead successfully');
      } else if (prescriptionActionType === 'submit') {
        await submitPrescription(prescription._id);
        toast.success('Prescription submitted for verification successfully');
      } else {
        await verifyPrescription(prescription._id, {
          approved: prescriptionActionType === 'approve',
          verificationNotes: prescriptionActionNotes,
          rejectionReason: prescriptionActionType === 'reject' ? prescriptionRejectionReason : undefined
        });
        toast.success(`Prescription ${prescriptionActionType === 'approve' ? 'verified' : 'rejected'} successfully`);
      }
      setShowPrescriptionActionModal(false);
      setPrescriptionActionNotes('');
      setPrescriptionRejectionReason('');
      fetchAppointment();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to process prescription');
    } finally {
      setPrescriptionActionLoading(false);
    }
  };

  const handleDirectApproveAndSendPrescription = async () => {
    if (!prescription) return;
    setPrescriptionActionLoading(true);
    try {
      if (prescription.status !== 'verified') {
        await verifyPrescription(prescription._id, {
          approved: true,
          verificationNotes: 'Approved & Sent directly by CC'
        });
      }
      await sendPrescriptionToLead(prescription._id, { notes: '' });
      const isResend = Boolean(prescription.sentToLeadAt || prescription.statusHistory?.some(h => h.status === 'sent_to_lead'));
      toast.success(isResend ? 'Updated prescription approved and resent to lead successfully' : 'Prescription approved and sent to lead successfully');
      fetchAppointment();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve and send prescription');
    } finally {
      setPrescriptionActionLoading(false);
    }
  };



  const canApproveAndSendPrescription = canManagePrescription && prescription && prescription.status !== 'sent_to_lead';
  const canResendPrescription = canManagePrescription && prescription && prescription.status === 'sent_to_lead';
  const canVerifyPrescription = canManagePrescription && prescription && ['submitted', 'pending_verification'].includes(prescription.status);
  const canSendToLead = canManagePrescription && prescription;
  const canSubmitPrescription = canManagePrescription && prescription && prescription.status === 'draft';

  const renderPortalModal = (open, onClose, title, children) => {
    if (!open) return null;
    return createPortal(
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-[#0f172a]/40 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-white rounded-[28px] border border-slate-100 shadow-[0_20px_80px_-20px_rgba(20,62,120,0.25)] w-full max-w-md max-h-[90vh] overflow-y-auto p-6 animate-in fade-in zoom-in-95 duration-200">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-black text-[#143E78] tracking-tight">{title}</h3>
            <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-xl hover:bg-slate-50 transition-colors">
              <XCircle className="w-4 h-4" />
            </button>
          </div>
          {children}
        </div>
      </div>,
      document.body
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#143E78]"></div>
      </div>
    );
  }

  if (!appointment) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Appointment not found</p>
        <Link to="/appointments" className="text-[#143E78] hover:text-[#0f2c56] font-bold">
          Back to appointments
        </Link>
      </div>
    );
  }

  const patientName = appointment.leadId?.patientName || appointment.patientName || 'Patient Case';
  const bookingId = appointment.bookingId || appointment.leadId?.bookingId || 'DOX-APP';
  const patientPhone = appointment.leadId?.patientPhone || appointment.patientPhone;
  const patientEmail = appointment.leadId?.patientEmail || appointment.patientEmail;
  const patientAge = appointment.leadId?.patientAge ?? appointment.patientAge;
  const patientGender = appointment.leadId?.patientGender || appointment.patientGender;
  const bloodGroup = appointment.leadId?.bloodGroup || appointment.bloodGroup;
  const patientAddress = [appointment.leadId?.patientAddress, appointment.leadId?.city].filter(Boolean).join(', ');
  const city = appointment.leadId?.city || appointment.leadId?.patientAddress || '—';
  const abhaId = appointment.leadId?.abhaId;
  const createdDate = appointment.createdAt || appointment.leadId?.createdAt;
  const leadSource = appointment.leadId?.leadGeneratedByType || appointment.leadId?.source || 'Direct Intake';
  const leadSourceName = appointment.leadId?.leadGeneratedByName || 'CRM Registration';
  const treatmentRequired = appointment.leadId?.treatmentRequired || appointment.leadId?.disease || 'Medical Consultation';
  const disease = appointment.leadId?.disease || 'Under Evaluation';
  const chiefComplaints = appointment.leadId?.chiefComplaints || prescription?.chiefComplaints || appointment.notes || 'No specific complaints recorded during intake.';
  const pastMedicalHistory = appointment.leadId?.pastMedicalHistory || prescription?.pastMedicalHistory || 'No previous significant surgical or medical history reported.';
  const comorbidities = appointment.leadId?.comorbidities;
  const paymentType = appointment.leadId?.paymentType || [];
  const hasAyushmanCard = appointment.leadId?.hasAyushmanCard;
  const allDocuments = [...(appointment.leadId?.documents || []), ...(prescription?.documents || [])];

  // Resolve active communication mode dynamically from form or appointment
  const communicationMode = formData?.communicationMode || appointment?.communicationMode || 'in_person';
  const commConfig = COMMUNICATION_MODE_CONFIG[communicationMode] || {
    label: (communicationMode || '').replace(/_/g, ' '),
    icon: Building2,
    color: 'bg-indigo-50 text-indigo-700 border-indigo-200'
  };
  const CommIcon = commConfig.icon || Building2;

  // Resolve active consultation type dynamically
  const appointmentType = formData?.appointmentType || appointment?.appointmentType || 'consultation';
  const appointmentTypeLabel = appointmentType === 'consultation'
    ? 'Online Consultation'
    : (appointmentType === 'pre_surgery_consult' ? 'Pre-Surgery Consult' : (appointmentType === 'follow_up' ? 'Follow-Up' : appointmentType.replace(/_/g, ' ')));

  // Resolve active doctor dynamically
  const activeDoctorId = formData?.appointmentDoctorId || (typeof appointment?.appointmentDoctorId === 'object'
    ? appointment.appointmentDoctorId?._id
    : appointment?.appointmentDoctorId);
  const activeDoctorObj = activeDoctorId
    ? (doctors.find(d => (d._id || d.id) === activeDoctorId) || (typeof appointment?.appointmentDoctorId === 'object' ? appointment.appointmentDoctorId : null))
    : null;
  const doctorName = activeDoctorObj?.user
    ? `Dr. ${activeDoctorObj.user.firstName || ''} ${activeDoctorObj.user.lastName || ''}`.trim()
    : (activeDoctorObj?.name || (activeDoctorObj?.doctorName ? `Dr. ${activeDoctorObj.doctorName}` : (activeDoctorId && appointment?.appointmentDoctorId?.name ? appointment.appointmentDoctorId.name : null)));
  const doctorSpecialization = activeDoctorObj?.specialization || activeDoctorObj?.doctorType || activeDoctorObj?.user?.specialization || (activeDoctorId ? (appointment?.appointmentDoctorId?.specialization || 'General Physician') : '');

  // Schedule & Provider presence states
  const hasDoctor = Boolean(activeDoctorId && doctorName);
  const activeDate = formData?.appointmentDate || (appointment?.status !== 'draft' ? appointment?.appointmentDate : (activeDoctorId ? appointment?.appointmentDate : null));
  const activeTime = formData?.appointmentTimeSlot || (appointment?.status !== 'draft' ? appointment?.appointmentTimeSlot : (activeDoctorId ? appointment?.appointmentTimeSlot : ''));
  const hasDate = Boolean(activeDate);
  const hasTime = Boolean(activeTime);
  const hasSchedule = Boolean(hasDate || hasTime);
  const hasAnyScheduleOrDoctorData = Boolean(hasDoctor || hasSchedule || appointment?.status !== 'draft');

  return (
    <div className="space-y-5 pb-12 font-sans">
      {/* 🖨️ PRINT STYLES */}
      <style>
        {`
          @media print {
            .no-print { display: none !important; }
            html, body {
              background: white !important;
              margin: 0 !important;
              padding: 0 !important;
            }
          }
        `}
      </style>

      {/* 🔝 BREADCRUMB & WORKSPACE ACTION BAR */}
      <div className="no-print flex flex-col md:flex-row md:items-center md:justify-between gap-3 pb-1">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/appointments')}
            className="p-2 text-slate-600 hover:text-slate-900 hover:bg-white bg-white border border-slate-200 rounded-xl transition-all active:scale-95 cursor-pointer shadow-2xs"
            title="Go Back to Appointments"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-slate-900">Appointment Details</h1>
              <span className={`px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider rounded-full border ${APPOINTMENT_TYPE_COLORS[appointmentType] || 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                {appointmentTypeLabel}
              </span>
              <span className={`px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider rounded-full border ${STATUS_BADGES[appointment.status] || 'bg-slate-100 text-slate-700'}`}>
                {appointment.status?.replace(/_/g, ' ')}
              </span>
            </div>
            <p className="text-xs text-slate-500 font-medium mt-0.5">
              Manage patient consultation, clinical prescription, and payment records
            </p>
          </div>
        </div>

        {/* 🛠️ Top Bar Action Buttons */}
        <div className="flex items-center flex-wrap gap-2.5">
          {/* Exotel Call & Status Badges */}
          {patientPhone && (
            <button
              type="button"
              onClick={handleExotelCall}
              disabled={isCallingPatient}
              className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-800 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer disabled:opacity-50"
              title="Click-to-call via Exotel"
            >
              {isCallingPatient ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-600" /> <span>Calling…</span></>
              ) : (
                <><PhoneCall className="w-3.5 h-3.5 text-emerald-600" /> <span>{patientPhone}</span></>
              )}
            </button>
          )}

          {latestCall && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-bold border ${getLightStatusBadge(latestCall.status).bg}`}
                title="Call Status (Exotel Telephony)"
              >
                <span className={`w-1.5 h-1.5 rounded-full ${getLightStatusBadge(latestCall.status).dot}`} />
                {getLightStatusBadge(latestCall.status).label}
              </span>

              <button
                type="button"
                onClick={() => handleOpenQuickDispModal()}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-bold border transition-all hover:shadow-xs cursor-pointer ${getLightDispositionBadge(getEffectiveDisposition(latestCall)).bg}`}
                title="Click to update follow-up status"
              >
                <span className={`w-1.5 h-1.5 rounded-full ${getLightDispositionBadge(getEffectiveDisposition(latestCall)).dot}`} />
                <span>{getLightDispositionBadge(getEffectiveDisposition(latestCall)).label}</span>
                <Edit className="w-3 h-3 ml-0.5 opacity-60" />
              </button>
            </div>
          )}

          <button
            onClick={() => copyBookingId(bookingId)}
            className="px-3 py-1.5 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer shadow-2xs"
            title="Copy Booking ID"
          >
            {copiedId ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5 text-slate-500" />}
            <span className="font-mono">{bookingId}</span>
          </button>

          <button
            onClick={() => window.print()}
            className="px-3 py-1.5 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer shadow-2xs"
          >
            <Printer className="w-3.5 h-3.5 text-slate-500" />
            <span>Print Summary</span>
          </button>

          {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && ['draft', 'pending_payment', 'confirmed'].includes(appointment?.status) && (
            <button
              onClick={openEditAppointmentModal}
              className="px-3.5 py-1.5 bg-white hover:bg-slate-50 border border-slate-200 text-[#143E78] rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer shadow-2xs"
            >
              <Edit className="w-3.5 h-3.5" />
              <span>Edit</span>
            </button>
          )}

          {canComplete && (
            <button
              onClick={openCompleteModal}
              className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer shadow-2xs shadow-blue-600/20"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>Mark Completed</span>
            </button>
          )}
        </div>
      </div>

      {/* 🚀 TWO-COLUMN CLINICAL WORKSPACE */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* ⬅️ LEFT COLUMN: PATIENT PROFILE DOSSIER & ACTIONS (lg:col-span-4) */}
        <div className="lg:col-span-4 space-y-5">
          {/* 1️⃣ Patient Identity & Demographics Dossier */}
          <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs space-y-4">
            <div className="flex items-start gap-3.5">
              <div className="w-12 h-12 rounded-2xl bg-[#143E78] text-white flex items-center justify-center font-black text-lg shadow-sm shrink-0 uppercase">
                {patientName?.charAt(0) || <User className="w-6 h-6" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-base font-bold text-slate-900 truncate tracking-tight">{patientName}</h2>
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-[11px] font-mono font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200/60">
                    {bookingId}
                  </span>
                  {bloodGroup && (
                    <span className="text-[10px] font-black text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded-md">
                      {bloodGroup}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Contact Bar */}
            <div className="grid grid-cols-1 gap-2 pt-1">
              {patientPhone && (
                <div className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-100 rounded-xl">
                  <div className="flex items-center gap-2 min-w-0">
                    <Phone className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span className="text-xs font-bold text-slate-800 font-mono truncate">{patientPhone}</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={handleExotelCall}
                      disabled={isCallingPatient}
                      className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-[10px] font-bold transition-colors flex items-center gap-1 cursor-pointer disabled:opacity-50"
                      title="Click-to-call via Exotel"
                    >
                      {isCallingPatient ? <Loader2 className="w-3 h-3 animate-spin" /> : <PhoneCall className="w-3 h-3" />}
                      <span>{isCallingPatient ? 'Calling…' : 'Call'}</span>
                    </button>
                    <a
                      href={`tel:${patientPhone}`}
                      className="p-1 hover:bg-slate-200 text-slate-500 rounded-lg transition-colors cursor-pointer"
                      title="Direct phone dial"
                    >
                      <Phone className="w-3 h-3" />
                    </a>
                    <button
                      onClick={() => copyPhoneNumber(patientPhone)}
                      className="p-1 hover:bg-slate-200 text-slate-500 rounded-lg transition-colors cursor-pointer"
                      title="Copy Phone"
                    >
                      {copiedPhone ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                    </button>
                  </div>
                </div>
              )}

              {patientEmail && (
                <div className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-100 rounded-xl">
                  <div className="flex items-center gap-2 min-w-0">
                    <Mail className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                    <span className="text-xs font-medium text-slate-700 truncate" title={patientEmail}>{patientEmail}</span>
                  </div>
                  <a
                    href={`mailto:${patientEmail}`}
                    className="px-2 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 rounded-lg text-[10px] font-bold transition-colors shrink-0"
                  >
                    Email
                  </a>
                </div>
              )}
            </div>

            {/* Demographics & Identifiers */}
            <div className="border-t border-slate-100 pt-3 space-y-2 text-xs">
              <div className="flex justify-between py-1 border-b border-slate-50">
                <span className="text-slate-500">Age / Gender</span>
                <span className="font-bold text-slate-800">
                  {patientAge ? `${patientAge} Yrs` : '—'} / {patientGender ? patientGender.charAt(0).toUpperCase() + patientGender.slice(1) : '—'}
                </span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-50">
                <span className="text-slate-500">Location / City</span>
                <span className="font-bold text-slate-800 text-right truncate max-w-[160px]" title={patientAddress || '—'}>
                  {patientAddress || '—'}
                </span>
              </div>
              {abhaId && (
                <div className="flex justify-between py-1 border-b border-slate-50">
                  <span className="text-slate-500">ABHA / Gov ID</span>
                  <span className="font-mono font-bold text-slate-800">{abhaId}</span>
                </div>
              )}
              <div className="flex justify-between py-1 border-b border-slate-50">
                <span className="text-slate-500">Referral Source</span>
                <span className="font-bold text-slate-800 uppercase text-[11px]">
                  {leadSource}
                </span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-slate-500">Referred By</span>
                <span className="font-bold text-slate-700 text-right truncate max-w-[160px]" title={leadSourceName}>
                  {leadSourceName}
                </span>
              </div>
            </div>
          </div>

          {/* 📞 Call & Follow-up Status Card */}
          <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-2xs space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                <PhoneCall className="w-3.5 h-3.5 text-violet-600" />
                Call & Follow-up Status
              </h3>
              {latestCall && (
                <button
                  type="button"
                  onClick={() => handleOpenQuickDispModal()}
                  className="inline-flex items-center gap-1 text-[11px] font-bold text-violet-700 hover:text-violet-800 bg-violet-50 hover:bg-violet-100 px-2 py-0.5 rounded-md border border-violet-200 transition-all cursor-pointer"
                >
                  <Edit className="w-3 h-3" />
                  <span>Update</span>
                </button>
              )}
            </div>

            {latestCall ? (
              <div className="space-y-2.5 text-xs">
                {/* Call Status Row */}
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-500 font-medium">Call Status:</span>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold border ${getLightStatusBadge(latestCall.status).bg}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${getLightStatusBadge(latestCall.status).dot}`} />
                    {getLightStatusBadge(latestCall.status).label}
                  </span>
                </div>

                {/* Follow-up Status Row */}
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-500 font-medium">Follow-up:</span>
                  <button
                    type="button"
                    onClick={() => handleOpenQuickDispModal()}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold border transition-all hover:opacity-90 cursor-pointer ${getLightDispositionBadge(getEffectiveDisposition(latestCall)).bg}`}
                    title="Click to update follow-up status"
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${getLightDispositionBadge(getEffectiveDisposition(latestCall)).dot}`} />
                    {getLightDispositionBadge(getEffectiveDisposition(latestCall)).label}
                    <Edit className="w-3 h-3 ml-0.5 opacity-60" />
                  </button>
                </div>

                {/* Time & Direction */}
                <div className="flex items-center justify-between text-[11px] text-slate-500 pt-2 border-t border-slate-100">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3 text-slate-400" />
                    {new Date(latestCall.createdAt || latestCall.startTime).toLocaleString('en-IN', {
                      day: '2-digit', month: 'short', year: 'numeric',
                      hour: '2-digit', minute: '2-digit', hour12: true
                    })}
                  </span>
                  <span className="capitalize font-bold text-slate-600">
                    {latestCall.direction || 'Inbound'} {latestCall.duration > 0 ? `• ${Math.floor(latestCall.duration / 60)}m ${latestCall.duration % 60}s` : ''}
                  </span>
                </div>

                {/* Follow-up Notes if any */}
                {(latestCall.dispositionNotes || latestCall.notes) && (
                  <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-200/80 text-[11px] text-slate-700 space-y-1">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Follow-up Note:</span>
                    <p className="italic text-slate-600">"{latestCall.dispositionNotes || latestCall.notes}"</p>
                    {latestCall.dispositionUpdatedBy && (
                      <p className="text-[10px] text-slate-400 text-right">
                        — {latestCall.dispositionUpdatedBy.firstName} {latestCall.dispositionUpdatedBy.lastName}
                      </p>
                    )}
                  </div>
                )}

                <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setShowFullCallHistory(!showFullCallHistory)}
                    className="text-xs font-bold text-[#143E78] hover:underline flex items-center gap-1 cursor-pointer"
                  >
                    <span>{showFullCallHistory ? 'Hide Call History' : `View All Calls (${callLogs.length})`}</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleExotelCall}
                    disabled={isCallingPatient}
                    className="text-xs font-bold text-emerald-700 hover:text-emerald-800 flex items-center gap-1 cursor-pointer disabled:opacity-50"
                  >
                    <PhoneCall className="w-3 h-3" />
                    <span>Call Now</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-slate-400 text-xs">
                <PhoneMissed className="w-6 h-6 mx-auto mb-1.5 text-slate-300" />
                <p>No call logs found for this patient yet.</p>
                {patientPhone && (
                  <button
                    type="button"
                    onClick={handleExotelCall}
                    disabled={isCallingPatient}
                    className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-colors cursor-pointer shadow-xs disabled:opacity-50"
                  >
                    {isCallingPatient ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PhoneCall className="w-3.5 h-3.5" />}
                    <span>Initiate Call via Exotel</span>
                  </button>
                )}
              </div>
            )}

            {/* Expandable Full Call History */}
            {showFullCallHistory && (
              <div className="pt-3 border-t border-slate-100">
                <CallHistoryPanel
                  leadId={targetLeadId}
                  refreshTrigger={callHistoryRefresh}
                  onLatestCallChange={setLatestCall}
                />
              </div>
            )}
          </div>

          {/* 2️⃣ Schedule & Doctor Provider Card */}
          <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-2.5">
              <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                <CalendarClock className="w-4 h-4 text-[#143E78]" />
                Schedule & Provider
              </h3>
              {hasSchedule && (user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && ['draft', 'pending_payment', 'confirmed'].includes(appointment?.status) && (
                <button
                  onClick={openEditAppointmentModal}
                  className="text-[10px] font-bold text-[#143E78] hover:underline flex items-center gap-1 cursor-pointer"
                >
                  <Edit className="w-3 h-3" /> Reschedule
                </button>
              )}
            </div>

            {!hasAnyScheduleOrDoctorData ? (
              <div className="py-7 px-4 text-center rounded-2xl bg-slate-50/80 border border-dashed border-slate-200 space-y-2">
                <div className="w-10 h-10 rounded-2xl bg-white border border-slate-200 text-slate-400 flex items-center justify-center mx-auto shadow-2xs">
                  <CalendarClock className="w-5 h-5 text-slate-400" />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-700">No Schedule & Provider Details</p>
                  <p className="text-[11px] text-slate-400 font-medium max-w-[220px] mx-auto mt-0.5 leading-snug">
                    Doctor and schedule details will appear here once selected in the workflow.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Date & Time Slot - only displayed when schedule is set */}
                {(hasDate || hasTime) && (
                  <div className="grid grid-cols-2 gap-2">
                    {hasDate && (
                      <div className={`p-3 bg-slate-50 border border-slate-100 rounded-xl ${!hasTime ? 'col-span-2' : ''}`}>
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Date</span>
                        <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                          {formatDateDisplay(activeDate)}
                        </span>
                      </div>
                    )}
                    {hasTime && (
                      <div className={`p-3 bg-slate-50 border border-slate-100 rounded-xl ${!hasDate ? 'col-span-2' : ''}`}>
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Time</span>
                        <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5 truncate">
                          <Clock className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                          {formatTime12Hour(activeTime)}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Consultation Type */}
                <div className={`p-3 rounded-xl border flex items-center gap-2.5 ${APPOINTMENT_TYPE_COLORS[appointmentType] || 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                  <Stethoscope className="w-4 h-4 shrink-0" />
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-wider block opacity-70">Consultation Type</span>
                    <span className="text-xs font-bold">{appointmentTypeLabel}</span>
                  </div>
                </div>

                {/* Communication Mode */}
                <div className={`p-3 rounded-xl border flex items-center gap-2.5 ${commConfig.color}`}>
                  <CommIcon className="w-4 h-4 shrink-0" />
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-wider block opacity-70">Consultation Mode</span>
                    <span className="text-xs font-bold">{commConfig.label}</span>
                  </div>
                </div>

                {/* Assigned Doctor - only displayed when a doctor is selected/assigned */}
                {hasDoctor && (
                  <div className="p-3.5 bg-slate-50 border border-slate-100 rounded-xl flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-white border border-slate-200 text-[#143E78] flex items-center justify-center font-bold shrink-0 shadow-2xs">
                      <Stethoscope className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Assigned Doctor</span>
                      <p className="text-xs font-bold text-slate-900 truncate">
                        {doctorName}
                      </p>
                      {doctorSpecialization && (
                        <p className="text-[10px] text-slate-500 font-medium truncate">{doctorSpecialization}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 3️⃣ Status & Stage Action Center */}
          <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs space-y-3.5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <span className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                Stage & Actions
              </span>
              <span className={`px-2.5 py-0.5 text-[10px] font-black uppercase rounded-lg border ${STATUS_BADGES[appointment.status] || 'bg-slate-100 text-slate-700'}`}>
                {appointment.status?.replace(/_/g, ' ')}
              </span>
            </div>

            <div className="space-y-2">
              {canConfirm && (
                <button
                  onClick={() => setShowConfirmModal(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-xl text-xs font-bold hover:bg-emerald-700 transition-all shadow-sm cursor-pointer"
                >
                  <CheckCircle className="w-4 h-4" /> Confirm Appointment
                </button>
              )}

              {appointment?.status === 'pending_payment' && (
                <button
                  onClick={handleSyncPayment}
                  disabled={syncing}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#143E78] text-white rounded-xl text-xs font-bold hover:bg-[#0f2c56] disabled:opacity-50 transition-all shadow-sm cursor-pointer"
                >
                  {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  {syncing ? 'Syncing...' : 'Sync Payment'}
                </button>
              )}

              {canComplete && (
                <button
                  onClick={openCompleteModal}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-xs font-bold hover:bg-blue-700 transition-all shadow-sm cursor-pointer"
                >
                  <CheckCircle className="w-4 h-4" /> Mark Completed
                </button>
              )}

              {canMarkLost && (
                <button
                  onClick={() => setShowLostModal(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-50 text-orange-700 hover:bg-orange-100 border border-orange-200 rounded-xl text-xs font-bold transition-all cursor-pointer"
                >
                  <XCircle className="w-4 h-4" /> Mark as Lost
                </button>
              )}

              {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && ['draft', 'pending_payment', 'confirmed'].includes(appointment?.status) && (
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200 rounded-xl text-xs font-bold transition-all cursor-pointer"
                >
                  <Trash2 className="w-4 h-4" /> Delete Appointment
                </button>
              )}

              {canRestore && (
                <button
                  onClick={() => setShowRestoreModal(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 rounded-xl text-xs font-bold transition-all cursor-pointer"
                >
                  <RefreshCw className="w-4 h-4" /> Restore Appointment
                </button>
              )}
            </div>

            {/* 💳 Payment Widget */}
            {(() => {
              const hasOnlineTxn = Boolean(
                appointment?.razorpayDetails?.paymentId?.startsWith('pay_') ||
                appointment?.razorpayDetails?.orderId?.startsWith('order_') ||
                appointment?.razorpayDetails?.orderId?.startsWith('plink_')
              );
              const hasOfflineProof = Boolean(
                appointment?.offlinePaymentDetails?.receiptUrl ||
                appointment?.offlinePaymentDetails?.receivedBy ||
                (appointment?.offlinePaymentDetails?.transactionReference && !appointment.offlinePaymentDetails.transactionReference.startsWith('pay_'))
              );

              // If Razorpay Txn ID (e.g. pay_...) exists, it is definitively Online (Razorpay)
              const isOnlinePaid = hasOnlineTxn || (!hasOfflineProof && appointment?.razorpayDetails?.status === 'paid');
              const isOfflinePaid = !isOnlinePaid && hasOfflineProof;
              const isPaid = appointment?.razorpayDetails?.status === 'paid' || isOnlinePaid || isOfflinePaid || appointment?.status === 'completed' || appointment?.status === 'confirmed';

              const paidAmount = (isOfflinePaid && appointment?.offlinePaymentDetails?.amount)
                ? appointment.offlinePaymentDetails.amount
                : (appointment?.finalPrice || 0);

              const paymentMethodLabel = isOnlinePaid
                ? 'Online (Razorpay)'
                : isOfflinePaid
                  ? `Offline (${appointment?.offlinePaymentDetails?.paymentMethod === 'card_pos' ? 'POS/Card' : appointment?.offlinePaymentDetails?.paymentMethod === 'bank_transfer' ? 'Bank NEFT' : (appointment?.offlinePaymentDetails?.paymentMethod || 'Cash').toUpperCase()})`
                  : 'Pending / Not Paid';

              return (
                <div className="bg-slate-50 rounded-xl border border-slate-100 p-3.5 space-y-2.5 mt-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">Consultation Fee</span>
                    <span className="font-black text-slate-900 text-sm">₹{paidAmount.toLocaleString('en-IN')}</span>
                  </div>

                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">Payment Status</span>
                    <span className={`px-2 py-0.5 text-[9px] font-black uppercase rounded-md border ${isPaid ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                      {isPaid ? 'Paid' : (appointment?.status === 'pending_payment' ? 'Pending Payment' : 'Not Paid')}
                    </span>
                  </div>

                  {/* 💵 Payment Mode */}
                  <div className="flex items-center justify-between text-xs pt-1.5 border-t border-slate-200/60">
                    <span className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">Payment Mode</span>
                    {isOnlinePaid ? (
                      <span className="inline-flex items-center gap-1 text-blue-800 bg-blue-100/90 px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider border border-blue-200">
                        <CreditCard className="w-3 h-3 text-blue-700" />
                        Online (Razorpay)
                      </span>
                    ) : isOfflinePaid ? (
                      <span className="inline-flex items-center gap-1 text-emerald-800 bg-emerald-100/90 px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider border border-emerald-200">
                        <Receipt className="w-3 h-3 text-emerald-700" />
                        {paymentMethodLabel}
                      </span>
                    ) : (
                      <span className="text-[10px] font-bold text-slate-400">
                        {appointment?.razorpayDetails?.paymentLinkUrl ? 'Online Link Sent' : 'Pending / Not Paid'}
                      </span>
                    )}
                  </div>

                  {/* Online Txn ID */}
                  {isOnlinePaid && (appointment?.razorpayDetails?.paymentId || appointment?.razorpayDetails?.orderId) && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">Txn ID</span>
                      <span className="font-mono text-[10px] font-bold text-slate-700 truncate max-w-[130px]" title={appointment.razorpayDetails.paymentId || appointment.razorpayDetails.orderId}>
                        {appointment.razorpayDetails.paymentId || appointment.razorpayDetails.orderId}
                      </span>
                    </div>
                  )}

                  {/* Offline Details only when offline paid */}
                  {isOfflinePaid && appointment?.offlinePaymentDetails?.transactionReference && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">Ref / UTR No</span>
                      <span className="font-mono text-[10px] font-bold text-slate-700 truncate max-w-[130px]" title={appointment.offlinePaymentDetails.transactionReference}>
                        {appointment.offlinePaymentDetails.transactionReference}
                      </span>
                    </div>
                  )}

                  {/* View Uploaded Offline Receipt if available */}
                  {isOfflinePaid && appointment?.offlinePaymentDetails?.receiptUrl && (
                    <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-200/60">
                      <span className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">Payment Proof</span>
                      <a
                        href={appointment.offlinePaymentDetails.receiptUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[10px] font-black text-[#143E78] hover:underline"
                      >
                        <span>View Receipt</span>
                        <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    </div>
                  )}

                  {/* Payment Link - ONLY shown when payment is unpaid/pending */}
                  {!isPaid && appointment?.razorpayDetails?.paymentLinkUrl && (
                    <div className="space-y-1.5 pt-1 border-t border-slate-200/60">
                      <span className="font-bold text-slate-500 uppercase tracking-wider text-[10px] block">Payment Link</span>
                      <div className="flex items-center gap-1.5">
                        <input
                          readOnly
                          value={appointment.razorpayDetails.paymentLinkUrl}
                          className="flex-1 px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-[10px] font-mono text-blue-700 outline-none select-all truncate"
                        />
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(appointment.razorpayDetails.paymentLinkUrl);
                            toast.success('Payment link copied!');
                          }}
                          className="px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-[10px] font-bold hover:bg-blue-100 transition-all shrink-0 cursor-pointer"
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Generate Link Button - ONLY shown when payment is unpaid/pending */}
                  {!isPaid && (user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && appointment?.status !== 'completed' && (
                    <button
                      onClick={openPaymentLinkModal}
                      className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-[#143E78] text-white rounded-lg text-[11px] font-bold hover:bg-[#0f2c56] transition-all cursor-pointer shadow-2xs mt-1"
                    >
                      <SendHorizontal className="w-3.5 h-3.5" />
                      {appointment?.razorpayDetails?.paymentLinkUrl ? 'Regenerate Link' : 'Generate Payment Link'}
                    </button>
                  )}
                </div>
              );
            })()}
          </div>

          {/* 4️⃣ Doctor Consultation Payout Card (Left Sidebar) */}
          {(user?.role === 'admin' || user?.role === 'accountant' || user?.role === 'customer_care' || user?.role === 'cc_head') && (
            <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs space-y-3.5">
              <div className="flex items-center justify-between border-b border-slate-100 pb-2.5">
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                  <Wallet className="w-4 h-4 text-[#143E78]" />
                  Doctor Consultation Payout
                </h3>
                <Link
                  to="/payouts"
                  className="text-[10px] font-bold text-[#143E78] hover:underline flex items-center gap-1"
                >
                  Master Payouts <ArrowRight className="w-3 h-3" />
                </Link>
              </div>

              {loadingPayouts ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-[#143E78]" />
                  <span className="ml-2 text-xs font-bold text-slate-500">Loading payout...</span>
                </div>
              ) : appointmentPayouts.length === 0 ? (
                <div className="text-center py-4 bg-slate-50 rounded-xl border border-slate-100 space-y-2">
                  <Wallet className="w-6 h-6 text-slate-300 mx-auto" />
                  <p className="text-xs font-bold text-slate-700">No doctor payout created yet</p>
                  <p className="text-[10px] text-slate-400 px-2">Complete consultation to generate payout</p>
                  <div className="flex flex-col gap-2 pt-1 px-2">
                    {appointment?.status === 'completed' && appointment?.razorpayDetails?.status === 'paid' && (
                      <button
                        onClick={handleGenerateAppointmentPayout}
                        disabled={generatingPayoutId === appointment._id}
                        className="w-full py-2 bg-[#143E78] text-white rounded-xl text-xs font-bold hover:bg-[#0f2c56] transition-all disabled:opacity-50 shadow-sm cursor-pointer flex items-center justify-center gap-1.5"
                      >
                        {generatingPayoutId === appointment._id ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                        <span>Generate Payout</span>
                      </button>
                    )}
                    <button
                      onClick={() => setShowManualPayoutModal(true)}
                      className="w-full py-2 bg-emerald-600 text-white rounded-xl text-xs font-bold hover:bg-emerald-700 transition-all shadow-sm cursor-pointer flex items-center justify-center gap-1.5"
                    >
                      <Wallet size={13} />
                      <span>Process Manual Payout</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {appointmentPayouts.map((payout) => {
                    const { gross, tds, net } = getPayoutMetrics(payout);
                    const docName = payout.doctor?.user ? `Dr. ${payout.doctor.user.firstName || ''} ${payout.doctor.user.lastName || ''}`.trim() : '—';
                    const isCompleted = payout.status === 'completed';
                    const isEmailSent = payout.emailSent || isCompleted;

                    return (
                      <div key={payout._id} className="border border-slate-200 rounded-xl p-3 space-y-2.5 bg-slate-50/70">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-slate-800 truncate max-w-[150px]">{docName}</span>
                          <span className={`px-2 py-0.5 text-[9px] font-black rounded-md border uppercase ${payout.status === 'completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                            'bg-amber-50 text-amber-700 border-amber-200'
                            }`}>
                            {payout.status}
                          </span>
                        </div>

                        <div className="grid grid-cols-3 gap-1.5 text-center text-xs bg-white rounded-lg p-2 border border-slate-100">
                          <div>
                            <span className="text-[9px] font-bold text-slate-400 uppercase block">Fee</span>
                            <span className="font-bold text-slate-800 text-[11px]">₹{gross.toLocaleString('en-IN')}</span>
                          </div>
                          <div>
                            <span className="text-[9px] font-bold text-slate-400 uppercase block">TDS</span>
                            <span className="font-bold text-rose-600 text-[11px]">−₹{tds.toLocaleString('en-IN')}</span>
                          </div>
                          <div>
                            <span className="text-[9px] font-bold text-slate-400 uppercase block">Net</span>
                            <span className="font-black text-emerald-700 text-[11px]">₹{net.toLocaleString('en-IN')}</span>
                          </div>
                        </div>

                        <div className="flex items-center justify-end gap-1.5 pt-1">
                          {!isEmailSent && (
                            <button
                              onClick={() => handleSendPayoutEmail(payout._id)}
                              disabled={sendingPayoutId === payout._id}
                              className="px-2.5 py-1 bg-indigo-600 text-white rounded-lg text-[10px] font-bold hover:bg-indigo-700 transition-all disabled:opacity-50 cursor-pointer flex items-center gap-1"
                            >
                              {sendingPayoutId === payout._id ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
                              Email
                            </button>
                          )}
                          {payout.status === 'processing' && (user?.role === 'admin' || user?.role === 'accountant') && (
                            <button
                              onClick={() => handleCompleteAppointmentPayout(payout._id)}
                              disabled={completingPayoutId === payout._id}
                              className="px-2.5 py-1 bg-emerald-600 text-white rounded-lg text-[10px] font-bold hover:bg-emerald-700 transition-all disabled:opacity-50 cursor-pointer flex items-center gap-1"
                            >
                              {completingPayoutId === payout._id ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
                              Complete
                            </button>
                          )}
                          <Link
                            to={`/payouts/${payout._id}`}
                            className="px-2 py-1 bg-white border border-slate-200 text-slate-700 rounded-lg text-[10px] font-bold hover:bg-slate-50 transition-all"
                          >
                            View
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* 5️⃣ Status Transition Timeline Card (Left Sidebar) */}
          <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs space-y-3.5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-2.5">
              <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                <History className="w-4 h-4 text-[#143E78]" />
                Status Transition Timeline ({appointment.statusHistory?.length || 0})
              </h3>
            </div>

            {appointment.statusHistory && appointment.statusHistory.length > 0 ? (
              <div className="space-y-3 pt-1">
                {appointment.statusHistory.map((entry, index) => (
                  <div key={index} className="flex items-start gap-2.5 pb-3 border-b border-slate-100 last:border-0 last:pb-0">
                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg border text-xs shrink-0 ${STATUS_BADGES[entry.status] || 'bg-slate-100 text-slate-700'}`}>
                      {(entry.status === 'confirmed' || entry.status === 'completed') && <CheckCircle2 size={14} />}
                      {(entry.status === 'cancelled' || entry.status === 'lost') && <XCircle size={14} />}
                      {entry.status === 'pending_payment' && <Clock size={14} />}
                      {!['confirmed', 'completed', 'cancelled', 'lost', 'pending_payment'].includes(entry.status) && <FileText size={14} />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1.5 flex-wrap">
                        <span className={`px-2 py-0.5 text-[9px] font-black uppercase rounded-md border ${STATUS_BADGES[entry.status] || 'bg-slate-100 text-slate-700'}`}>
                          {entry.status?.replace(/_/g, ' ')}
                        </span>
                        <span className="text-[10px] font-medium text-slate-400 font-mono">
                          {new Date(entry.changedAt).toLocaleString('en-IN')}
                        </span>
                      </div>
                      {entry.notes && <p className="text-[11px] text-slate-600 font-medium mt-1 leading-snug">{entry.notes}</p>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-slate-400 text-xs font-bold bg-slate-50 rounded-xl">
                No status history recorded yet.
              </div>
            )}
          </div>
        </div>

        {/* ➡️ RIGHT COLUMN: 6-STAGE PIPELINE STEPPER & WORKFLOW (lg:col-span-8) */}
        <div className="lg:col-span-8 space-y-5">
          {/* 🌟 6-STAGE HORIZONTAL PIPELINE STEPPER */}
          {(() => {
            // Sequential step completion calculation
            const isLeadComplete = Boolean(appointment.leadId || appointment.patientName);

            const hasClinicalDetails = Boolean(
              (appointment.leadId?.treatmentRequired && appointment.leadId.treatmentRequired !== 'Under Evaluation') ||
              appointment.leadId?.chiefComplaints ||
              appointment.leadId?.pastMedicalHistory ||
              (appointment.leadId?.documents && appointment.leadId.documents.length > 0) ||
              prescription?.chiefComplaints ||
              prescription?.provisionalDiagnosis
            );
            const isClinicalComplete = isLeadComplete && hasClinicalDetails;

            const hasDoctorAssigned = Boolean(
              appointment.appointmentDoctorId &&
              (appointment.appointmentDoctorId._id || appointment.appointmentDoctorId.name || typeof appointment.appointmentDoctorId === 'string')
            );
            const isDoctorTypeComplete = isClinicalComplete && hasDoctorAssigned;

            const hasScheduleSlot = Boolean(appointment.appointmentDate && appointment.appointmentTimeSlot);
            const isScheduleComplete = isDoctorTypeComplete && hasScheduleSlot;

            const isPaidOrConfirmed = Boolean(
              appointment.razorpayDetails?.status === 'paid' ||
              (appointment.finalPrice > 0 && appointment.paymentStatus === 'paid') ||
              appointment.status === 'confirmed' ||
              appointment.status === 'completed'
            );
            const isPaymentComplete = isScheduleComplete && isPaidOrConfirmed;

            const isConfirmComplete = isPaymentComplete && (
              appointment.status === 'confirmed' || appointment.status === 'completed'
            );

            const STAGE_STATUS = {
              lead: isLeadComplete,
              clinical: isClinicalComplete,
              doctor_type: isDoctorTypeComplete,
              schedule: isScheduleComplete,
              payment: isPaymentComplete,
              confirm: isConfirmComplete
            };

            const STAGES_LIST = [
              { key: 'lead', label: 'SELECT LEAD', icon: User },
              { key: 'clinical', label: 'CLINICAL INFO', icon: FileText },
              { key: 'doctor_type', label: 'DOCTOR TYPE', icon: Stethoscope },
              { key: 'schedule', label: 'SCHEDULE', icon: Calendar },
              { key: 'payment', label: 'PAYMENT', icon: CreditCard },
              { key: 'confirm', label: 'CONFIRM', icon: CheckCircle }
            ];

            return (
              <div className="bg-white rounded-2xl border border-slate-200/90 p-4 sm:p-5 shadow-2xs">
                <div className="flex items-center justify-between w-full">
                  {STAGES_LIST.map((step, index, arr) => {
                    const StepIcon = step.icon;
                    const isLast = index === arr.length - 1;
                    const isComplete = STAGE_STATUS[step.key];
                    const isActive = activeTab === step.key;

                    // Accessible only if it's step 0 OR all preceding steps are strictly complete
                    const isAccessible = index === 0 || arr.slice(0, index).every(prev => STAGE_STATUS[prev.key]);

                    return (
                      <div key={step.key} className="flex items-center flex-1 last:flex-initial">
                        <button
                          type="button"
                          disabled={!isAccessible}
                          onClick={() => {
                            if (isAccessible) {
                              setActiveTab(step.key);
                            } else {
                              toast.error('Please complete the previous stage data first');
                            }
                          }}
                          className={`flex flex-col items-center group relative shrink-0 transition-transform ${isAccessible
                            ? 'cursor-pointer active:scale-95'
                            : 'cursor-not-allowed opacity-40'
                            }`}
                          title={!isAccessible ? 'Complete previous stage to unlock' : step.label}
                        >
                          <div
                            className={`w-10 h-10 rounded-2xl flex items-center justify-center transition-all duration-200 ${isComplete && !isActive
                              ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20'
                              : isActive
                                ? 'bg-[#143E78] text-white shadow-lg shadow-[#143E78]/30 scale-110 ring-4 ring-[#143E78]/15'
                                : isAccessible
                                  ? 'bg-slate-100 text-slate-600 border border-slate-200 hover:border-slate-300 hover:text-slate-800'
                                  : 'bg-slate-100 text-slate-300 border border-slate-200'
                              }`}
                          >
                            {isComplete && !isActive ? (
                              <Check className="w-5 h-5 stroke-[2.5]" />
                            ) : (
                              <StepIcon className="w-5 h-5" />
                            )}
                          </div>
                          <span
                            className={`text-[9px] sm:text-[10px] mt-2 font-black uppercase tracking-wider whitespace-nowrap transition-colors ${isActive
                              ? 'text-[#143E78]'
                              : isComplete
                                ? 'text-emerald-700'
                                : isAccessible
                                  ? 'text-slate-500 group-hover:text-slate-700'
                                  : 'text-slate-300'
                              }`}
                          >
                            {step.label}
                          </span>
                        </button>

                        {!isLast && (
                          <div className="flex-1 mx-2 sm:mx-3 mb-5">
                            <div className="h-1 rounded-full bg-slate-100 relative overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all duration-300 ${isComplete ? 'bg-emerald-500 w-full' : 'bg-slate-200 w-full'
                                  }`}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* 📋 STAGE 1: SELECT LEAD / INTAKE */}
          {activeTab === 'lead' && (
            <div className="space-y-5 animate-in fade-in duration-200">
              <div className="bg-white rounded-3xl border-2 border-slate-100 hover:border-slate-200 p-5 sm:p-6 shadow-sm space-y-4">
                <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider flex items-center gap-2">
                    <User className="w-4 h-4 text-[#143E78]" />
                    Stage 1: Patient Lead Dossier
                  </h3>
                  {appointment.leadId && (
                    <Link
                      to={`/leads/${typeof appointment.leadId === 'object' ? appointment.leadId._id : appointment.leadId}`}
                      className="px-3 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 rounded-xl text-xs font-black transition-all flex items-center gap-1.5"
                    >
                      <span>Open Master Lead File</span>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </Link>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Patient Demographics</span>
                      {canEditPatient && (
                        <button
                          type="button"
                          onClick={() => setShowEditPatientModal(true)}
                          className="inline-flex items-center gap-1 text-[10px] font-bold text-[#143E78] bg-blue-50 hover:bg-blue-100 border border-blue-200/80 px-2 py-0.5 rounded-lg transition-all cursor-pointer"
                          title="Edit Patient Details"
                        >
                          <Edit className="w-2.5 h-2.5" /> Edit
                        </button>
                      )}
                    </div>
                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between">
                        <span className="text-slate-500 font-bold">Full Name:</span>
                        <strong className="text-slate-900 font-black">{patientName}</strong>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500 font-bold">Phone:</span>
                        <strong className="text-slate-900 font-mono font-bold">{patientPhone || '—'}</strong>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500 font-bold">Email:</span>
                        <strong className="text-slate-900 truncate max-w-[150px] font-bold">{patientEmail || '—'}</strong>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500 font-bold">Age / Gender:</span>
                        <strong className="text-slate-900 font-bold">{patientAge ? `${patientAge} Yrs` : '—'} / {patientGender || '—'}</strong>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500 font-bold">Blood Group:</span>
                        <strong className="text-rose-600 font-bold">{bloodGroup}</strong>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl space-y-2">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Acquisition & Intake</span>
                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between">
                        <span className="text-slate-500 font-bold">Booking ID:</span>
                        <strong className="text-[#143E78] font-mono font-black">{bookingId}</strong>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500 font-bold">Source:</span>
                        <strong className="text-slate-900 uppercase font-bold">{leadSource}</strong>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500 font-bold">Referred By:</span>
                        <strong className="text-slate-900 truncate max-w-[150px] font-bold">{leadSourceName}</strong>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500 font-bold">Registered On:</span>
                        <strong className="text-slate-900 font-bold">{formatDateDisplay(createdDate)}</strong>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500 font-bold">City / Location:</span>
                        <strong className="text-slate-900 font-bold">{city}</strong>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer Navigation */}
                <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => navigate('/appointments')}
                    className="px-5 py-3 border-2 border-slate-200 text-slate-700 rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-slate-50 transition-all flex items-center gap-2 cursor-pointer"
                  >
                    <ChevronLeft size={16} /> Back to Appointments
                  </button>
                  <div className="flex items-center gap-3">
                    {['admin', 'customer_care', 'cc_head'].includes(user?.role) && (
                      <button
                        type="button"
                        onClick={() => setShowLostModal(true)}
                        className="px-5 py-3 bg-rose-600 text-white rounded-2xl hover:bg-rose-700 text-xs font-black uppercase tracking-wider flex items-center gap-2 transition-all shadow-md shadow-rose-200 cursor-pointer"
                      >
                        <XCircle size={16} /> Mark as Lost
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleSaveAndNext('lead')}
                      className="px-6 py-3 bg-[#143E78] text-white rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-[#0f2c56] transition-all flex items-center gap-2 shadow-lg shadow-[#143E78]/20 cursor-pointer"
                    >
                      <span>Next: Clinical Info</span>
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 📋 STAGE 2: CLINICAL INFO (Interactive Form Workspace) */}
          {activeTab === 'clinical' && (
            <div className="space-y-6 animate-in fade-in duration-200">
              {/* Doctor's Prescription Findings (Diagnosis, Follow-Up, Tests) */}
              {prescription && (prescription.provisionalDiagnosis || prescription.followUp || (prescription.investigations && prescription.investigations.length > 0) || prescription.advice) && (
                <div className="p-5 bg-gradient-to-r from-blue-50/90 via-indigo-50/50 to-blue-50/70 border-2 border-blue-200 rounded-3xl space-y-3.5 shadow-sm">
                  <div className="flex items-center justify-between gap-2 flex-wrap border-b border-blue-200/70 pb-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-xl bg-[#143E78] text-white flex items-center justify-center font-black shrink-0 shadow-sm">
                        <Stethoscope size={16} />
                      </div>
                      <div>
                        <h4 className="text-sm font-black text-[#143E78] tracking-tight">Doctor's Consultation &amp; Prescription Record</h4>
                        <p className="text-[10px] font-bold text-slate-500">Live clinical assessment recorded by attending physician</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2.5 py-1 text-[10px] font-black uppercase rounded-lg border ${prescription.status === 'verified' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                        prescription.status === 'sent_to_lead' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                          prescription.status === 'rejected' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                            'bg-amber-50 text-amber-700 border-amber-200'
                        }`}>
                        {prescription.status?.replace(/_/g, ' ')}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                    {prescription.provisionalDiagnosis && (
                      <div className="p-3.5 bg-white/90 rounded-2xl border border-blue-100 space-y-1 shadow-2xs">
                        <span className="text-[10px] font-black text-[#143E78] uppercase tracking-widest flex items-center gap-1.5">
                          <Activity size={13} /> Diagnosis / Health Condition
                        </span>
                        <p className="text-xs font-black text-slate-900 leading-snug">{prescription.provisionalDiagnosis}</p>
                      </div>
                    )}

                    {prescription.followUp && (
                      <div className="p-3.5 bg-white/90 rounded-2xl border border-indigo-100 space-y-1 shadow-2xs">
                        <span className="text-[10px] font-black text-indigo-700 uppercase tracking-widest flex items-center gap-1.5">
                          <Calendar size={13} /> Next Follow-Up / Visit
                        </span>
                        <p className="text-xs font-black text-indigo-950 leading-snug">{prescription.followUp}</p>
                      </div>
                    )}

                    {prescription.advice && (
                      <div className="p-3.5 bg-white/90 rounded-2xl border border-emerald-100 space-y-1 shadow-2xs md:col-span-2">
                        <span className="text-[10px] font-black text-emerald-800 uppercase tracking-widest flex items-center gap-1.5">
                          <Lightbulb size={13} /> Doctor's Clinical Advice &amp; Guidance
                        </span>
                        <p className="text-xs font-bold text-emerald-950 leading-relaxed whitespace-pre-wrap">{prescription.advice}</p>
                      </div>
                    )}
                  </div>

                  {prescription.investigations && prescription.investigations.length > 0 && (
                    <div className="pt-2 border-t border-blue-200/70 space-y-2">
                      <span className="text-[10px] font-black text-[#143E78] uppercase tracking-widest flex items-center gap-1.5">
                        <TestTubes size={13} /> Recommended Diagnostic Lab Tests &amp; Scans ({prescription.investigations.length})
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {prescription.investigations.map((inv, idx) => (
                          <div key={inv.id || idx} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-blue-200 text-[#143E78] rounded-xl text-xs font-black shadow-2xs">
                            <span>{inv.icon || '🔬'}</span>
                            <span>{inv.label || inv.name}</span>
                            {inv.category && <span className="text-[9px] font-bold text-slate-400">({inv.category})</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* 2-Column Responsive Form Workspace */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* LEFT COLUMN: Clinical Notes & History (7 Cols) */}
                <div className="lg:col-span-7 space-y-5">
                  {/* Chief Complaints Card */}
                  <div className="bg-white rounded-3xl border-2 border-slate-100 hover:border-slate-200 p-5 sm:p-6 shadow-sm transition-all space-y-3.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-xl bg-rose-50 border border-rose-100 flex items-center justify-center text-rose-600 shrink-0">
                          <Heart size={16} />
                        </div>
                        <label className="text-sm font-black text-slate-900 tracking-tight">
                          Chief Complaints & Symptoms
                        </label>
                      </div>
                      {clinicalInfo.chiefComplaints && (
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">
                          {clinicalInfo.chiefComplaints.length} chars
                        </span>
                      )}
                    </div>

                    <textarea
                      value={clinicalInfo.chiefComplaints}
                      onChange={(e) => setClinicalInfo(prev => ({ ...prev, chiefComplaints: e.target.value }))}
                      placeholder="Describe patient's symptoms, duration, pain scale, or reasons for surgical consultation..."
                      rows={4}
                      className="w-full px-4 py-3.5 border-2 rounded-2xl font-bold text-sm text-slate-800 outline-none transition-all resize-none shadow-inner bg-slate-50/50 hover:bg-white focus:bg-white border-slate-200 focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10"
                    />
                  </div>

                  {/* Past Medical History Card */}
                  <div className="bg-white rounded-3xl border-2 border-slate-100 hover:border-slate-200 p-5 sm:p-6 shadow-sm transition-all space-y-3.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 shrink-0">
                          <ClipboardList size={16} />
                        </div>
                        <label className="text-sm font-black text-slate-900 tracking-tight">
                          Past Medical & Surgical History
                        </label>
                      </div>
                      {clinicalInfo.pastMedicalHistory && (
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">
                          {clinicalInfo.pastMedicalHistory.length} chars
                        </span>
                      )}
                    </div>

                    <textarea
                      value={clinicalInfo.pastMedicalHistory}
                      onChange={(e) => setClinicalInfo(prev => ({ ...prev, pastMedicalHistory: e.target.value }))}
                      placeholder="Document prior surgeries, major illnesses, drug allergies (e.g., Penicillin), past hospital admissions..."
                      rows={3}
                      className="w-full px-4 py-3.5 border-2 rounded-2xl font-bold text-sm text-slate-800 outline-none transition-all resize-none shadow-inner bg-slate-50/50 hover:bg-white focus:bg-white border-slate-200 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-600/10"
                    />
                  </div>

                  {/* Comorbidities Card */}
                  <div className="bg-white rounded-3xl border-2 border-slate-100 hover:border-slate-200 p-5 sm:p-6 shadow-sm transition-all space-y-3.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-600 shrink-0">
                          <AlertCircle size={16} />
                        </div>
                        <label className="text-sm font-black text-slate-900 tracking-tight">
                          Comorbidities & Chronic Risk Factors
                        </label>
                      </div>
                      {clinicalInfo.comorbidities && (
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">
                          {clinicalInfo.comorbidities.length} chars
                        </span>
                      )}
                    </div>

                    <textarea
                      value={clinicalInfo.comorbidities}
                      onChange={(e) => setClinicalInfo(prev => ({ ...prev, comorbidities: e.target.value }))}
                      placeholder="Document ongoing chronic conditions, blood pressure stability, regular insulin/medications..."
                      rows={3}
                      className="w-full px-4 py-3.5 border-2 rounded-2xl font-bold text-sm text-slate-800 outline-none transition-all resize-none shadow-inner bg-slate-50/50 hover:bg-white focus:bg-white border-slate-200 focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10"
                    />
                  </div>
                </div>

                {/* RIGHT COLUMN: Scheme & Documents Vault (5 Cols) */}
                <div className="lg:col-span-5 space-y-5">
                  {/* Identity & Verification Card */}
                  <div className="bg-white rounded-3xl border-2 border-slate-100 hover:border-slate-200 p-5 sm:p-6 shadow-sm transition-all space-y-4">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 shrink-0">
                        <BadgeCheck size={16} />
                      </div>
                      <div>
                        <h4 className="text-sm font-black text-slate-900 tracking-tight">Identity & Verification</h4>
                        <p className="text-[11px] font-bold text-slate-400">Govt ID & eligibility cards</p>
                      </div>
                    </div>

                    {/* Patient ID Proof */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs font-black text-slate-600 uppercase tracking-wider">
                        <span className="flex items-center gap-1.5">
                          <FileCheck size={14} className="text-[#143E78]" />
                          Patient ID Proof (Aadhaar / Voter)
                        </span>
                        {idProof && (
                          <span className="text-[10px] font-black text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
                            Attached
                          </span>
                        )}
                      </div>
                      <input
                        type="file"
                        id="id-proof-upload"
                        className="hidden"
                        accept=".pdf,image/*"
                        onChange={(e) => handleClinicalFileUpload(Array.from(e.target.files), 'id_proof')}
                      />
                      {idProof ? (
                        <div className="flex items-center justify-between p-3 bg-emerald-50/70 rounded-2xl border-2 border-emerald-200 shadow-sm group">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="w-8 h-8 rounded-xl bg-emerald-500 text-white flex items-center justify-center shrink-0">
                              <CheckCircle2 size={16} />
                            </div>
                            <span className="text-xs font-bold text-slate-800 truncate">{idProof.name || 'ID Proof Attached'}</span>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => handleFilePreview(idProof)}
                              className="p-1.5 text-emerald-700 hover:text-emerald-900 bg-white hover:bg-emerald-100 rounded-xl border border-emerald-200 transition-all shadow-sm cursor-pointer"
                              title="Preview Document"
                            >
                              <Eye size={15} />
                            </button>
                            <button
                              type="button"
                              onClick={() => setIdProof(null)}
                              className="p-1.5 text-rose-500 hover:text-rose-700 bg-white hover:bg-rose-50 rounded-xl border border-rose-200 transition-all shadow-sm cursor-pointer"
                              title="Remove Document"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => document.getElementById('id-proof-upload')?.click()}
                          disabled={uploadingDoc}
                          className="w-full px-4 py-3 border-2 border-dashed border-slate-200 hover:border-[#143E78] bg-slate-50/50 hover:bg-[#143E78]/5 rounded-2xl text-xs font-black text-slate-600 hover:text-[#143E78] transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-98"
                        >
                          <Upload size={15} /> Upload Patient ID Proof
                        </button>
                      )}
                    </div>

                    {/* Ayushman Card */}
                    {(coverageMode === 'ayushman' || ayushmanCard) && (
                      <div className="space-y-2 pt-2 border-t border-slate-100">
                        <div className="flex items-center justify-between text-xs font-black text-slate-600 uppercase tracking-wider">
                          <span className="flex items-center gap-1.5">
                            <Shield size={14} className="text-amber-500" />
                            Ayushman Card (PM-JAY)
                          </span>
                          {ayushmanCard && (
                            <span className="text-[10px] font-black text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200">
                              Attached
                            </span>
                          )}
                        </div>
                        <input
                          type="file"
                          id="ayushman-card-upload"
                          className="hidden"
                          accept=".pdf,image/*"
                          onChange={(e) => handleClinicalFileUpload(Array.from(e.target.files), 'ayushman')}
                        />
                        {ayushmanCard ? (
                          <div className="flex items-center justify-between p-3 bg-amber-50/80 rounded-2xl border-2 border-amber-200 shadow-sm group">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className="w-8 h-8 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0">
                                <CheckCircle2 size={16} />
                              </div>
                              <span className="text-xs font-bold text-slate-800 truncate">{ayushmanCard.name || 'Ayushman Card Attached'}</span>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                type="button"
                                onClick={() => handleFilePreview(ayushmanCard)}
                                className="p-1.5 text-amber-700 hover:text-amber-900 bg-white hover:bg-amber-100 rounded-xl border border-amber-200 transition-all shadow-sm cursor-pointer"
                                title="Preview Document"
                              >
                                <Eye size={15} />
                              </button>
                              <button
                                type="button"
                                onClick={() => setAyushmanCard(null)}
                                className="p-1.5 text-rose-500 hover:text-rose-700 bg-white hover:bg-rose-50 rounded-xl border border-rose-200 transition-all shadow-sm cursor-pointer"
                                title="Remove Document"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => document.getElementById('ayushman-card-upload')?.click()}
                            disabled={uploadingDoc}
                            className="w-full px-4 py-3 border-2 border-dashed border-amber-200 hover:border-amber-500 bg-amber-50/40 hover:bg-amber-50 rounded-2xl text-xs font-black text-amber-800 transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-98"
                          >
                            <Upload size={15} /> Upload Ayushman Card
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Diagnostic Reports Vault Card */}
                  <div className="bg-white rounded-3xl border-2 border-slate-100 hover:border-slate-200 p-5 sm:p-6 shadow-sm transition-all space-y-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-xl bg-purple-50 border border-purple-100 flex items-center justify-center text-purple-600 shrink-0">
                          <FileText size={16} />
                        </div>
                        <div>
                          <h4 className="text-sm font-black text-slate-900 tracking-tight">Diagnostic Reports Vault</h4>
                          <p className="text-[11px] font-bold text-slate-400">Scans, ultrasound, blood tests, prescriptions</p>
                        </div>
                      </div>
                      <span className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-xl text-[10px] font-black uppercase tracking-wider">
                        {medicalReports.length} / 15
                      </span>
                    </div>

                    <input
                      type="file"
                      id="medical-reports-upload"
                      className="hidden"
                      accept=".pdf,image/*"
                      multiple
                      onChange={(e) => handleClinicalFileUpload(Array.from(e.target.files), 'medical')}
                    />

                    {/* Drag & Drop Upload Zone */}
                    <div
                      onDragOver={(e) => { e.preventDefault(); setIsDraggingReports(true); }}
                      onDragLeave={() => setIsDraggingReports(false)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDraggingReports(false);
                        if (e.dataTransfer.files?.length) {
                          handleClinicalFileUpload(Array.from(e.dataTransfer.files), 'medical');
                        }
                      }}
                      onClick={() => document.getElementById('medical-reports-upload')?.click()}
                      className={`p-5 rounded-2xl border-2 border-dashed text-center cursor-pointer transition-all ${isDraggingReports
                        ? 'border-[#143E78] bg-[#143E78]/10 scale-[1.01]'
                        : 'border-slate-200 hover:border-[#143E78] bg-slate-50/50 hover:bg-[#143E78]/5'
                        }`}
                    >
                      {uploadingDoc ? (
                        <div className="flex flex-col items-center justify-center py-2 gap-2">
                          <Loader2 size={24} className="animate-spin text-[#143E78]" />
                          <p className="text-xs font-black text-[#143E78] uppercase tracking-wider">Uploading documents...</p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center gap-1.5">
                          <div className="w-10 h-10 rounded-2xl bg-white shadow-sm border border-slate-100 flex items-center justify-center text-[#143E78]">
                            <Upload size={18} />
                          </div>
                          <p className="text-xs font-black text-slate-700 mt-1">
                            Click to upload or drag & drop reports
                          </p>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                            PDF, PNG, JPG (Up to 50MB each)
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Attached Medical Reports List */}
                    {medicalReports.length > 0 ? (
                      <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                        {medicalReports.map((report, idx) => {
                          const isPDF = report.url?.toLowerCase().endsWith('.pdf') || report.name?.toLowerCase().endsWith('.pdf');
                          return (
                            <div
                              key={idx}
                              className="flex items-center justify-between p-2.5 bg-slate-50 hover:bg-blue-50/50 rounded-2xl border border-slate-200/80 transition-all group"
                            >
                              <div className="flex items-center gap-2.5 min-w-0">
                                <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-white shrink-0 ${isPDF ? 'bg-rose-500' : 'bg-indigo-500'}`}>
                                  {isPDF ? <FileText size={15} /> : <Image size={15} />}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-xs font-bold text-slate-800 truncate">{report.name || `Medical Report ${idx + 1}`}</p>
                                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                                    {isPDF ? 'PDF Document' : 'Image Scan'}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <button
                                  type="button"
                                  onClick={() => handleFilePreview(report)}
                                  className="p-1.5 text-[#143E78] hover:text-[#143E78] bg-white hover:bg-blue-100 rounded-xl border border-slate-200 transition-all shadow-sm cursor-pointer"
                                  title="Preview Document"
                                >
                                  <Eye size={15} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setMedicalReports(prev => prev.filter((_, i) => i !== idx))}
                                  className="p-1.5 text-rose-500 hover:text-rose-700 bg-white hover:bg-rose-50 rounded-xl border border-rose-200 transition-all shadow-sm cursor-pointer"
                                  title="Remove Document"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-4 bg-slate-50/70 rounded-2xl border border-dashed border-slate-200 text-center">
                        <Paperclip size={20} className="mx-auto text-slate-300 mb-1" />
                        <p className="text-[11px] font-bold text-slate-400">No medical reports attached yet</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer Navigation Bar */}
              <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setActiveTab('lead')}
                  className="px-5 py-3 border-2 border-slate-200 text-slate-700 rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-slate-50 transition-all flex items-center gap-2 cursor-pointer"
                >
                  <ChevronLeft size={16} /> Back
                </button>
                <div className="flex items-center gap-3">
                  {['admin', 'customer_care', 'cc_head'].includes(user?.role) && (
                    <button
                      type="button"
                      onClick={() => setShowLostModal(true)}
                      className="px-5 py-3 bg-rose-600 text-white rounded-2xl hover:bg-rose-700 text-xs font-black uppercase tracking-wider flex items-center gap-2 transition-all shadow-md shadow-rose-200 cursor-pointer"
                    >
                      <XCircle size={16} /> Mark as Lost
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleSaveAndNext('clinical')}
                    disabled={savingStage}
                    className="px-6 py-3 bg-[#143E78] text-white rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-[#0f2c56] disabled:opacity-50 transition-all flex items-center gap-2 shadow-lg shadow-[#143E78]/20 cursor-pointer"
                  >
                    {savingStage ? <Loader2 size={16} className="animate-spin" /> : null}
                    <span>{savingStage ? 'Saving...' : 'Next: Doctor Type'}</span>
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 📋 STAGE 3: DOCTOR TYPE & ASSIGNMENT (Exact UI Matching User Request) */}
          {activeTab === 'doctor_type' && (
            <div className="space-y-6 animate-in fade-in duration-200">
              <div className="bg-white rounded-3xl border-2 border-slate-100 hover:border-slate-200 p-5 sm:p-6 shadow-sm space-y-6">
                {/* Header */}
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#143E78] to-[#2B5CA6] text-white flex items-center justify-center font-bold text-lg shadow-md shadow-[#143E78]/15">
                    <Stethoscope size={24} />
                  </div>
                  <div>
                    <h2 className="text-xl font-black text-slate-900 tracking-tight">Doctor Assignment</h2>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">CHOOSE CONSULTATION TYPE AND DOCTOR</p>
                  </div>
                </div>

                {/* 1. Consultation Type Selector (Top) */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                    <Sparkles size={14} className="text-amber-500" />
                    Consultation Type
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {APPOINTMENT_TYPES.map(type => {
                      const isSelected = formData.appointmentType === type.value;
                      const colorClass = APPOINTMENT_TYPE_COLORS[type.value] || 'bg-slate-50 text-slate-700 border-slate-200';
                      return (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, appointmentType: type.value }))}
                          className={`p-4 rounded-2xl border-2 text-left transition-all duration-200 cursor-pointer ${isSelected
                            ? `${colorClass} border-current shadow-md`
                            : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm'
                            }`}
                        >
                          <p className="text-sm font-black uppercase tracking-tight">{type.label}</p>
                          <p className="text-[10px] font-bold text-slate-500 mt-1">{type.desc}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 2. Mode of Communication Selector (Second) */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                    <Sparkles size={14} className="text-indigo-500" />
                    Mode of Communication
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {COMMUNICATION_MODES.map(mode => {
                      const isSelected = formData.communicationMode === mode.value;
                      const colorClass = COMMUNICATION_MODE_COLORS[mode.value] || 'bg-slate-50 text-slate-700 border-slate-200';
                      return (
                        <button
                          key={mode.value}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, communicationMode: mode.value }))}
                          className={`p-4 rounded-2xl border-2 text-left transition-all duration-200 cursor-pointer ${isSelected
                            ? `${colorClass} border-current shadow-md`
                            : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm'
                            }`}
                        >
                          <p className="text-sm font-black uppercase tracking-tight">{mode.label}</p>
                          <p className="text-[10px] font-bold text-slate-500 mt-1">{mode.desc}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 3. 2-Choice Cards: IN-HOUSE DOCTOR vs EMPANELMENT (Third) */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                    <ShieldCheck size={14} className="text-emerald-600" />
                    Doctor Category & Pricing Model
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => {
                        setDoctorType('in_house');
                        setFormData(prev => ({ ...prev, appointmentDoctorId: '' }));
                        setSelectedCouponId('');
                      }}
                      className={`p-5 rounded-2xl border-2 text-left transition-all duration-200 cursor-pointer ${doctorType === 'in_house'
                        ? 'bg-emerald-50 border-emerald-500 shadow-lg shadow-emerald-100'
                        : 'bg-white border-slate-200 hover:border-slate-300'
                        }`}
                    >
                      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center mb-3 ${doctorType === 'in_house' ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500'
                        }`}>
                        <ShieldCheck size={20} />
                      </div>
                      <p className="text-sm font-black text-slate-900 uppercase tracking-tight">In-House Doctor</p>
                      <p className="text-xs font-bold text-slate-500 mt-1">Free consultation. No payment required.</p>
                      {doctorType === 'in_house' && (
                        <span className="inline-flex items-center gap-1 mt-3 px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-lg text-[10px] font-black uppercase tracking-wider">
                          <CheckCircle size={12} /> Selected
                        </span>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setDoctorType('coupon');
                        setFormData(prev => ({ ...prev, appointmentDoctorId: '' }));
                        setSelectedCouponId('');
                        setPricing(prev => {
                          const taxType = (prev.taxType && prev.taxType !== 'none') ? prev.taxType : 'cgst_sgst';
                          const taxPct = (prev.taxPercentage && prev.taxPercentage > 0) ? prev.taxPercentage : 18;
                          const pricingResult = calculateFromTotal(prev.finalPrice || 1000, prev.platformFee, prev.otherCharges, prev.discountPercentage, taxType, taxPct);
                          return {
                            ...prev,
                            ...pricingResult,
                            taxType,
                            taxPercentage: taxPct,
                            editing: true
                          };
                        });
                      }}
                      className={`p-5 rounded-2xl border-2 text-left transition-all duration-200 cursor-pointer ${doctorType === 'coupon'
                        ? 'bg-amber-50 border-amber-500 shadow-lg shadow-amber-100'
                        : 'bg-white border-slate-200 hover:border-slate-300'
                        }`}
                    >
                      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center mb-3 ${doctorType === 'coupon' ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-500'
                        }`}>
                        <Tag size={20} />
                      </div>
                      <p className="text-sm font-black text-slate-900 uppercase tracking-tight">Empanelment</p>
                      <p className="text-xs font-bold text-slate-500 mt-1">Paid consultation. Payment link will be generated.</p>
                      {doctorType === 'coupon' && (
                        <span className="inline-flex items-center gap-1 mt-3 px-2.5 py-1 bg-amber-100 text-amber-700 rounded-lg text-[10px] font-black uppercase tracking-wider">
                          <CheckCircle size={12} /> Selected
                        </span>
                      )}
                    </button>
                  </div>
                </div>

                {/* Coupon & Pricing Tax Container (Shown for Empanelment) */}
                {doctorType === 'coupon' && ['admin', 'customer_care', 'cc_head'].includes(user?.role) && (
                  <div className="p-5 bg-amber-50/70 rounded-2xl border-2 border-amber-200 space-y-4">
                    <label className="flex items-center gap-2 text-[11px] font-black text-amber-800 uppercase tracking-widest">
                      <Tag size={14} className="text-amber-600" /> Select Coupon <span className="text-slate-400 font-bold">(Optional)</span>
                    </label>
                    <div className="relative">
                      <select
                        value={selectedCouponId}
                        onChange={(e) => {
                          const coupon = coupons.find(p => p._id === e.target.value);
                          setSelectedCouponId(e.target.value);
                          if (coupon) {
                            const taxType = (coupon.taxType && coupon.taxType !== 'none') ? coupon.taxType : 'cgst_sgst';
                            const taxPct = (coupon.taxPercentage !== undefined && coupon.taxPercentage !== null && coupon.taxPercentage > 0) ? coupon.taxPercentage : 18;
                            const pricingResult = calculateFromTotal(
                              pricing.finalPrice,
                              pricing.platformFee,
                              pricing.otherCharges || 0,
                              coupon.discountPercentage || 0,
                              taxType,
                              taxPct
                            );
                            setPricing(prev => ({
                              ...prev,
                              ...pricingResult,
                              discountPercentage: coupon.discountPercentage || 0,
                              editing: true,
                              taxType,
                              taxPercentage: taxPct,
                              taxName: coupon.taxName || ''
                            }));
                            setCouponOriginal({
                              discountPercentage: coupon.discountPercentage || 0,
                              taxType,
                              taxPercentage: taxPct,
                              taxName: coupon.taxName || ''
                            });
                          } else {
                            const pricingResult = calculateFromTotal(pricing.finalPrice, pricing.platformFee, pricing.otherCharges || 0, 0, 'cgst_sgst', 18);
                            setPricing(prev => ({
                              ...prev,
                              ...pricingResult,
                              discountPercentage: 0,
                              editing: true,
                              taxType: 'cgst_sgst',
                              taxPercentage: 18,
                              taxName: ''
                            }));
                            setCouponOriginal(null);
                          }
                        }}
                        className="w-full px-4 py-3.5 bg-white border-2 border-amber-200 rounded-2xl font-bold text-sm focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10 transition-all outline-none appearance-none cursor-pointer"
                      >
                        <option value="">-- Select coupon --</option>
                        {coupons.map(coupon => (
                          <option key={coupon._id} value={coupon._id}>
                            {coupon?.name} {coupon.discountPercentage > 0 ? `(Save ${coupon.discountPercentage}%)` : ''}
                          </option>
                        ))}
                      </select>
                      <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 rotate-90 w-4 h-4 text-amber-400 pointer-events-none" />
                    </div>
                    {coupons.length === 0 && (
                      <p className="text-xs font-bold text-amber-600 uppercase tracking-wider">No coupons available.</p>
                    )}

                    {/* Coupon Pricing & Tax Editor */}
                    <div className="mt-3 p-4 bg-white rounded-2xl border border-amber-200 space-y-3 shadow-2xs">
                      <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Pricing & Tax</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Total Patient Invoice (₹)</label>
                          <input
                            type="number"
                            min="0"
                            value={pricing.finalPrice}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 0;
                              const pricingResult = calculateFromTotal(val, pricing.platformFee, pricing.otherCharges, pricing.discountPercentage, pricing.taxType, pricing.taxPercentage);
                              setPricing(prev => ({ ...prev, ...pricingResult }));
                            }}
                            className="w-full px-3 py-2 border border-amber-200 rounded-xl font-bold text-sm outline-none bg-white focus:border-amber-500"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Platform Charges (₹)</label>
                          <input
                            type="number"
                            min="0"
                            value={pricing.platformFee}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 0;
                              const pricingResult = calculateFromTotal(pricing.finalPrice, val, pricing.otherCharges, pricing.discountPercentage, pricing.taxType, pricing.taxPercentage);
                              setPricing(prev => ({ ...prev, ...pricingResult }));
                            }}
                            className="w-full px-3 py-2 border border-amber-200 rounded-xl font-bold text-sm outline-none bg-white focus:border-amber-500"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Consultation Fee (₹)</label>
                          <div className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl font-bold text-sm text-slate-700">
                            {formatCurrency(pricing.doctorFee)}
                          </div>
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Discount (%)</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={pricing.discountPercentage}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 0;
                              const pricingResult = calculateFromTotal(pricing.finalPrice, pricing.platformFee, pricing.otherCharges, val, pricing.taxType, pricing.taxPercentage);
                              setPricing(prev => ({ ...prev, discountPercentage: val, ...pricingResult }));
                            }}
                            className="w-full px-3 py-2 border border-amber-200 rounded-xl font-bold text-sm outline-none bg-white focus:border-amber-500"
                          />
                        </div>

                        <div className="col-span-2">
                          <div className="flex items-center justify-between bg-slate-50 border-2 border-slate-200 rounded-xl px-4 py-2.5">
                            <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">SUB TOTAL (₹)</span>
                            <span className="text-sm font-black text-slate-900">{formatCurrency(pricing.netAmount || 0)}</span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Tax Type</label>
                          <select
                            value={pricing.taxType}
                            onChange={(e) => {
                              const newTaxType = e.target.value;
                              const newTaxPct = (newTaxType === 'cgst_sgst' || newTaxType === 'gst') ? 18 : (newTaxType === 'none' ? 0 : pricing.taxPercentage);
                              const pricingResult = calculateFromTotal(pricing.finalPrice, pricing.platformFee, pricing.otherCharges, pricing.discountPercentage, newTaxType, newTaxPct);
                              setPricing(prev => ({ ...prev, taxType: newTaxType, taxPercentage: newTaxPct, ...pricingResult }));
                            }}
                            className="w-full px-3 py-2 border border-amber-200 rounded-xl font-bold text-sm outline-none appearance-none bg-white focus:border-amber-500 cursor-pointer"
                          >
                            <option value="none">None</option>
                            <option value="gst">GST</option>
                            <option value="cgst_sgst">CGST + SGST</option>
                            <option value="custom">Custom</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Tax Percentage (%)</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={pricing.taxPercentage}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 0;
                              const pricingResult = calculateFromTotal(pricing.finalPrice, pricing.platformFee, pricing.otherCharges, pricing.discountPercentage, pricing.taxType, val);
                              setPricing(prev => ({ ...prev, taxPercentage: val, ...pricingResult }));
                            }}
                            className="w-full px-3 py-2 border border-amber-200 rounded-xl font-bold text-sm outline-none bg-white focus:border-amber-500"
                          />
                        </div>
                      </div>

                      {/* Pricing Breakdown Lines */}
                      {(() => {
                        const taxPercentage = Number(pricing.taxPercentage || 0);
                        const taxType = pricing.taxType || 'none';
                        const pf = Number(pricing.platformFee || 0);
                        const other = Number(pricing.otherCharges || 0);
                        const discountAmount = pf > 0 && pricing.discountPercentage > 0 ? Math.round((pf * pricing.discountPercentage) / 100) : 0;
                        const taxAmount = pricing.taxAmount || 0;
                        const calculatedFinal = pricing.finalPrice || 0;
                        const netAmount = pricing.netAmount || 0;

                        return (
                          <div className="pt-3 border-t border-amber-100 space-y-2 text-xs">
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Consultation Fee</span>
                              <span className="text-sm font-black text-slate-900">{formatCurrency(pricing.doctorFee)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Platform Charges</span>
                              <span className="text-sm font-black text-slate-900">{formatCurrency(pf)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Other Charges</span>
                              <span className="text-sm font-black text-slate-900">{formatCurrency(other)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">SUB TOTAL</span>
                              <span className="text-sm font-black text-slate-900">{formatCurrency(netAmount)}</span>
                            </div>
                            {discountAmount > 0 && (
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Discount</span>
                                <span className="text-sm font-black text-emerald-600">-{formatCurrency(discountAmount)}</span>
                              </div>
                            )}
                            {taxAmount > 0 && (
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                                  {taxType === 'cgst_sgst' ? `CGST + SGST @ ${taxPercentage}%` : `GST @ ${taxPercentage}%`}
                                </span>
                                <span className="text-sm font-black text-amber-600">{formatCurrency(taxAmount)}</span>
                              </div>
                            )}
                            <div className="pt-3 border-t-2 border-dashed border-amber-200 flex items-center justify-between">
                              <span className="text-sm font-black text-slate-600 uppercase tracking-wider">Total Payable</span>
                              <span className="text-2xl font-black text-[#143E78]">{formatCurrency(calculatedFinal)}</span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* Info Alerts */}
                {doctorType === 'coupon' && ['admin', 'customer_care', 'cc_head'].includes(user?.role) && !selectedCouponId && (
                  <div className="p-4 bg-blue-50 rounded-2xl border-2 border-blue-200">
                    <p className="text-xs font-bold text-blue-700">
                      No coupon selected. Financials will use default settings. You can select a coupon later to apply discount.
                    </p>
                  </div>
                )}

                {doctorType === 'coupon' && !['admin', 'customer_care', 'cc_head'].includes(user?.role) && (
                  <div className="p-4 bg-blue-50 rounded-2xl border-2 border-blue-200">
                    <p className="text-xs font-bold text-blue-700">
                      Empanelment consultation. Payment link amount will be ₹500 by default unless updated by admin.
                    </p>
                  </div>
                )}

                {/* 4. Filter Doctors by Type */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                    <Stethoscope size={14} className="text-[#143E78]" />
                    Doctor Type Filter
                  </label>
                  <div className="relative">
                    <select
                      value={availabilityDoctorType}
                      onChange={(e) => setAvailabilityDoctorType(e.target.value)}
                      className="w-full px-4 py-3.5 border-2 rounded-2xl font-bold text-sm transition-all outline-none appearance-none bg-white border-slate-200 focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 cursor-pointer"
                    >
                      <option value="">All Doctor Types (Show All Doctors)</option>
                      <option value="Surgeon">Surgeon</option>
                      <option value="Anesthesiologist">Anesthesiologist</option>
                      <option value="Specialist">Specialist</option>
                    </select>
                    <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 rotate-90 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>

                {/* 5. Available Doctors Forecast Banner */}
                {loadingForecast ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 size={20} className="animate-spin text-[#143E78]" />
                    <span className="ml-2 text-xs font-bold text-slate-500">Checking availability...</span>
                  </div>
                ) : (
                  <div className="p-4 bg-[#143E78]/5 rounded-2xl border-2 border-[#143E78]/10">
                    <p className="text-[10px] font-black text-[#143E78] uppercase tracking-widest mb-1">
                      Available Doctors: {doctors.length}
                    </p>
                    <p className="text-xs font-bold text-slate-600">
                      Showing all registered doctors in the system
                    </p>
                  </div>
                )}

                {/* 6. Doctor Select Dropdown */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                    <Stethoscope size={14} className="text-[#143E78]" />
                    Doctor <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      value={formData.appointmentDoctorId}
                      onChange={(e) => setFormData(prev => ({ ...prev, appointmentDoctorId: e.target.value }))}
                      className="w-full px-4 py-3.5 border-2 rounded-2xl font-bold text-sm transition-all outline-none appearance-none bg-white border-slate-200 focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 cursor-pointer"
                    >
                      <option value="">-- Select doctor --</option>
                      {doctors
                        .filter(doc => {
                          if (availabilityDoctorType) {
                            const dt = doc.doctorType || doc.user?.doctorCategory || '';
                            if (dt.toLowerCase() !== availabilityDoctorType.toLowerCase()) return false;
                          }
                          return true;
                        })
                        .map(doc => {
                          const docId = doc._id || doc.id;
                          const firstName = doc.user?.firstName || doc.firstName || doc.name?.split(' ')[0] || '';
                          const lastName = doc.user?.lastName || doc.lastName || doc.name?.split(' ').slice(1).join(' ') || '';
                          const docName = (firstName || lastName) ? `${firstName} ${lastName}`.trim() : (doc.user?.name || doc.name || doc.doctorName || 'Doctor');
                          const phone = doc.user?.phone || doc.phone || doc.contactNumber || '';
                          const spec = doc.specialization || doc.doctorType || doc.user?.specialization || 'General';
                          const fee = doc.financials?.consultationFee || doc.consultationFee;

                          return (
                            <option key={docId} value={docId}>
                              Dr. {docName} {phone ? `| ${phone}` : ''} | {spec} {fee ? `| ₹${fee}` : ''}
                            </option>
                          );
                        })}
                    </select>
                    <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 rotate-90 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>

                  {formData.appointmentDoctorId && doctorConsultationFee > 0 && (
                    <div className="p-3 bg-[#143E78]/5 rounded-xl border border-[#143E78]/10 flex items-center justify-between">
                      <span className="text-[10px] font-black text-[#143E78] uppercase tracking-widest">Consultation Fee</span>
                      <span className="text-sm font-black text-[#143E78]">{formatCurrency(doctorConsultationFee)}</span>
                    </div>
                  )}

                  {doctors.length === 0 && (
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">No doctors available.</p>
                  )}
                </div>

                {/* Footer Navigation Bar */}
                <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setActiveTab('clinical')}
                    className="px-5 py-3 border-2 border-slate-200 text-slate-700 rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-slate-50 transition-all flex items-center gap-2 cursor-pointer"
                  >
                    <ChevronLeft size={16} /> Back
                  </button>
                  <div className="flex items-center gap-3">
                    {['admin', 'customer_care', 'cc_head'].includes(user?.role) && (
                      <button
                        type="button"
                        onClick={() => setShowLostModal(true)}
                        className="px-5 py-3 bg-rose-600 text-white rounded-2xl hover:bg-rose-700 text-xs font-black uppercase tracking-wider flex items-center gap-2 transition-all shadow-md shadow-rose-200 cursor-pointer"
                      >
                        <XCircle size={16} /> Mark as Lost
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleSaveAndNext('doctor_type')}
                      disabled={savingStage}
                      className="px-6 py-3 bg-[#143E78] text-white rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-[#0f2c56] disabled:opacity-50 transition-all flex items-center gap-2 shadow-lg shadow-[#143E78]/20 cursor-pointer"
                    >
                      {savingStage ? <Loader2 size={16} className="animate-spin" /> : null}
                      <span>{savingStage ? 'Assigning...' : 'Next: Schedule'}</span>
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 📋 STAGE 4: SCHEDULE */}
          {activeTab === 'schedule' && (
            <div className="space-y-6 animate-in fade-in duration-200">
              <div className="bg-white rounded-3xl border-2 border-slate-100 hover:border-slate-200 p-5 sm:p-6 shadow-sm space-y-6">
                <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-2xl bg-indigo-50 text-indigo-700 flex items-center justify-center font-black shrink-0">
                      <Calendar size={18} />
                    </div>
                    <div>
                      <h3 className="text-base font-black text-slate-900">Schedule Appointment</h3>
                      <p className="text-[11px] font-bold text-slate-400">SELECT DATE AND TIME SLOT</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                      <Calendar size={14} className="text-[#143E78]" />
                      Appointment Date <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        min={new Date().toISOString().split('T')[0]}
                        value={formData.appointmentDate || availabilityDate}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData(prev => ({ ...prev, appointmentDate: val, appointmentTimeSlot: '' }));
                          setAvailabilityDate(val);
                        }}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                      />
                      <div className="relative flex-1">
                        <input
                          type="text"
                          value={formatDateDisplay(formData.appointmentDate || availabilityDate)}
                          onChange={(e) => {
                            const formatted = formatWithHyphens(e.target.value);
                            const parsed = parseDateInput(formatted);
                            setFormData(prev => ({ ...prev, appointmentDate: parsed || '', appointmentTimeSlot: '' }));
                            setAvailabilityDate(parsed || '');
                          }}
                          placeholder="DD-MM-YYYY"
                          className={`w-full px-4 py-3.5 border-2 rounded-2xl font-bold text-sm transition-all outline-none ${isPastDate(formData.appointmentDate || availabilityDate)
                            ? 'bg-rose-50/50 border-rose-400 focus:border-rose-500 text-rose-900'
                            : 'bg-white border-slate-200 focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10'
                            }`}
                        />
                        <Calendar size={16} className={`absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${isPastDate(formData.appointmentDate || availabilityDate) ? 'text-rose-500' : 'text-slate-400'}`} />
                      </div>
                    </div>
                    {isPastDate(formData.appointmentDate || availabilityDate) && (
                      <p className="text-[11px] font-bold text-rose-600 flex items-center gap-1.5 mt-1 bg-rose-50 border border-rose-200 rounded-xl px-3 py-1.5 animate-in fade-in">
                        <AlertCircle size={13} className="shrink-0 text-rose-500" />
                        Selected date ({formatDateDisplay(formData.appointmentDate || availabilityDate)}) has already passed. Please select today or an upcoming date.
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                        <Clock size={14} className="text-[#143E78]" />
                        Appointment Time <span className="text-rose-500">*</span>
                      </label>
                      {formData.appointmentTimeSlot && (
                        <span className="px-2.5 py-0.5 bg-[#143E78]/10 text-[#143E78] rounded-lg text-[11px] font-black tracking-wide font-mono">
                          {formatTime12Hour(formData.appointmentTimeSlot)}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <input
                          type="time"
                          value={(() => {
                            const { hour, minute } = parseTimeSlotTo24H(formData.appointmentTimeSlot);
                            return (formData.appointmentTimeSlot && (formData.appointmentTimeSlot.includes(':') || formData.appointmentTimeSlot.length >= 4)) ? `${hour}:${minute}` : (formData.appointmentTimeSlot || '');
                          })()}
                          onChange={(e) => setFormData(prev => ({ ...prev, appointmentTimeSlot: e.target.value }))}
                          className="w-full px-4 py-3.5 border-2 rounded-2xl font-bold text-sm transition-all outline-none bg-white border-slate-200 focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10"
                        />
                      </div>

                      {/* ☀️ / 🌙 AM-PM Selector */}
                      <div className="flex items-center p-1 bg-slate-100/90 rounded-2xl border-2 border-slate-200 shrink-0 h-[48px] shadow-2xs">
                        <button
                          type="button"
                          onClick={() => {
                            const current = getTimeParts(formData.appointmentTimeSlot);
                            let newH = current.hour24;
                            if (current.hour24 >= 12) newH = current.hour24 - 12;
                            const newTime = `${String(newH).padStart(2, '0')}:${current.minute}`;
                            setFormData(prev => ({ ...prev, appointmentTimeSlot: newTime }));
                          }}
                          className={`px-3.5 h-full rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer ${getTimeParts(formData.appointmentTimeSlot).period === 'AM'
                            ? 'bg-[#143E78] text-white shadow-sm'
                            : 'text-slate-500 hover:text-slate-900 hover:bg-slate-200/70'
                            }`}
                        >
                          AM
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const current = getTimeParts(formData.appointmentTimeSlot);
                            let newH = current.hour24;
                            if (current.hour24 < 12) newH = current.hour24 + 12;
                            const newTime = `${String(newH).padStart(2, '0')}:${current.minute}`;
                            setFormData(prev => ({ ...prev, appointmentTimeSlot: newTime }));
                          }}
                          className={`px-3.5 h-full rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer ${getTimeParts(formData.appointmentTimeSlot).period === 'PM'
                            ? 'bg-[#143E78] text-white shadow-sm'
                            : 'text-slate-500 hover:text-slate-900 hover:bg-slate-200/70'
                            }`}
                        >
                          PM
                        </button>
                      </div>
                    </div>
                    {formData.appointmentDoctorId && (formData.appointmentDate || availabilityDate) && (() => {
                      const targetDate = formData.appointmentDate || availabilityDate;
                      const selectedDateObj = new Date(targetDate + 'T00:00:00');
                      const dayName = selectedDateObj.toLocaleDateString('en-US', { weekday: 'long' });
                      const doctorForecast = forecast.find(f => f.doctorId === formData.appointmentDoctorId);
                      const daySchedule = doctorForecast?.weeklySchedule?.find(day => day.day === dayName);
                      const isAvailable = daySchedule?.isAvailable === true;
                      return isAvailable ? (
                        <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">Doctor available on selected date</p>
                      ) : null;
                    })()}
                  </div>
                </div>

                {/* Patient Details Card */}
                {(patientName || formData.patientName || patientPhone) && (
                  <div className="p-5 bg-[#143E78]/5 rounded-2xl border-2 border-[#143E78]/20 space-y-4">
                    <p className="text-[10px] font-black text-[#143E78] uppercase tracking-widest flex items-center gap-2">
                      <ShieldCheck size={14} /> Patient Details
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Patient Name</p>
                        <p className="text-sm font-black text-slate-900 mt-0.5">{formData.patientName || patientName || '-'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Phone</p>
                        <p className="text-sm font-black text-slate-900 mt-0.5">{formData.patientPhone || patientPhone || '-'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Email</p>
                        <input
                          type="email"
                          value={formData.patientEmail !== undefined ? formData.patientEmail : (patientEmail || '')}
                          onChange={(e) => setFormData(prev => ({ ...prev, patientEmail: e.target.value }))}
                          placeholder="Enter patient email"
                          className="w-full mt-0.5 px-3 py-2 border-2 rounded-xl font-bold text-sm transition-all outline-none bg-white border-[#143E78]/20 focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Footer Navigation Bar */}
                <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setActiveTab('doctor_type')}
                    className="px-5 py-3 border-2 border-slate-200 text-slate-700 rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-slate-50 transition-all flex items-center gap-2 cursor-pointer"
                  >
                    <ChevronLeft size={16} /> Back
                  </button>
                  <div className="flex items-center gap-3">
                    {['admin', 'customer_care', 'cc_head'].includes(user?.role) && (
                      <button
                        type="button"
                        onClick={() => setShowLostModal(true)}
                        className="px-5 py-3 bg-rose-600 text-white rounded-2xl hover:bg-rose-700 text-xs font-black uppercase tracking-wider flex items-center gap-2 transition-all shadow-md shadow-rose-200 cursor-pointer"
                      >
                        <XCircle size={16} /> Mark as Lost
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleSaveAndNext('schedule')}
                      disabled={savingStage}
                      className="px-6 py-3 bg-[#143E78] text-white rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-[#0f2c56] disabled:opacity-50 transition-all flex items-center gap-2 shadow-lg shadow-[#143E78]/20 cursor-pointer"
                    >
                      {savingStage ? <Loader2 size={16} className="animate-spin" /> : null}
                      <span>{savingStage ? 'Updating...' : 'Next: Payment'}</span>
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 📋 STAGE 5: PAYMENT & BILLING */}
          {activeTab === 'payment' && (
            <div className="space-y-6 animate-in fade-in duration-200">
              <div className="bg-white rounded-3xl border-2 border-slate-100 hover:border-slate-200 p-5 sm:p-6 shadow-sm space-y-6">
                <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center font-black shrink-0">
                      <CreditCard size={18} />
                    </div>
                    <div>
                      <h3 className="text-base font-black text-slate-900">Payment & Razorpay Gateway</h3>
                      <p className="text-[11px] font-bold text-slate-400">Manage patient billing, link generation, and doctor payouts</p>
                    </div>
                  </div>

                  <span className={`px-3 py-1 text-xs font-black uppercase rounded-xl border ${appointment?.razorpayDetails?.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                    appointment?.status === 'pending_payment' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                      'bg-slate-100 text-slate-700 border-slate-200'
                    }`}>
                    {appointment?.razorpayDetails?.status === 'paid' ? 'Paid' : (appointment?.status === 'pending_payment' ? 'Pending Payment' : 'Unpaid')}
                  </span>
                </div>

                {/* Pricing Breakdown Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3.5">
                  <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block mb-1">Consultation Fee</span>
                    <span className="text-lg font-black text-slate-900">₹{(appointment?.finalPrice || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block mb-1">Payment Status</span>
                    <span className={`text-xs font-black ${appointment?.razorpayDetails?.status === 'paid' ? 'text-emerald-700' : 'text-amber-700'}`}>
                      {appointment?.razorpayDetails?.status === 'paid' ? 'Verified Paid' : 'Pending Payment'}
                    </span>
                  </div>
                  <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block mb-1">Payment Mode / Method</span>
                    <span className="text-xs font-black text-slate-800 uppercase">
                      {Boolean(appointment?.offlinePaymentDetails?.receiptUrl || appointment?.offlinePaymentDetails?.transactionReference || (appointment?.offlinePaymentDetails?.paidAt && (appointment?.status === 'confirmed' || appointment?.status === 'completed')))
                        ? `Offline (${appointment.offlinePaymentDetails?.paymentMethod === 'card_pos' ? 'POS/Card' : appointment.offlinePaymentDetails?.paymentMethod === 'bank_transfer' ? 'Bank Transfer' : (appointment.offlinePaymentDetails?.paymentMethod || 'Cash').toUpperCase()})`
                        : appointment?.razorpayDetails?.status === 'paid'
                          ? 'Online (Razorpay Gateway)'
                          : (appointment?.razorpayDetails?.paymentLinkUrl ? 'Online Link Sent' : 'Pending / Unpaid')}
                    </span>
                  </div>
                  <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block mb-1">
                      {Boolean(appointment?.offlinePaymentDetails?.receiptUrl || appointment?.offlinePaymentDetails?.transactionReference) ? 'Ref / UTR No' : 'Txn / Order ID'}
                    </span>
                    <span className="text-xs font-mono font-bold text-slate-700 truncate block">
                      {(Boolean(appointment?.offlinePaymentDetails?.receiptUrl || appointment?.offlinePaymentDetails?.transactionReference) && appointment?.offlinePaymentDetails?.transactionReference) || (appointment?.razorpayDetails?.status === 'paid' && (appointment?.razorpayDetails?.paymentId || appointment?.razorpayDetails?.orderId)) || '—'}
                    </span>
                  </div>
                </div>

                {/* Payment Method Switcher Tabs */}
                <div className="flex items-center gap-4 border-b border-slate-200">
                  <button
                    type="button"
                    onClick={() => setPaymentModeTab('online')}
                    className={`pb-3 px-2 font-black text-xs uppercase tracking-wider flex items-center gap-2 border-b-2 transition-all cursor-pointer ${paymentModeTab === 'online'
                      ? 'border-[#143E78] text-[#143E78]'
                      : 'border-transparent text-slate-400 hover:text-slate-600'
                      }`}
                  >
                    <CreditCard size={15} /> Online Payment Link (Razorpay)
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentModeTab('offline')}
                    className={`pb-3 px-2 font-black text-xs uppercase tracking-wider flex items-center gap-2 border-b-2 transition-all cursor-pointer ${paymentModeTab === 'offline'
                      ? 'border-emerald-600 text-emerald-700'
                      : 'border-transparent text-slate-400 hover:text-slate-600'
                      }`}
                  >
                    <Receipt size={15} /> Offline Payment / Upload Receipt (Cash / UPI / POS)
                    {appointment?.offlinePaymentDetails?.receiptUrl && (
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full text-[9px] font-black">Verified Receipt</span>
                    )}
                  </button>
                </div>

                {/* TAB 1: Online Payment Link Widget */}
                {paymentModeTab === 'online' && (
                  <div className="p-5 bg-slate-50 border border-slate-200 rounded-2xl space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-black text-slate-700 uppercase tracking-wider">Patient Payment Link</span>
                      {appointment?.razorpayDetails?.status === 'paid' && (
                        <span className="text-xs font-black text-emerald-600 flex items-center gap-1">
                          <CheckCircle2 size={14} /> Settled
                        </span>
                      )}
                    </div>

                    {appointment?.razorpayDetails?.paymentLinkUrl ? (
                      <div className="flex items-center gap-2">
                        <input
                          readOnly
                          value={appointment.razorpayDetails.paymentLinkUrl}
                          className="flex-1 px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-mono text-blue-700 outline-none select-all"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(appointment.razorpayDetails.paymentLinkUrl);
                            toast.success('Payment link copied!');
                          }}
                          className="px-4 py-2.5 bg-emerald-600 text-white rounded-xl text-xs font-black uppercase tracking-wider hover:bg-emerald-700 transition-all cursor-pointer shadow-sm shrink-0"
                        >
                          Copy Link
                        </button>
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 font-bold">No payment link generated yet.</p>
                    )}

                    {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && appointment?.status !== 'completed' && (
                      <div className="pt-2 flex gap-3">
                        <button
                          type="button"
                          onClick={openPaymentLinkModal}
                          className="px-4 py-2.5 bg-[#143E78] text-white rounded-xl text-xs font-black uppercase tracking-wider hover:bg-[#0f2c56] transition-all flex items-center gap-2 shadow-sm cursor-pointer"
                        >
                          <SendHorizontal size={14} />
                          <span>{appointment?.razorpayDetails?.paymentLinkUrl ? 'Regenerate Link' : 'Generate Payment Link'}</span>
                        </button>
                        <button
                          type="button"
                          onClick={handleSyncPayment}
                          disabled={syncing}
                          className="px-4 py-2.5 bg-white border-2 border-slate-200 text-slate-700 rounded-xl text-xs font-black uppercase tracking-wider hover:bg-slate-50 transition-all flex items-center gap-2 cursor-pointer"
                        >
                          {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                          <span>Sync Status</span>
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* TAB 2: Offline Payment & Receipt Upload Widget */}
                {paymentModeTab === 'offline' && (
                  <div className="p-5 bg-slate-50 border border-slate-200 rounded-3xl space-y-5">
                    {/* Header Banner */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center font-black">
                          <Receipt size={16} />
                        </div>
                        <div>
                          <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider">
                            Record Offline Payment & Upload Receipt
                          </h4>
                          <p className="text-[10px] text-slate-500 font-bold">
                            For Cash at Clinic, Direct QR / UPI, POS Card swipe, or Bank NEFT payments
                          </p>
                        </div>
                      </div>

                      {appointment?.offlinePaymentDetails?.receiptUrl && (
                        <span className="px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-xl text-[10px] font-black uppercase tracking-wider flex items-center gap-1">
                          <CheckCircle2 size={12} /> Receipt Recorded
                        </span>
                      )}
                    </div>

                    {/* If Already Recorded Offline Payment Details */}
                    {appointment?.offlinePaymentDetails?.receiptUrl && (
                      <div className="bg-emerald-50/60 border border-emerald-200 rounded-2xl p-4 space-y-3">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                          <div>
                            <span className="text-[10px] font-black text-emerald-700 uppercase tracking-wider block">Payment Mode</span>
                            <span className="font-black text-slate-900 capitalize">
                              {appointment.offlinePaymentDetails.paymentMethod?.replace('_', ' ') || 'Cash'}
                            </span>
                          </div>
                          <div>
                            <span className="text-[10px] font-black text-emerald-700 uppercase tracking-wider block">Amount Paid</span>
                            <span className="font-black text-slate-900">
                              ₹{(appointment.offlinePaymentDetails.amount || appointment.finalPrice || 0).toLocaleString('en-IN')}
                            </span>
                          </div>
                          <div>
                            <span className="text-[10px] font-black text-emerald-700 uppercase tracking-wider block">Reference / Bill No</span>
                            <span className="font-black text-slate-900">
                              {appointment.offlinePaymentDetails.transactionReference || '—'}
                            </span>
                          </div>
                          <div>
                            <span className="text-[10px] font-black text-emerald-700 uppercase tracking-wider block">Recorded At</span>
                            <span className="font-bold text-slate-700">
                              {formatDateTime(appointment.offlinePaymentDetails.paidAt)}
                            </span>
                          </div>
                        </div>

                        {/* Receipt File Attachment Card */}
                        <div className="flex items-center justify-between bg-white border border-emerald-200 rounded-xl p-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                              <FileCheck size={18} />
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-black text-slate-900 truncate">
                                {appointment.offlinePaymentDetails.receiptFilename || 'Payment_Receipt'}
                              </p>
                              <p className="text-[10px] text-slate-400 font-bold">
                                {appointment.offlinePaymentDetails.receiptSize ? `${Math.round(appointment.offlinePaymentDetails.receiptSize / 1024)} KB` : 'Verified Document'}
                              </p>
                            </div>
                          </div>
                          <a
                            href={appointment.offlinePaymentDetails.receiptUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3.5 py-1.5 bg-[#143E78] text-white rounded-xl text-xs font-black uppercase tracking-wider hover:bg-[#0f2c56] transition-all flex items-center gap-1.5 shadow-sm shrink-0"
                          >
                            <ExternalLink size={13} /> View Receipt
                          </a>
                        </div>
                      </div>
                    )}

                    {/* Offline Form Inputs */}
                    <div className="space-y-4 pt-1">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Amount Paid */}
                        <div className="space-y-1.5">
                          <label className="block text-[11px] font-black text-slate-600 uppercase tracking-wider">
                            1. Amount Paid (₹) <span className="text-rose-500">*</span>
                          </label>
                          <input
                            type="number"
                            value={offlinePaymentForm.amount !== '' ? offlinePaymentForm.amount : (pricing?.finalPrice ?? appointment?.finalPrice ?? '')}
                            onChange={(e) => setOfflinePaymentForm(prev => ({ ...prev, amount: e.target.value, isAutoAmount: false }))}
                            placeholder="e.g. 1000"
                            className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-2xl text-sm font-bold text-slate-900 outline-none focus:border-[#143E78] transition-all"
                          />
                        </div>

                        {/* Reference / UTR / Bill No */}
                        <div className="space-y-1.5">
                          <label className="block text-[11px] font-black text-slate-600 uppercase tracking-wider">
                            2. Bill / UTR / Transaction No.
                          </label>
                          <input
                            type="text"
                            value={offlinePaymentForm.transactionReference}
                            onChange={(e) => setOfflinePaymentForm(prev => ({ ...prev, transactionReference: e.target.value }))}
                            placeholder="e.g. UTR12345678, Bill #982"
                            className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-2xl text-sm font-bold text-slate-900 outline-none focus:border-[#143E78] transition-all"
                          />
                        </div>
                      </div>

                      {/* Receipt File Upload Dropzone */}
                      <div className="space-y-1.5">
                        <label className="block text-[11px] font-black text-slate-600 uppercase tracking-wider">
                          3. Upload Payment Receipt / Bill Copy
                        </label>

                        {offlinePaymentForm.receiptUrl ? (
                          <div className="flex items-center justify-between p-3.5 bg-emerald-50 border-2 border-emerald-200 rounded-2xl">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0">
                                <FileCheck size={20} />
                              </div>
                              <div className="min-w-0">
                                <p className="text-xs font-black text-emerald-950 truncate">
                                  {offlinePaymentForm.receiptFilename || 'Payment_Receipt'}
                                </p>
                                <p className="text-[10px] text-emerald-700 font-bold flex items-center gap-1">
                                  <CheckCircle2 size={11} /> Ready to confirm (Uploaded to Cloud)
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <a
                                href={offlinePaymentForm.receiptUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 text-slate-600 hover:text-[#143E78] bg-white rounded-xl border border-slate-200"
                                title="Preview Receipt"
                              >
                                <ExternalLink size={14} />
                              </a>
                              <button
                                type="button"
                                onClick={() => setOfflinePaymentForm(prev => ({ ...prev, receiptUrl: '', receiptFilename: '', receiptSize: 0 }))}
                                className="p-2 text-rose-600 hover:text-rose-700 bg-white rounded-xl border border-rose-200"
                                title="Remove File"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <label className={`border-2 border-dashed rounded-2xl p-5 flex flex-col items-center justify-center cursor-pointer transition-all ${uploadingReceipt ? 'bg-slate-100 border-slate-300 cursor-wait' : 'bg-white border-slate-300 hover:border-[#143E78] hover:bg-blue-50/20'
                            }`}>
                            <input
                              type="file"
                              accept="image/*,application/pdf"
                              onChange={handleReceiptUpload}
                              disabled={uploadingReceipt}
                              className="hidden"
                            />
                            {uploadingReceipt ? (
                              <div className="flex flex-col items-center gap-2">
                                <Loader2 className="w-6 h-6 text-[#143E78] animate-spin" />
                                <span className="text-xs font-black text-slate-600">Uploading receipt to cloud...</span>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center gap-1.5 text-center">
                                <div className="w-10 h-10 rounded-2xl bg-[#143E78]/10 text-[#143E78] flex items-center justify-center mb-1">
                                  <Upload size={18} />
                                </div>
                                <p className="text-xs font-black text-slate-800">
                                  Click or drag & drop to upload Payment Receipt
                                </p>
                                <p className="text-[10px] text-slate-400 font-bold">
                                  Supports JPG, PNG, WebP or PDF receipt copies (Max 10MB)
                                </p>
                              </div>
                            )}
                          </label>
                        )}
                      </div>

                      {/* Staff Notes */}
                      <div className="space-y-1.5">
                        <label className="block text-[11px] font-black text-slate-600 uppercase tracking-wider">
                          4. Staff Remarks / Collected By Notes (Optional)
                        </label>
                        <input
                          type="text"
                          value={offlinePaymentForm.notes}
                          onChange={(e) => setOfflinePaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                          placeholder="e.g. Collected cash at reception counter, verified by front desk"
                          className="w-full px-4 py-2.5 bg-white border-2 border-slate-200 rounded-2xl text-xs font-bold text-slate-800 outline-none focus:border-[#143E78] transition-all"
                        />
                      </div>

                      {/* Submit Offline Payment & Confirm Appointment */}
                      {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head' || user?.role === 'doctor') && (
                        <div className="pt-2">
                          <button
                            type="button"
                            onClick={handleRecordOfflinePayment}
                            disabled={recordingOfflinePayment || uploadingReceipt}
                            className="w-full sm:w-auto px-6 py-3.5 bg-emerald-600 text-white rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-emerald-700 disabled:opacity-50 transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-200 cursor-pointer"
                          >
                            {recordingOfflinePayment ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                            <span>{recordingOfflinePayment ? 'Recording & Confirming...' : 'Record Payment & Confirm Appointment'}</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Footer Navigation Bar */}
                <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setActiveTab('schedule')}
                    className="px-5 py-3 border-2 border-slate-200 text-slate-700 rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-slate-50 transition-all flex items-center gap-2 cursor-pointer"
                  >
                    <ChevronLeft size={16} /> Back
                  </button>
                  <div className="flex items-center gap-3">
                    {['admin', 'customer_care', 'cc_head'].includes(user?.role) && (
                      <button
                        type="button"
                        onClick={() => setShowLostModal(true)}
                        className="px-5 py-3 bg-rose-600 text-white rounded-2xl hover:bg-rose-700 text-xs font-black uppercase tracking-wider flex items-center gap-2 transition-all shadow-md shadow-rose-200 cursor-pointer"
                      >
                        <XCircle size={16} /> Mark as Lost
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleSaveAndNext('payment')}
                      className="px-6 py-3 bg-[#143E78] text-white rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-[#0f2c56] transition-all flex items-center gap-2 shadow-lg shadow-[#143E78]/20 cursor-pointer"
                    >
                      <span>Next: Confirm & Prescription</span>
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 📋 STAGE 6: CONFIRM & PRESCRIPTION */}
          {activeTab === 'confirm' && (
            <div className="space-y-6 animate-in fade-in duration-200">
              {/* Summary Review Card */}
              <div className="bg-white rounded-3xl border-2 border-slate-100 hover:border-slate-200 p-5 sm:p-6 shadow-sm space-y-4">
                <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <h3 className="text-base font-black text-slate-900 uppercase tracking-wider flex items-center gap-2">
                    <ShieldCheck size={18} className="text-emerald-600" />
                    Appointment Review & Status
                  </h3>
                  <span className={`px-3 py-1 text-xs font-black uppercase rounded-xl border ${STATUS_BADGES[appointment.status] || 'bg-slate-100 text-slate-700'}`}>
                    {appointment.status?.replace(/_/g, ' ')}
                  </span>
                </div>

                {/* 🌟 Unified Completion Banner when completed */}
                {appointment?.status === 'completed' && (
                  <div className="p-3.5 bg-emerald-50/90 border border-emerald-200 rounded-2xl flex flex-wrap items-center justify-between gap-3 animate-in fade-in">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-black shrink-0 shadow-xs">
                        <CheckCircle2 size={16} />
                      </div>
                      <div>
                        <h4 className="text-xs font-black text-emerald-950">Appointment & Consultation Completed</h4>
                        <p className="text-[11px] text-emerald-700 font-medium">Consultation finished, documentation filed & doctor payout settled</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="px-2.5 py-1 bg-white border border-emerald-200 text-emerald-800 text-[10px] font-black rounded-lg uppercase tracking-wider shadow-2xs flex items-center gap-1">
                        <Check size={11} className="text-emerald-600" /> Consultation Done
                      </span>
                      {prescription && (
                        <span className="px-2.5 py-1 bg-white border border-blue-200 text-blue-800 text-[10px] font-black rounded-lg uppercase tracking-wider shadow-2xs flex items-center gap-1">
                          <Check size={11} className="text-blue-600" /> Prescription {prescription.status?.replace(/_/g, ' ')}
                        </span>
                      )}
                      {appointmentPayouts.length > 0 && (
                        <span className="px-2.5 py-1 bg-white border border-teal-200 text-teal-800 text-[10px] font-black rounded-lg uppercase tracking-wider shadow-2xs flex items-center gap-1">
                          <Check size={11} className="text-teal-600" /> Payout Settled ({appointmentPayouts[0]?.manualNetAmount ? `₹${appointmentPayouts[0].manualNetAmount}` : (appointmentPayouts[0]?.amount ? `₹${appointmentPayouts[0].amount}` : 'Settled')})
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3.5">
                  <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Patient</span>
                    <p className="text-xs font-black text-slate-900 mt-0.5">{patientName}</p>
                    <p className="text-[10px] text-slate-500 font-bold">{patientPhone}</p>
                  </div>

                  <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Doctor</span>
                    <p className="text-xs font-black text-slate-900 mt-0.5">{doctorName || 'Not Assigned'}</p>
                    <p className="text-[10px] text-slate-500 font-bold">{doctorSpecialization}</p>
                  </div>

                  <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Schedule</span>
                    <p className="text-xs font-black text-slate-900 mt-0.5">{formatDateDisplay(appointment.appointmentDate)}</p>
                    <p className="text-[10px] text-slate-500 font-bold">{formatTime12Hour(appointment.appointmentTimeSlot)}</p>
                  </div>

                  {appointment?.offlinePaymentDetails?.receiptUrl && (
                    <div className="p-3.5 bg-emerald-50 rounded-2xl border border-emerald-200 sm:col-span-2 md:col-span-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-black shrink-0">
                          <Receipt size={16} />
                        </div>
                        <div>
                          <span className="text-[10px] font-black text-emerald-800 uppercase tracking-wider block">
                            Paid via {appointment.offlinePaymentDetails.paymentMethod?.replace('_', ' ')?.toUpperCase() || 'OFFLINE'} (₹{(appointment.offlinePaymentDetails.amount || appointment.finalPrice || 0).toLocaleString('en-IN')})
                          </span>
                          <p className="text-xs font-bold text-slate-700">
                            Ref: {appointment.offlinePaymentDetails.transactionReference || 'N/A'} • {formatDateTime(appointment.offlinePaymentDetails.paidAt)}
                          </p>
                        </div>
                      </div>
                      <a
                        href={appointment.offlinePaymentDetails.receiptUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3.5 py-1.5 bg-emerald-700 text-white rounded-xl text-xs font-black uppercase tracking-wider hover:bg-emerald-800 transition-all flex items-center justify-center gap-1.5 shadow-sm shrink-0"
                      >
                        <ExternalLink size={13} /> View Receipt
                      </a>
                    </div>
                  )}
                </div>

                {/* Primary Action Buttons */}
                {canConfirm && (
                  <div className="flex flex-wrap items-center gap-3 pt-2">
                    <button
                      onClick={() => setShowConfirmModal(true)}
                      className="px-5 py-3 bg-emerald-600 text-white rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-emerald-700 transition-all shadow-md shadow-emerald-200 flex items-center gap-2 cursor-pointer"
                    >
                      <CheckCircle size={16} /> Confirm Appointment
                    </button>
                  </div>
                )}
              </div>

              {/* Prescription Management Card */}
              <div className="bg-white rounded-3xl border-2 border-slate-100 hover:border-slate-200 p-5 sm:p-6 shadow-sm space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-2xl bg-blue-50 text-blue-700 flex items-center justify-center font-black shrink-0">
                      <Pill size={18} />
                    </div>
                    <div>
                      <h3 className="text-base font-black text-slate-900">Patient Prescription</h3>
                      {prescription && (
                        <p className="text-[10px] text-slate-400 font-bold">Created {formatDateTime(prescription.createdAt)}</p>
                      )}
                    </div>
                  </div>

                  {canManagePrescription && (
                    <div className="flex flex-wrap items-center gap-2">
                      {prescription ? (
                        <>
                          <button
                            onClick={handleDownloadPDF}
                            disabled={downloadingPDF}
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-black text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl hover:bg-emerald-100 transition-all disabled:opacity-50 cursor-pointer"
                          >
                            {downloadingPDF ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                            <span>{downloadingPDF ? 'Generating...' : 'Download PDF'}</span>
                          </button>
                          <button
                            onClick={openEditPrescription}
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-black text-[#143E78] bg-[#143E78]/5 border border-[#143E78]/20 rounded-xl hover:bg-[#143E78]/10 transition-all cursor-pointer"
                          >
                            <Edit size={14} /> View &amp; Edit
                          </button>
                          {canSubmitPrescription && (
                            <button
                              onClick={() => openPrescriptionActionModal('submit')}
                              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-black text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl hover:bg-emerald-100 transition-all cursor-pointer"
                            >
                              <Send size={14} /> Submit
                            </button>
                          )}
                          {canApproveAndSendPrescription && (
                            <button
                              onClick={handleDirectApproveAndSendPrescription}
                              disabled={prescriptionActionLoading}
                              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-black text-white bg-emerald-600 border border-emerald-600 rounded-xl hover:bg-emerald-700 transition-all disabled:opacity-50 cursor-pointer shadow-md shadow-emerald-200"
                            >
                              {prescriptionActionLoading ? <Loader2 size={14} className="animate-spin" /> : <SendHorizontal size={14} />}
                              <span>{Boolean(prescription.sentToLeadAt || prescription.statusHistory?.some(h => h.status === 'sent_to_lead')) ? 'Approve & Resend' : 'Approve & Send'}</span>
                            </button>
                          )}
                          {canResendPrescription && (
                            <button
                              onClick={handleDirectApproveAndSendPrescription}
                              disabled={prescriptionActionLoading}
                              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-black text-white bg-emerald-600 border border-emerald-600 rounded-xl hover:bg-emerald-700 transition-all disabled:opacity-50 cursor-pointer shadow-md shadow-emerald-200"
                            >
                              {prescriptionActionLoading ? <Loader2 size={14} className="animate-spin" /> : <SendHorizontal size={14} />}
                              <span>Resend to Lead</span>
                            </button>
                          )}
                          {canVerifyPrescription && (
                            <button
                              onClick={() => openPrescriptionActionModal('reject')}
                              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-black text-rose-700 bg-rose-50 border border-rose-200 rounded-xl hover:bg-rose-100 transition-all cursor-pointer"
                            >
                              <XCircle size={14} /> Reject
                            </button>
                          )}
                          <button
                            onClick={handleSendFormToDoctor}
                            disabled={sendingFormToDoctor}
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-black text-white bg-[#143E78] border border-[#143E78] rounded-xl hover:bg-[#0f2f5e] transition-all disabled:opacity-50 cursor-pointer shadow-md shadow-[#143E78]/20"
                            title={prescription.prescriptionFormSentAt ? `Form last sent: ${formatDateTime(prescription.prescriptionFormSentAt)}. Click to resend form & draft prescription to doctor for correction.` : 'Send prescription form link to doctor'}
                          >
                            {sendingFormToDoctor ? <Loader2 size={14} className="animate-spin" /> : <SendHorizontal size={14} />}
                            <span>{prescription.prescriptionFormSentAt ? 'Resend to Doctor' : 'Send to Doctor'}</span>
                          </button>
                        </>
                      ) : (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={openCreatePrescription}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-black text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl hover:bg-emerald-100 transition-all cursor-pointer"
                          >
                            <Plus size={14} /> Quick Create
                          </button>
                          <button
                            onClick={() => navigate(`/prescriptions/create?appointmentId=${appointment._id}`)}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-black text-[#143E78] bg-[#143E78]/5 border border-[#143E78]/20 rounded-xl hover:bg-[#143E78]/10 transition-all cursor-pointer"
                          >
                            <FileText size={14} /> Full Form
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {prescription ? (
                  <div className="space-y-5">
                    {/* Status, Doctor, and Count badges */}
                    <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-50 p-3.5 rounded-2xl border border-slate-100">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`px-2.5 py-1 text-[10px] font-black uppercase rounded-lg border ${prescription.status === 'verified' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                          prescription.status === 'sent_to_lead' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                            prescription.status === 'rejected' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                              'bg-amber-50 text-amber-700 border-amber-200'
                          }`}>
                          {prescription.status?.replace(/_/g, ' ')}
                        </span>
                        <span className="px-2.5 py-1 text-[10px] font-black text-slate-600 bg-white border border-slate-200 rounded-lg">
                          {prescriptionItems.length} Medication(s)
                        </span>
                        {investigations.length > 0 && (
                          <span className="px-2.5 py-1 text-[10px] font-black text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg">
                            {investigations.length} Investigation(s)
                          </span>
                        )}
                      </div>

                      {prescription.prescribedBy && (
                        <span className="text-xs font-bold text-slate-500 flex items-center gap-1.5">
                          <Stethoscope size={14} className="text-[#143E78]" />
                          <span>Prescribed by: <strong className="text-slate-900">{prescription.prescribedBy.name || `${prescription.prescribedBy.firstName || ''} ${prescription.prescribedBy.lastName || ''}`.trim() || 'Attending Doctor'}</strong></span>
                        </span>
                      )}
                    </div>

                    {/* 1. Clinical Presentation & Diagnosis Grid */}
                    {(prescription.provisionalDiagnosis || prescription.chiefComplaints || prescription.pastMedicalHistory || appointment.leadId?.comorbidities) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                        {prescription.provisionalDiagnosis && (
                          <div className="p-4 bg-blue-50/60 rounded-2xl border border-blue-100 space-y-1 md:col-span-2">
                            <span className="text-[10px] font-black text-[#143E78] uppercase tracking-widest flex items-center gap-1.5">
                              <Stethoscope size={13} /> Provisional / Final Diagnosis
                            </span>
                            <p className="text-sm font-black text-slate-900 leading-snug">{prescription.provisionalDiagnosis}</p>
                          </div>
                        )}

                        {prescription.chiefComplaints && (
                          <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100 space-y-1">
                            <span className="text-[10px] font-black text-rose-600 uppercase tracking-widest flex items-center gap-1.5">
                              <Heart size={13} /> Chief Complaints & Symptoms
                            </span>
                            <p className="text-xs font-bold text-slate-800 leading-relaxed whitespace-pre-wrap">{prescription.chiefComplaints}</p>
                          </div>
                        )}

                        {prescription.pastMedicalHistory && (
                          <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100 space-y-1">
                            <span className="text-[10px] font-black text-indigo-600 uppercase tracking-widest flex items-center gap-1.5">
                              <ClipboardList size={13} /> Past Medical & Surgical History
                            </span>
                            <p className="text-xs font-bold text-slate-800 leading-relaxed whitespace-pre-wrap">{prescription.pastMedicalHistory}</p>
                          </div>
                        )}

                        {appointment.leadId?.comorbidities && (
                          <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100 space-y-1">
                            <span className="text-[10px] font-black text-amber-600 uppercase tracking-widest flex items-center gap-1.5">
                              <AlertCircle size={13} /> Comorbidities & Risk Factors
                            </span>
                            <p className="text-xs font-bold text-slate-800 leading-relaxed">{appointment.leadId.comorbidities}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* 2. Rx Medications Table */}
                    <div className="space-y-2">
                      <h4 className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-2">
                        <Pill size={14} className="text-[#143E78]" /> Prescribed Medications ({prescriptionItems.length})
                      </h4>
                      {prescriptionItems.length > 0 ? (
                        <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-2xs">
                          <table className="min-w-full divide-y divide-slate-100 text-xs">
                            <thead className="bg-slate-50 font-black text-slate-600">
                              <tr>
                                <th className="px-3.5 py-2.5 text-left text-[10px] font-black uppercase tracking-wider">#</th>
                                <th className="px-3.5 py-2.5 text-left text-[10px] font-black uppercase tracking-wider">Type</th>
                                <th className="px-3.5 py-2.5 text-left text-[10px] font-black uppercase tracking-wider">Medicine Name</th>
                                <th className="px-3.5 py-2.5 text-left text-[10px] font-black uppercase tracking-wider">Strength</th>
                                <th className="px-3.5 py-2.5 text-left text-[10px] font-black uppercase tracking-wider">Frequency</th>
                                <th className="px-3.5 py-2.5 text-left text-[10px] font-black uppercase tracking-wider">Duration</th>
                                <th className="px-3.5 py-2.5 text-left text-[10px] font-black uppercase tracking-wider">Instructions</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-slate-100">
                              {prescriptionItems.map((item, index) => (
                                <tr key={index} className="hover:bg-slate-50/60 transition-colors">
                                  <td className="px-3.5 py-2.5 font-bold text-slate-400">{index + 1}</td>
                                  <td className="px-3.5 py-2.5 font-bold text-slate-600 capitalize">{item.type ? item.type.replace(/_/g, ' ') : '-'}</td>
                                  <td className="px-3.5 py-2.5 font-black text-slate-900">{item.medicineName || '-'}</td>
                                  <td className="px-3.5 py-2.5 font-bold text-slate-700">{item.strength || '-'} {item.unit || ''}</td>
                                  <td className="px-3.5 py-2.5 font-black text-indigo-700">
                                    <span className="px-2 py-0.5 bg-indigo-50 border border-indigo-100 rounded-md">
                                      {item.frequency ? item.frequency.replace(/_/g, ' ') : '-'}
                                    </span>
                                  </td>
                                  <td className="px-3.5 py-2.5 font-bold text-slate-700">{item.duration || '-'} {item.durationUnit || ''}</td>
                                  <td className="px-3.5 py-2.5 text-slate-600 font-medium">{item.instructions ? item.instructions.replace(/_/g, ' ') : '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="p-4 bg-slate-50 rounded-2xl border border-dashed border-slate-200 text-center text-xs font-bold text-slate-400">
                          No medication items prescribed.
                        </div>
                      )}
                    </div>

                    {/* 3. Diagnostic Investigations / Lab Tests */}
                    {investigations.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-2">
                          <TestTubes size={14} className="text-[#143E78]" /> Recommended Diagnostic Investigations & Tests ({investigations.length})
                        </h4>
                        <div className="flex flex-wrap gap-2.5">
                          {investigations.map((inv, idx) => (
                            <div key={inv.id || idx} className="inline-flex items-center gap-2 px-3.5 py-2 bg-[#143E78]/5 border-2 border-[#143E78]/15 rounded-2xl">
                              <span className="text-sm">{inv.icon || '🔬'}</span>
                              <div>
                                <p className="text-xs font-black text-[#143E78]">{inv.label || inv.name}</p>
                                {inv.category && <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{inv.category}</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 4. Clinical Advice & Follow-Up Directions Grid */}
                    {(prescription.advice || prescription.followUp || prescription.notes) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                        {prescription.advice && (
                          <div className="p-4 bg-emerald-50/60 rounded-2xl border border-emerald-100 space-y-1.5">
                            <span className="text-[10px] font-black text-emerald-800 uppercase tracking-widest flex items-center gap-1.5">
                              <Lightbulb size={13} className="text-emerald-600" /> Clinical Advice & Dietary/Lifestyle Guidance
                            </span>
                            <p className="text-xs font-bold text-emerald-950 leading-relaxed whitespace-pre-wrap">{prescription.advice}</p>
                          </div>
                        )}

                        {prescription.followUp && (
                          <div className="p-4 bg-indigo-50/60 rounded-2xl border border-indigo-100 space-y-1.5">
                            <span className="text-[10px] font-black text-indigo-800 uppercase tracking-widest flex items-center gap-1.5">
                              <Calendar size={13} className="text-indigo-600" /> Follow-Up Schedule & Review Instructions
                            </span>
                            <p className="text-xs font-bold text-indigo-950 leading-relaxed whitespace-pre-wrap">{prescription.followUp}</p>
                          </div>
                        )}

                        {prescription.notes && (
                          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-1.5 md:col-span-2">
                            <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                              <MessageSquare size={13} className="text-[#143E78]" /> Doctor Remarks & Clinical Notes
                            </span>
                            <p className="text-xs font-bold text-slate-700 leading-relaxed whitespace-pre-wrap">{prescription.notes}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* 5. Verification / Rejection Notes */}
                    {prescription.verificationNotes && (
                      <div className="p-3.5 bg-emerald-50/80 border border-emerald-200 rounded-2xl text-xs space-y-1">
                        <span className="font-black text-emerald-800 uppercase tracking-wider text-[10px] flex items-center gap-1.5">
                          <ShieldCheck size={13} /> Verification Attestation
                        </span>
                        <p className="text-emerald-900 font-bold">{prescription.verificationNotes}</p>
                      </div>
                    )}

                    {prescription.rejectionReason && (
                      <div className="p-3.5 bg-rose-50/80 border border-rose-200 rounded-2xl text-xs space-y-1">
                        <span className="font-black text-rose-800 uppercase tracking-wider text-[10px] flex items-center gap-1.5">
                          <XCircle size={13} /> Rejection Reason
                        </span>
                        <p className="text-rose-900 font-bold">{prescription.rejectionReason}</p>
                      </div>
                    )}

                    {/* 6. Doctor Signature Card */}
                    {prescription.doctorSignature && (
                      <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between flex-wrap gap-3">
                        <div>
                          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Attending Doctor Signature</span>
                          <p className="text-xs font-black text-slate-800 mt-0.5">
                            {prescription.prescribedBy?.name || `${prescription.prescribedBy?.firstName || ''} ${prescription.prescribedBy?.lastName || ''}`.trim() || 'Attending Physician'}
                          </p>
                        </div>
                        <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-2xs">
                          <img
                            src={prescription.doctorSignature.startsWith('http') ? prescription.doctorSignature : `http://localhost:5000/${prescription.doctorSignature}`}
                            alt="Doctor Signature"
                            className="h-12 w-auto object-contain max-w-[160px]"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-slate-50 rounded-2xl border border-slate-100">
                    <Pill size={24} className="text-slate-300 mx-auto mb-2" />
                    <p className="text-xs font-black text-slate-600">No prescription recorded yet</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">Click Quick Create or Full Form above to generate a prescription</p>
                  </div>
                )}
              </div>

              {/* 💰 Doctor Consultation Payout Hub (Shown when completed or when payouts exist) */}
              {(appointment?.status === 'completed' || appointmentPayouts.length > 0) && (
                <div className="bg-white rounded-3xl border-2 border-emerald-100/90 hover:border-emerald-200 p-5 sm:p-6 shadow-sm space-y-4 animate-in fade-in slide-in-from-bottom-2">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
                    <div className="flex items-center gap-2.5">
                      <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center font-black shrink-0 shadow-xs">
                        <Banknote size={20} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-base font-black text-slate-900">Doctor Consultation Payout</h3>
                          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
                            <CheckCircle2 size={11} /> {appointmentPayouts[0]?.status === 'completed' ? 'Disbursed / Settled' : (appointmentPayouts[0]?.status ? `Payout ${appointmentPayouts[0].status}` : 'Settled')}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-400 font-bold mt-0.5">
                          {appointmentPayouts[0]?.emailSent
                            ? 'Payout receipt sent to doctor & settlement finalized successfully'
                            : 'Automated settlement for attending physician on consultation completion'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      {appointmentPayouts[0] && (
                        <button
                          type="button"
                          onClick={() => handleSendPayoutEmail(appointmentPayouts[0]._id)}
                          disabled={sendingPayoutId === appointmentPayouts[0]._id}
                          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-black text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-xl hover:bg-indigo-100 transition-all disabled:opacity-50 cursor-pointer"
                        >
                          {sendingPayoutId === appointmentPayouts[0]._id ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
                          <span>Email Receipt</span>
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          const existingPayout = appointmentPayouts[0];
                          const { gross, tdsPct, net } = getPayoutMetrics(existingPayout);
                          const grossNum = gross || Number(appointment?.doctorFee || appointment?.finalPrice || 0);
                          const calculatedNet = net || calculateManualNetAmount(grossNum, tdsPct || 10);
                          setManualPayoutForm({
                            grossAmount: grossNum ? String(grossNum) : '',
                            tdsPercentage: String(tdsPct || 10),
                            netAmount: String(calculatedNet),
                            transactionId: existingPayout?.transactionId || '',
                            transactionDate: existingPayout?.transactionDate ? new Date(existingPayout.transactionDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                            notes: existingPayout?.description || `Consultation fee for appointment ${appointment?.bookingId || ''}`
                          });
                          setShowManualPayoutModal(true);
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-black text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all cursor-pointer"
                      >
                        <Plus size={13} />
                        <span>{appointmentPayouts.length > 0 ? 'Adjust Payout' : 'Manual Payout'}</span>
                      </button>
                    </div>
                  </div>

                  {/* Financial Ledger & Doctor Details */}
                  {appointmentPayouts.length > 0 ? (
                    <div className="space-y-4">
                      {appointmentPayouts.map((payout, pIdx) => {
                        const docUser = payout.doctor?.user || appointment?.appointmentDoctorId?.user;
                        const doctorName = docUser ? `Dr. ${docUser.firstName || ''} ${docUser.lastName || ''}`.trim() : (appointment?.doctorName || 'Assigned Doctor');
                        const { gross, tds, net } = getPayoutMetrics(payout);

                        return (
                          <div key={payout._id || pIdx} className="space-y-4">
                            {/* Doctor Dossier Ribbon */}
                            <div className="flex items-center justify-between p-3.5 bg-slate-50 border border-slate-100 rounded-2xl flex-wrap gap-3">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-[#143E78] text-white flex items-center justify-center font-black text-sm uppercase shrink-0">
                                  {doctorName.replace('Dr. ', '').charAt(0) || 'D'}
                                </div>
                                <div>
                                  <h4 className="text-xs font-black text-slate-900">{doctorName}</h4>
                                  <p className="text-[11px] font-bold text-slate-500">
                                    {payout.doctor?.specialization || appointment?.appointmentDoctorId?.specialization || 'Consultant Doctor'}
                                    {docUser?.phone ? ` • ${docUser.phone}` : ''}
                                  </p>
                                </div>
                              </div>

                              <div className="text-right">
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Settlement ID</span>
                                <span className="text-xs font-mono font-bold text-slate-700">{payout._id}</span>
                              </div>
                            </div>

                            {/* 4 Financial metric tiles */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                              <div className="bg-slate-50/80 border border-slate-200/60 rounded-2xl p-3.5 space-y-1">
                                <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider block">Gross Fee</span>
                                <p className="text-base font-black text-slate-900">₹{gross.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                                <span className="text-[10px] font-bold text-slate-400 block">Consultation Rate</span>
                              </div>

                              <div className="bg-rose-50/70 border border-rose-100 rounded-2xl p-3.5 space-y-1">
                                <span className="text-[10px] font-black text-rose-600 uppercase tracking-wider block">TDS (10%)</span>
                                <p className="text-base font-black text-rose-700">− ₹{tds.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                                <span className="text-[10px] font-bold text-rose-500/80 block">Tax Deducted</span>
                              </div>

                              <div className="bg-emerald-50 border-2 border-emerald-200/80 rounded-2xl p-3.5 space-y-1 shadow-xs">
                                <span className="text-[10px] font-black text-emerald-800 uppercase tracking-wider block">Net Doctor Payout</span>
                                <p className="text-lg font-black text-emerald-700">₹{net.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                                <span className="text-[10px] font-black text-emerald-600 block">100% Doctor Share</span>
                              </div>

                              <div className="bg-blue-50/70 border border-blue-100 rounded-2xl p-3.5 space-y-1">
                                <span className="text-[10px] font-black text-blue-700 uppercase tracking-wider block">Disbursal & Status</span>
                                <p className="text-xs font-black text-slate-900 mt-1">
                                  {payout.verifiedAt ? formatDateTime(payout.verifiedAt) : (payout.createdAt ? formatDateTime(payout.createdAt) : 'Just Now')}
                                </p>
                                <span className="text-[10px] font-bold text-emerald-700 block">
                                  {payout.emailSent ? '✓ Receipt Sent & Settled' : '✓ Auto-Approved by CC'}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-6 bg-slate-50 border border-slate-200 rounded-2xl text-center space-y-3">
                      <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto">
                        <Banknote size={24} />
                      </div>
                      <div>
                        <h4 className="text-xs font-black text-slate-800">No Doctor Payout Created Yet</h4>
                        <p className="text-[11px] font-bold text-slate-400 mt-0.5">
                          Click the button below to generate the consultation payout for the attending doctor.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleGenerateAppointmentPayout}
                        disabled={generatingPayoutId === appointment?._id}
                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-md shadow-emerald-200 transition-all disabled:opacity-50 cursor-pointer"
                      >
                        {generatingPayoutId === appointment?._id ? <Loader2 size={14} className="animate-spin" /> : <Banknote size={14} />}
                        <span>{generatingPayoutId === appointment?._id ? 'Generating...' : 'Generate Doctor Payout'}</span>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Footer Navigation Bar */}
              <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setActiveTab('payment')}
                  className="px-5 py-3 border-2 border-slate-200 text-slate-700 rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-slate-50 transition-all flex items-center gap-2 cursor-pointer"
                >
                  <ChevronLeft size={16} /> Back
                </button>
                {canConfirm && (
                  <button
                    type="button"
                    onClick={() => setShowConfirmModal(true)}
                    className="px-6 py-3 bg-emerald-600 text-white rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-emerald-700 transition-all flex items-center gap-2 shadow-lg shadow-emerald-200 cursor-pointer"
                  >
                    <CheckCircle size={16} />
                    <span>Confirm Appointment</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 📄 Global Document Preview Modal */}
        {previewFile && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-in fade-in">
            <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <h4 className="text-sm font-black text-slate-800 truncate">{previewFile.name}</h4>
                <button
                  onClick={() => setPreviewFile(null)}
                  className="p-1.5 text-slate-400 hover:text-slate-700 bg-slate-100 rounded-xl cursor-pointer"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="p-4 flex-1 overflow-auto flex items-center justify-center bg-slate-50 min-h-[400px]">
                {previewFile.isPDF ? (
                  <iframe src={previewFile.url} className="w-full h-[600px] rounded-xl border" title="Preview" />
                ) : (
                  <img src={previewFile.url} alt={previewFile.name} className="max-w-full max-h-[600px] object-contain rounded-xl shadow" />
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {renderPortalModal(showConfirmModal, () => setShowConfirmModal(false), 'Confirm Appointment', (
        <div className="space-y-5">
          <p className="text-sm text-slate-600 font-medium leading-relaxed">Confirm this appointment?</p>
          <div className="flex gap-3">
            <button onClick={() => setShowConfirmModal(false)} className="flex-1 px-4 py-3 border border-slate-200 rounded-2xl text-xs font-black text-slate-600 hover:bg-slate-50 transition-colors">Dismiss</button>
            <button onClick={handleConfirm} disabled={updating} className="flex-1 px-4 py-3 bg-emerald-600 text-white rounded-2xl text-xs font-black hover:bg-emerald-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
              {updating && <Loader2 className="w-4 h-4 animate-spin" />} Confirm
            </button>
          </div>
        </div>
      ))}

      {renderPortalModal(showCompleteModal, () => setShowCompleteModal(false), 'Mark Completed', (
        <div className="space-y-5">
          <p className="text-sm text-slate-600 font-medium leading-relaxed">Mark this appointment as completed?</p>
          <div className="flex gap-3">
            <button onClick={() => setShowCompleteModal(false)} className="flex-1 px-4 py-3 border border-slate-200 rounded-2xl text-xs font-black text-slate-600 hover:bg-slate-50 transition-colors">Dismiss</button>
            <button onClick={handleComplete} disabled={updating} className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-2xl text-xs font-black hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
              {updating && <Loader2 className="w-4 h-4 animate-spin" />} Complete
            </button>
          </div>
        </div>
      ))}

      {renderPortalModal(showLostModal, () => { setShowLostModal(false); setLostReason(''); }, 'Mark Lost', (
        <div className="space-y-5">
          <textarea
            value={lostReason}
            onChange={(e) => setLostReason(e.target.value)}
            placeholder="Enter failure reason..."
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold outline-none focus:border-[#143E78] transition-colors"
            rows={3}
          />
          <div className="flex gap-3">
            <button onClick={() => { setShowLostModal(false); setLostReason(''); }} className="flex-1 px-4 py-3 border border-slate-200 rounded-2xl text-xs font-black text-slate-600 hover:bg-slate-50 transition-colors">Dismiss</button>
            <button onClick={handleMarkLost} disabled={updating} className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-2xl text-xs font-black hover:bg-orange-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
              {updating && <Loader2 className="w-4 h-4 animate-spin" />} Mark Lost
            </button>
          </div>
        </div>
      ))}

      {renderPortalModal(showOverrideModal, () => { setShowOverrideModal(false); setOverrideAction(''); setLostReason(''); }, 'MD Override', (
        <div className="space-y-4">
          <select
            value={overrideAction}
            onChange={(e) => setOverrideAction(e.target.value)}
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold outline-none focus:border-[#143E78] transition-colors"
          >
            <option value="">Select action...</option>
            <option value="book_now">Book Now</option>
            <option value="mark_lost_permanent">Mark Lost Permanent</option>
          </select>
          {overrideAction === 'book_now' && (
            <>
              <input type="date" value={overrideDate} onChange={(e) => setOverrideDate(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold outline-none focus:border-[#143E78] transition-colors" />
              <input type="text" value={overrideTimeSlot} onChange={(e) => setOverrideTimeSlot(e.target.value)} placeholder="Time slot (e.g., 10:00 AM)" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold outline-none focus:border-[#143E78] transition-colors" />
              <select value={overrideDoctorId} onChange={(e) => setOverrideDoctorId(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold outline-none focus:border-[#143E78] transition-colors">
                <option value="">Select doctor...</option>
                {doctors.map(doc => (
                  <option key={doc._id} value={doc._id}>
                    Dr. {doc.user?.firstName} {doc.user?.lastName} - {doc.specialization} {doc.financials?.consultationFee ? `(Fee: ₹${doc.financials.consultationFee})` : ''}
                  </option>
                ))}
              </select>
            </>
          )}
          {overrideAction === 'mark_lost_permanent' && (
            <textarea
              value={lostReason}
              onChange={(e) => setLostReason(e.target.value)}
              placeholder="Enter reason..."
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold outline-none focus:border-[#143E78] transition-colors"
              rows={3}
            />
          )}
          <div className="flex gap-3">
            <button onClick={() => { setShowOverrideModal(false); setOverrideAction(''); setLostReason(''); }} className="flex-1 px-4 py-3 border border-slate-200 rounded-2xl text-xs font-black text-slate-600 hover:bg-slate-50 transition-colors">Dismiss</button>
            <button onClick={handleOverride} disabled={updating || !overrideAction} className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-2xl text-xs font-black hover:bg-purple-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
              {updating && <Loader2 className="w-4 h-4 animate-spin" />} Apply
            </button>
          </div>
        </div>
      ))}

      {renderPortalModal(showPrescriptionModal, () => { setShowPrescriptionModal(false); setEditingPrescription(null); }, editingPrescription ? 'Edit Prescription' : 'Create Prescription', (
        <form onSubmit={handleSavePrescription} className="space-y-5">
          {!editingPrescription ? (
            <>
              <div className="space-y-3">
                <label className="block text-[11px] font-black text-slate-500 uppercase tracking-widest">Chief Complaints <span className="text-rose-500">*</span></label>
                <textarea
                  value={prescriptionForm.chiefComplaints}
                  onChange={(e) => setPrescriptionForm((prev) => ({ ...prev, chiefComplaints: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-2xl text-sm font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all"
                  placeholder="e.g. Fever since 2 days, Cold and sore throat, Body ache"
                />
                {prescriptionErrors.chiefComplaints && (
                  <p className="text-[10px] font-bold text-rose-500 uppercase tracking-wider">{prescriptionErrors.chiefComplaints}</p>
                )}
              </div>

              <div className="space-y-3">
                <label className="block text-[11px] font-black text-slate-500 uppercase tracking-widest">Past Medical History</label>
                <textarea
                  value={prescriptionForm.pastMedicalHistory}
                  onChange={(e) => setPrescriptionForm((prev) => ({ ...prev, pastMedicalHistory: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-2xl text-sm font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all"
                  placeholder="e.g. Diabetes, Hypertension, Previous surgeries, Allergies"
                />
              </div>

              {(appointment?.leadId?.documents || []).length > 0 && (
                <div className="bg-blue-50 border-2 border-blue-100 rounded-2xl p-4 space-y-3">
                  <p className="text-[10px] font-black text-blue-700 uppercase tracking-widest flex items-center gap-2">
                    <FileText className="w-3.5 h-3.5" /> Lead Medical Documents
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {(appointment.leadId.documents || []).map((doc, idx) => (
                      <a key={idx} href={doc.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white text-blue-700 rounded-lg text-[10px] font-black uppercase tracking-wider hover:bg-blue-100 transition-colors border border-blue-200">
                        <ExternalLink className="w-3 h-3" /> {doc.name || `Document ${idx + 1}`}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="space-y-3">
                <label className="block text-[11px] font-black text-slate-500 uppercase tracking-widest">Chief Complaints <span className="text-rose-500">*</span></label>
                <textarea
                  value={prescriptionForm.chiefComplaints}
                  onChange={(e) => setPrescriptionForm((prev) => ({ ...prev, chiefComplaints: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-2xl text-sm font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all"
                  placeholder="e.g. Fever since 2 days, Cold and sore throat, Body ache"
                />
                {prescriptionErrors.chiefComplaints && (
                  <p className="text-[10px] font-bold text-rose-500 uppercase tracking-wider">{prescriptionErrors.chiefComplaints}</p>
                )}
              </div>

              <div className="space-y-3">
                <label className="block text-[11px] font-black text-slate-500 uppercase tracking-widest">Past Medical History</label>
                <textarea
                  value={prescriptionForm.pastMedicalHistory}
                  onChange={(e) => setPrescriptionForm((prev) => ({ ...prev, pastMedicalHistory: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-2xl text-sm font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all"
                  placeholder="e.g. Diabetes, Hypertension, Previous surgeries, Allergies"
                />
              </div>

              <div className="space-y-3">
                <label className="block text-[11px] font-black text-slate-500 uppercase tracking-widest">Provisional Diagnosis</label>
                <input
                  type="text"
                  value={prescriptionForm.provisionalDiagnosis}
                  onChange={(e) => setPrescriptionForm((prev) => ({ ...prev, provisionalDiagnosis: e.target.value }))}
                  className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-2xl text-sm font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all"
                  placeholder="e.g. Acute Upper Respiratory Tract Infection (URTI)"
                />
              </div>

              <div className="space-y-3">
                <label className="block text-[11px] font-black text-slate-500 uppercase tracking-widest">Follow-up</label>
                <input
                  type="text"
                  value={prescriptionForm.followUp}
                  onChange={(e) => setPrescriptionForm((prev) => ({ ...prev, followUp: e.target.value }))}
                  className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-2xl text-sm font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all"
                  placeholder="e.g. Review after 3 Days"
                />
              </div>

              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => setShowAdvice(!showAdvice)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border-2 border-slate-200 rounded-xl text-xs font-black text-slate-700 hover:bg-slate-50 transition-all"
                >
                  {showAdvice ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  Advice
                </button>
                {showAdvice && (
                  <textarea
                    value={prescriptionForm.advice}
                    onChange={(e) => setPrescriptionForm((prev) => ({ ...prev, advice: e.target.value }))}
                    rows={3}
                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-2xl text-sm font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all"
                    placeholder="Additional advice for the patient..."
                  />
                )}
              </div>

              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => setShowNotes(!showNotes)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border-2 border-slate-200 rounded-xl text-xs font-black text-slate-700 hover:bg-slate-50 transition-all"
                >
                  {showNotes ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  Notes
                </button>
                {showNotes && (
                  <textarea
                    value={prescriptionForm.notes}
                    onChange={(e) => setPrescriptionForm((prev) => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-2xl text-sm font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all"
                    placeholder="Additional notes for the prescription..."
                  />
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest">
                    Prescription Items <span className="text-rose-500 ml-1">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={addPrescriptionItem}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-black text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl hover:bg-emerald-100 transition-all"
                  >
                    <Plus size={14} /> Add Item
                  </button>
                </div>

                {prescriptionErrors.prescriptionItems && (
                  <div className="flex items-center gap-1.5 mb-3">
                    <AlertCircle className="w-3.5 h-3.5 text-rose-500" />
                    <p className="text-[10px] font-bold text-rose-500 uppercase tracking-wider">{prescriptionErrors.prescriptionItems}</p>
                  </div>
                )}

                <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                  {prescriptionForm.prescriptionItems.map((item, index) => (
                    <div key={index} className="relative bg-slate-50/80 border border-slate-200 rounded-2xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-black text-slate-700 uppercase tracking-wider">Item {index + 1}</span>
                        {prescriptionForm.prescriptionItems.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removePrescriptionItem(index)}
                            className="inline-flex items-center gap-1 text-rose-600 hover:text-rose-700 text-xs font-bold transition-colors"
                          >
                            <Trash2 size={14} /> Remove
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Medicine Name *</label>
                          <input
                            type="text"
                            value={item.medicineName}
                            onChange={(e) => handlePrescriptionItemChange(index, 'medicineName', e.target.value)}
                            className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] transition-all"
                            placeholder="e.g., Paracetamol"
                          />
                          {prescriptionErrors[`item_${index}_medicineName`] && (
                            <p className="mt-1 text-[10px] font-bold text-rose-500 uppercase tracking-wider">{prescriptionErrors[`item_${index}_medicineName`]}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Strength</label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={item.strength || ''}
                              onChange={(e) => handlePrescriptionItemChange(index, 'strength', e.target.value)}
                              className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] transition-all"
                              placeholder="e.g. 500"
                            />
                            <select
                              value={customUnitItems[index] ? '' : (item.unit || 'mg')}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === 'other') {
                                  setCustomUnitItems((prev) => ({ ...prev, [index]: true }));
                                  handlePrescriptionItemChange(index, 'unit', '');
                                } else {
                                  setCustomUnitItems((prev) => {
                                    const next = { ...prev };
                                    delete next[index];
                                    return next;
                                  });
                                  handlePrescriptionItemChange(index, 'unit', val);
                                }
                              }}
                              className="w-24 px-2 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 outline-none focus:border-[#143E78] transition-all appearance-none"
                            >
                              {UNIT_OPTIONS.filter((f) => f.value !== 'other').map((f) => (
                                <option key={f.value} value={f.value}>{f.label}</option>
                              ))}
                              <option value="other">Other (custom)</option>
                            </select>
                          </div>
                          {customUnitItems[index] && (
                            <input
                              type="text"
                              value={item.unit}
                              onChange={(e) => handlePrescriptionItemChange(index, 'unit', e.target.value)}
                              placeholder="Enter custom unit"
                              className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] transition-all mt-2"
                            />
                          )}
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Type</label>
                          <select
                            value={customTypeItems[index] ? '' : (item.type || '')}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === 'other') {
                                setCustomTypeItems((prev) => ({ ...prev, [index]: true }));
                                handlePrescriptionItemChange(index, 'type', '');
                              } else {
                                setCustomTypeItems((prev) => {
                                  const next = { ...prev };
                                  delete next[index];
                                  return next;
                                });
                                handlePrescriptionItemChange(index, 'type', val);
                              }
                            }}
                            className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 outline-none focus:border-[#143E78] transition-all appearance-none"
                          >
                            <option value="">Select type</option>
                            {TYPE_OPTIONS.filter((f) => f.value !== 'other').map((f) => (
                              <option key={f.value} value={f.value}>{f.label}</option>
                            ))}
                            <option value="other">Other (custom)</option>
                          </select>
                          {customTypeItems[index] && (
                            <input
                              type="text"
                              value={item.type}
                              onChange={(e) => handlePrescriptionItemChange(index, 'type', e.target.value)}
                              placeholder="Enter custom type"
                              className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] transition-all mt-2"
                            />
                          )}
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Frequency *</label>
                          <select
                            value={customFrequencyItems[index] ? '' : (item.frequency || '')}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === 'other') {
                                setCustomFrequencyItems((prev) => ({ ...prev, [index]: true }));
                                handlePrescriptionItemChange(index, 'frequency', '');
                              } else {
                                setCustomFrequencyItems((prev) => {
                                  const next = { ...prev };
                                  delete next[index];
                                  return next;
                                });
                                handlePrescriptionItemChange(index, 'frequency', val);
                              }
                            }}
                            className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 outline-none focus:border-[#143E78] transition-all appearance-none"
                          >
                            <option value="">Select frequency</option>
                            {FREQUENCY_OPTIONS.filter((f) => f.value !== 'other').map((f) => (
                              <option key={f.value} value={f.value}>{f.label}</option>
                            ))}
                            <option value="other">Other (custom)</option>
                          </select>
                          {customFrequencyItems[index] && (
                            <input
                              type="text"
                              value={item.frequency}
                              onChange={(e) => handlePrescriptionItemChange(index, 'frequency', e.target.value)}
                              placeholder="Enter custom frequency"
                              className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] transition-all mt-2"
                            />
                          )}
                          {prescriptionErrors[`item_${index}_frequency`] && (
                            <p className="mt-1 text-[10px] font-bold text-rose-500 uppercase tracking-wider">{prescriptionErrors[`item_${index}_frequency`]}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Duration *</label>
                          <div className="flex gap-2">
                            <input
                              type="number"
                              value={item.duration || ''}
                              onChange={(e) => handlePrescriptionItemChange(index, 'duration', e.target.value)}
                              className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] transition-all"
                              placeholder="e.g. 3"
                              min="0"
                            />
                            <select
                              value={item.durationUnit || 'days'}
                              onChange={(e) => handlePrescriptionItemChange(index, 'durationUnit', e.target.value)}
                              className="w-24 px-2 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 outline-none focus:border-[#143E78] transition-all appearance-none"
                            >
                              {DURATION_UNIT_OPTIONS.filter((f) => f.value !== 'other').map((f) => (
                                <option key={f.value} value={f.value}>{f.label}</option>
                              ))}
                            </select>
                          </div>
                          {prescriptionErrors[`item_${index}_duration`] && (
                            <p className="mt-1 text-[10px] font-bold text-rose-500 uppercase tracking-wider">{prescriptionErrors[`item_${index}_duration`]}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
                        <div className="flex-1 min-w-[160px]">
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Instructions</label>
                          <select
                            value={customInstructionsItems[index] ? '' : (item.instructions || '')}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === 'other') {
                                setCustomInstructionsItems((prev) => ({ ...prev, [index]: true }));
                                handlePrescriptionItemChange(index, 'instructions', '');
                              } else {
                                setCustomInstructionsItems((prev) => {
                                  const next = { ...prev };
                                  delete next[index];
                                  return next;
                                });
                                handlePrescriptionItemChange(index, 'instructions', val);
                              }
                            }}
                            className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 outline-none focus:border-[#143E78] transition-all appearance-none"
                          >
                            <option value="">Select instructions</option>
                            {INSTRUCTIONS_OPTIONS.filter((f) => f.value !== 'other').map((f) => (
                              <option key={f.value} value={f.value}>{f.label}</option>
                            ))}
                            <option value="other">Other (custom)</option>
                          </select>
                          {customInstructionsItems[index] && (
                            <input
                              type="text"
                              value={item.instructions}
                              onChange={(e) => handlePrescriptionItemChange(index, 'instructions', e.target.value)}
                              placeholder="Enter custom instructions"
                              className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] transition-all mt-2"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-black text-slate-500 uppercase tracking-widest mb-2">Investigations</label>
                <div className="relative">
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                      <input
                        type="text"
                        value={investigationSearch}
                        onChange={(e) => setInvestigationSearch(e.target.value)}
                        onFocus={() => setShowInvestigationSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowInvestigationSuggestions(false), 150)}
                        placeholder="Search investigations..."
                        className="w-full pl-10 pr-4 py-2.5 bg-white border-2 border-slate-200 rounded-xl text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all"
                      />
                      <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    </div>
                  </div>

                  {showInvestigationSuggestions && filteredInvestigations.length > 0 && (
                    <div className="absolute z-20 w-full mt-2 bg-white border-2 border-slate-200 rounded-2xl shadow-xl shadow-slate-200/50 max-h-72 overflow-y-auto">
                      {Object.entries(groupedInvestigations).map(([key, group]) => (
                        <div key={key}>
                          <div className="px-4 py-2 bg-slate-50 text-[10px] font-black text-slate-500 uppercase tracking-widest sticky top-0 border-b border-slate-100">
                            {group.icon} {group.label}
                          </div>
                          {group.items.map((item) => (
                            <button
                              key={item.id}
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => addInvestigation(item)}
                              className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[#143E78]/5 transition-colors text-left"
                            >
                              <span className="text-xs font-bold text-slate-900">{item.label}</span>
                              <span className="text-[10px] font-black text-[#143E78] bg-[#143E78]/10 px-2 py-0.5 rounded-lg">{item.category}</span>
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                  {showInvestigationSuggestions && filteredInvestigations.length === 0 && investigationSearch.trim() && (
                    <div className="absolute z-20 w-full mt-2 bg-white border-2 border-slate-200 rounded-2xl shadow-xl shadow-slate-200/50 px-4 py-3">
                      <p className="text-xs font-bold text-slate-400">No investigations found</p>
                    </div>
                  )}
                </div>

                {prescriptionForm.investigations.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {prescriptionForm.investigations.map((inv, index) => (
                      <div
                        key={inv.id || index}
                        className="inline-flex items-center gap-2 px-3 py-2 bg-[#143E78]/5 border-2 border-[#143E78]/20 rounded-xl"
                      >
                        <span className="text-sm">{inv.icon}</span>
                        <div>
                          <p className="text-[11px] font-black text-[#143E78]">{inv.label}</p>
                          <p className="text-[10px] font-bold text-slate-500">{inv.category}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeInvestigation(index)}
                          className="inline-flex items-center justify-center w-5 h-5 text-rose-600 hover:text-rose-700 hover:bg-rose-50 rounded-md transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>


            </>
          )}

          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={() => { setShowPrescriptionModal(false); setEditingPrescription(null); }}
              className="px-5 py-2.5 border border-slate-200 rounded-2xl text-xs font-black text-slate-600 hover:bg-slate-50 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={savingPrescription}
              className="px-5 py-2.5 bg-[#143E78] text-white rounded-2xl text-xs font-black hover:bg-[#0f2f5e] disabled:opacity-50 transition-all shadow-lg shadow-[#143E78]/20 flex items-center gap-2"
            >
              {savingPrescription && <Loader2 className="w-4 h-4 animate-spin" />}
              {editingPrescription ? 'Update Prescription' : 'Create Prescription'}
            </button>
          </div>
        </form>
      ))}
      {renderPortalModal(showCompleteModal, () => setShowCompleteModal(false), 'Complete Appointment & Settle Doctor Payout', (
        <div className="space-y-5">
          <div className="bg-blue-50/70 border border-blue-100 rounded-2xl p-4 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#143E78] text-white flex items-center justify-center font-black shrink-0">
                <CheckCircle2 size={20} />
              </div>
              <div>
                <h4 className="text-sm font-black text-slate-900">Mark Consultation as Completed</h4>
                <p className="text-xs text-slate-500 font-medium">This will finalize the appointment and automatically calculate the doctor consultation payout.</p>
              </div>
            </div>

            {/* Doctor Payout Preview */}
            <div className="bg-white rounded-xl p-3.5 border border-blue-200/60 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500 font-bold">Attending Doctor:</span>
                <span className="text-slate-900 font-black">
                  {appointment?.appointmentDoctorId?.user ? `Dr. ${appointment.appointmentDoctorId.user.firstName || ''} ${appointment.appointmentDoctorId.user.lastName || ''}`.trim() : (appointment?.doctorName || 'Assigned Doctor')}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500 font-bold">Consultation Rate (Gross):</span>
                <span className="text-slate-900 font-black">
                  ₹{Number(appointment?.doctorFee || appointment?.finalPrice || appointment?.appointmentDoctorId?.financials?.consultationFee || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500 font-bold">TDS Deduction (10%):</span>
                <span className="text-rose-600 font-bold">
                  − ₹{(Math.round(Number(appointment?.doctorFee || appointment?.finalPrice || appointment?.appointmentDoctorId?.financials?.consultationFee || 0) * 0.10)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="border-t border-slate-100 pt-2 flex items-center justify-between">
                <span className="text-xs font-black text-slate-800">Net Doctor Payout:</span>
                <span className="text-sm font-black text-emerald-600">
                  ₹{(Math.round(Number(appointment?.doctorFee || appointment?.finalPrice || appointment?.appointmentDoctorId?.financials?.consultationFee || 0) * 0.90)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={() => setShowCompleteModal(false)}
              className="flex-1 px-4 py-3 border border-slate-200 rounded-2xl text-xs font-black text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleComplete}
              disabled={updating}
              className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-600/20 disabled:opacity-50 cursor-pointer"
            >
              {updating ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
              <span>{updating ? 'Completing...' : 'Complete & Settle Payout'}</span>
            </button>
          </div>
        </div>
      ))}

      {renderPortalModal(showDeleteModal, () => setShowDeleteModal(false), 'Delete Appointment', (
        <div className="space-y-5">
          <p className="text-sm text-slate-600 font-medium leading-relaxed">This action cannot be undone. Delete this appointment?</p>
          <div className="flex gap-3">
            <button onClick={() => setShowDeleteModal(false)} className="flex-1 px-4 py-3 border border-slate-200 rounded-2xl text-xs font-black text-slate-600 hover:bg-slate-50 transition-colors">Keep</button>
            <button onClick={handleDelete} disabled={deleting} className="flex-1 px-4 py-3 bg-rose-600 text-white rounded-2xl text-xs font-black hover:bg-rose-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      ))}

      {renderPortalModal(showRestoreModal, () => { setShowRestoreModal(false); setRestoreNotes(''); }, 'Restore Appointment', (
        <div className="space-y-5">
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
              <FileText size={14} className="text-[#143E78]" />
              Restore Reason
            </label>
            <textarea
              value={restoreNotes}
              onChange={(e) => setRestoreNotes(e.target.value)}
              placeholder="Why is this appointment being restored?..."
              rows={3}
              className="w-full px-4 py-3.5 bg-slate-50 border-2 border-slate-200 rounded-2xl font-bold text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all resize-none"
            />
          </div>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">
            This will restore the appointment from '{appointment?.status?.replace('_', ' ')}' to 'Confirmed' status. The appointment will become active again.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => { setShowRestoreModal(false); setRestoreNotes(''); }}
              className="flex-1 px-4 py-3 border border-slate-200 rounded-2xl text-xs font-black text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleRestore}
              disabled={restoring}
              className="flex-1 px-4 py-3 bg-amber-600 text-white rounded-2xl text-xs font-black hover:bg-amber-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              {restoring && <Loader2 size={16} className="animate-spin" />}
              Restore
            </button>
          </div>
        </div>
      ))}

      {renderPortalModal(showEditAppointmentModal, () => setShowEditAppointmentModal(false), 'Edit Appointment', (
        <div className="space-y-5">
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
              <Calendar size={14} className="text-[#143E78]" />
              Appointment Date
            </label>
            <input
              type="date"
              value={editAppointmentForm.appointmentDate}
              onChange={(e) => setEditAppointmentForm(prev => ({ ...prev, appointmentDate: e.target.value }))}
              className="w-full px-4 py-3.5 bg-white border-2 border-slate-200 rounded-2xl font-bold text-sm focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all outline-none"
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                <Clock size={14} className="text-[#143E78]" />
                Appointment Time
              </label>
              {editAppointmentForm.appointmentTimeSlot && (
                <span className="px-2 py-0.5 bg-[#143E78]/10 text-[#143E78] rounded-md text-[10px] font-black tracking-wide font-mono">
                  {formatTime12Hour(editAppointmentForm.appointmentTimeSlot)}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  type="time"
                  value={editAppointmentForm.appointmentTimeSlot}
                  onChange={(e) => setEditAppointmentForm(prev => ({ ...prev, appointmentTimeSlot: e.target.value }))}
                  className="w-full px-4 py-3.5 bg-white border-2 border-slate-200 rounded-2xl font-bold text-sm focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all outline-none"
                />
              </div>
              <div className="flex items-center p-1 bg-slate-100 rounded-2xl border-2 border-slate-200 shrink-0 h-[48px]">
                <button
                  type="button"
                  onClick={() => {
                    const current = getTimeParts(editAppointmentForm.appointmentTimeSlot);
                    let newH = current.hour24;
                    if (current.hour24 >= 12) newH = current.hour24 - 12;
                    const newTime = `${String(newH).padStart(2, '0')}:${current.minute}`;
                    setEditAppointmentForm(prev => ({ ...prev, appointmentTimeSlot: newTime }));
                  }}
                  className={`px-3.5 h-full rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer ${getTimeParts(editAppointmentForm.appointmentTimeSlot).period === 'AM'
                    ? 'bg-[#143E78] text-white shadow-sm'
                    : 'text-slate-500 hover:text-slate-900 hover:bg-slate-200/70'
                    }`}
                >
                  AM
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const current = getTimeParts(editAppointmentForm.appointmentTimeSlot);
                    let newH = current.hour24;
                    if (current.hour24 < 12) newH = current.hour24 + 12;
                    const newTime = `${String(newH).padStart(2, '0')}:${current.minute}`;
                    setEditAppointmentForm(prev => ({ ...prev, appointmentTimeSlot: newTime }));
                  }}
                  className={`px-3.5 h-full rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer ${getTimeParts(editAppointmentForm.appointmentTimeSlot).period === 'PM'
                    ? 'bg-[#143E78] text-white shadow-sm'
                    : 'text-slate-500 hover:text-slate-900 hover:bg-slate-200/70'
                    }`}
                >
                  PM
                </button>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
              <Stethoscope size={14} className="text-[#143E78]" />
              Assigned Doctor
            </label>
            <div className="relative">
              <select
                value={editAppointmentForm.appointmentDoctorId}
                onChange={(e) => setEditAppointmentForm(prev => ({ ...prev, appointmentDoctorId: e.target.value }))}
                className="w-full px-4 py-3.5 bg-white border-2 border-slate-200 rounded-2xl font-bold text-sm focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all outline-none appearance-none"
              >
                <option value="">-- No doctor assigned --</option>
                {doctors.map(doc => (
                  <option key={doc._id} value={doc._id}>
                    Dr. {doc.user?.firstName} {doc.user?.lastName} | {doc.user?.phone || 'No phone'} | {doc.specialization || 'General'}
                  </option>
                ))}
              </select>
              <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 rotate-90 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
              <FileText size={14} className="text-[#143E78]" />
              Notes
            </label>
            <textarea
              value={editAppointmentForm.notes}
              onChange={(e) => setEditAppointmentForm(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Additional notes..."
              rows={3}
              className="w-full px-4 py-3.5 bg-white border-2 border-slate-200 rounded-2xl font-bold text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all resize-none"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setShowEditAppointmentModal(false)}
              className="flex-1 px-4 py-3 border border-slate-200 rounded-2xl text-xs font-black text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateAppointment}
              disabled={updatingAppointment}
              className="flex-1 px-4 py-3 bg-[#143E78] text-white rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-[#0f2c56] disabled:opacity-50 transition-all flex items-center justify-center gap-2"
            >
              {updatingAppointment && <Loader2 size={16} className="animate-spin" />}
              <Save size={16} /> Save Changes
            </button>
          </div>
        </div>
      ))}

      {renderPortalModal(showPrescriptionDeleteModal, () => setShowPrescriptionDeleteModal(false), 'Delete Prescription', (
        <div className="space-y-5">
          <p className="text-sm text-slate-600 font-medium leading-relaxed">This action cannot be undone. Delete this prescription?</p>
          <div className="flex gap-3">
            <button onClick={() => setShowPrescriptionDeleteModal(false)} className="flex-1 px-4 py-3 border border-slate-200 rounded-2xl text-xs font-black text-slate-600 hover:bg-slate-50 transition-colors">Keep</button>
            <button onClick={handleDeletePrescription} disabled={deleting} className="flex-1 px-4 py-3 bg-rose-600 text-white rounded-2xl text-xs font-black hover:bg-rose-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      ))}

      {renderPortalModal(showPrescriptionActionModal, () => { setShowPrescriptionActionModal(false); setPrescriptionActionNotes(''); setPrescriptionRejectionReason(''); }, prescriptionActionType === 'sendToLead' ? 'Send Prescription to Lead' : prescriptionActionType === 'submit' ? 'Submit for Verification' : prescriptionActionType === 'approve' ? 'Verify Prescription' : 'Reject Prescription', (
        <div className="space-y-5">
          {prescriptionActionType === 'reject' ? (
            <div>
              <label className="block text-[11px] font-black text-slate-500 uppercase tracking-widest mb-2">Rejection Reason *</label>
              <textarea
                value={prescriptionRejectionReason}
                onChange={(e) => setPrescriptionRejectionReason(e.target.value)}
                rows={3}
                className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-2xl text-sm font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all"
                placeholder="Provide a reason for rejection..."
              />
            </div>
          ) : (
            <div>
              <label className="block text-[11px] font-black text-slate-500 uppercase tracking-widest mb-2">Notes</label>
              <textarea
                value={prescriptionActionNotes}
                onChange={(e) => setPrescriptionActionNotes(e.target.value)}
                rows={3}
                className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-2xl text-sm font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all"
                placeholder="Add notes..."
              />
            </div>
          )}
          <div className="flex gap-3 justify-end pt-2">
            <button
              onClick={() => { setShowPrescriptionActionModal(false); setPrescriptionActionNotes(''); setPrescriptionRejectionReason(''); }}
              className="px-5 py-2.5 border border-slate-200 rounded-2xl text-xs font-black text-slate-600 hover:bg-slate-50 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handlePrescriptionAction}
              disabled={prescriptionActionLoading || (prescriptionActionType === 'reject' && !prescriptionRejectionReason.trim())}
              className="px-5 py-2.5 bg-[#143E78] text-white rounded-2xl text-xs font-black hover:bg-[#0f2f5e] disabled:opacity-50 transition-all shadow-lg shadow-[#143E78]/20 flex items-center gap-2"
            >
              {prescriptionActionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {prescriptionActionType === 'sendToLead' ? 'Send to Lead' : prescriptionActionType === 'submit' ? 'Submit for Verification' : prescriptionActionType === 'approve' ? 'Verify' : 'Reject'}
            </button>
          </div>
        </div>
      ))}

      {renderPortalModal(showManualPayoutModal, () => setShowManualPayoutModal(false), appointmentPayouts.length > 0 ? 'Adjust / Update Doctor Payout' : 'Process Manual Payout', (
        <div className="space-y-5">
          <div className="bg-slate-50 rounded-2xl p-4 space-y-2 border border-slate-200">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Appointment ID</span>
              <span className="text-xs font-bold text-slate-900">{appointment?.bookingId || appointment?._id}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Patient</span>
              <span className="text-xs font-bold text-slate-900">{appointment?.leadId?.patientName || appointment?.patientName || '—'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Doctor</span>
              <span className="text-xs font-bold text-slate-900">{appointment?.appointmentDoctorId?.user ? `Dr. ${appointment.appointmentDoctorId.user.firstName || ''} ${appointment.appointmentDoctorId.user.lastName || ''}`.trim() : '—'}</span>
            </div>
          </div>

          <div className="space-y-3">
            <label className="block text-[11px] font-black text-slate-500 uppercase tracking-widest">Gross Amount (₹) <span className="text-rose-500">*</span></label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={manualPayoutForm.grossAmount}
              onChange={(e) => handleManualPayoutFieldChange('grossAmount', e.target.value)}
              className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-2xl text-sm font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all"
              placeholder="Enter gross amount"
            />
          </div>

          <div className="space-y-3">
            <label className="block text-[11px] font-black text-slate-500 uppercase tracking-widest">TDS Percentage (%) <span className="text-rose-500">*</span></label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={manualPayoutForm.tdsPercentage}
              onChange={(e) => handleManualPayoutFieldChange('tdsPercentage', e.target.value)}
              className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-2xl text-sm font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all"
              placeholder="Enter TDS percentage"
            />
          </div>

          <div className="bg-emerald-50 border-2 border-emerald-100 rounded-2xl p-4 space-y-2">
            <p className="text-[10px] font-black text-emerald-700 uppercase tracking-widest">Calculation Preview</p>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-600">Gross:</span>
              <span className="text-xs font-black text-slate-900">₹{Number(manualPayoutForm.grossAmount || 0).toLocaleString('en-IN')}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-600">TDS ({Number(manualPayoutForm.tdsPercentage || 0).toLocaleString('en-IN')}%):</span>
              <span className="text-xs font-black text-rose-600">− ₹{((Number(manualPayoutForm.grossAmount || 0) * Number(manualPayoutForm.tdsPercentage || 0)) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div className="border-t border-emerald-200 pt-2 flex items-center justify-between">
              <span className="text-xs font-black text-slate-900">Net Payout:</span>
              <span className="text-sm font-black text-emerald-700">₹{Number(manualPayoutForm.netAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          </div>

          <div className="space-y-3">
            <label className="block text-[11px] font-black text-slate-500 uppercase tracking-widest">Transaction ID</label>
            <input
              type="text"
              value={manualPayoutForm.transactionId}
              onChange={(e) => handleManualPayoutFieldChange('transactionId', e.target.value)}
              className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-2xl text-sm font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all"
              placeholder="Optional transaction reference"
            />
          </div>

          <div className="space-y-3">
            <label className="block text-[11px] font-black text-slate-500 uppercase tracking-widest">Payout Date</label>
            <input
              type="date"
              value={manualPayoutForm.transactionDate}
              onChange={(e) => handleManualPayoutFieldChange('transactionDate', e.target.value)}
              className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-2xl text-sm font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all"
            />
          </div>

          <div className="space-y-3">
            <label className="block text-[11px] font-black text-slate-500 uppercase tracking-widest">Notes</label>
            <textarea
              value={manualPayoutForm.notes}
              onChange={(e) => handleManualPayoutFieldChange('notes', e.target.value)}
              rows={3}
              className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-2xl text-sm font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all"
              placeholder="Optional notes..."
            />
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <button
              onClick={() => setShowManualPayoutModal(false)}
              className="px-5 py-2.5 border border-slate-200 rounded-2xl text-xs font-black text-slate-600 hover:bg-slate-50 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateManualPayout}
              disabled={creatingManualPayout || !manualPayoutForm.grossAmount}
              className="px-5 py-2.5 bg-emerald-600 text-white rounded-2xl text-xs font-black hover:bg-emerald-700 disabled:opacity-50 transition-all shadow-lg shadow-emerald-200 flex items-center gap-2"
            >
              {creatingManualPayout && <Loader2 className="w-4 h-4 animate-spin" />}
              {creatingManualPayout ? 'Saving...' : (appointmentPayouts.length > 0 ? 'Save & Update Payout' : 'Create Payout')}
            </button>
          </div>
        </div>
      ))}

      {renderPortalModal(
        showPaymentLinkModal,
        () => setShowPaymentLinkModal(false),
        appointment?.razorpayDetails?.paymentLinkUrl ? 'Resend Payment Link' : 'Generate Payment Link',
        (
          <div className="space-y-4">
            <p className="text-xs text-slate-600 font-medium leading-relaxed">
              {appointment?.razorpayDetails?.paymentLinkUrl ? (
                <>Generate a replacement payment link for patient <strong>{appointment?.patientName}</strong>. If the link expired or was lost, you can update the email address below before re-sending.</>
              ) : (
                <>Generate a secure online payment link for patient <strong>{appointment?.patientName}</strong> to pay the consultation fee of <strong>₹{(appointment?.finalPrice || 0).toLocaleString('en-IN')}</strong>.</>
              )}
            </p>
            <div className="space-y-2">
              <label className="block text-[11px] font-black text-slate-500 uppercase tracking-widest">
                Patient Email Address <span className="text-rose-500">*</span>
              </label>
              <input
                type="email"
                value={paymentLinkEmail}
                onChange={(e) => setPaymentLinkEmail(e.target.value)}
                placeholder="e.g. patient@example.com"
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-2xl text-sm font-bold text-slate-900 outline-none focus:border-[#143E78] transition-colors"
              />
            </div>
            {appointment?.razorpayDetails?.paymentLinkUrl ? (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3.5 text-xs text-amber-800 font-bold space-y-1">
                <p className="font-black text-[11px] uppercase tracking-wider text-amber-900 flex items-center gap-1.5">
                  <AlertCircle size={14} /> Re-sending Payment Link
                </p>
                <p className="text-[11px]">
                  Generating a new link will invalidate any previous link and automatically send notifications to this updated email.
                </p>
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-3.5 text-xs text-blue-800 font-bold space-y-1">
                <p className="font-black text-[11px] uppercase tracking-wider text-blue-900 flex items-center gap-1.5">
                  <CreditCard size={14} /> Instant Payment Gateway Link
                </p>
                <p className="text-[11px]">
                  A secure Razorpay payment link will be sent to the patient's email and displayed on this dashboard for immediate copying.
                </p>
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowPaymentLinkModal(false)}
                className="flex-1 px-4 py-3 border border-slate-200 rounded-2xl text-xs font-black text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Dismiss
              </button>
              <button
                onClick={handleGenerateOrResendPaymentLink}
                disabled={generatingPaymentLink || !paymentLinkEmail.trim()}
                className="flex-1 px-4 py-3 bg-[#143E78] text-white rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-[#0f2c56] disabled:opacity-50 transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-900/20"
              >
                {generatingPaymentLink ? <Loader2 className="w-4 h-4 animate-spin" /> : <SendHorizontal className="w-4 h-4" />}
                {generatingPaymentLink ? 'Generating...' : (appointment?.razorpayDetails?.paymentLinkUrl ? 'Resend Payment Link' : 'Generate & Send Link')}
              </button>
            </div>
          </div>
        )
      )}

      {/* 📞 QUICK CALL FOLLOW-UP STATUS MODAL */}
      {showQuickDispModal && latestCall && createPortal(
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100000] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border border-slate-200 animate-zoomIn">
            <div className="bg-violet-700 p-5 text-white flex justify-between items-center shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                  <PhoneCall className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-base font-bold">Update Follow-up Status</h2>
                  <p className="text-[11px] text-violet-200">{patientName} • {patientPhone}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowQuickDispModal(false)}
                className="p-1.5 hover:bg-white/20 rounded-lg text-white/80 hover:text-white transition-all cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-slate-500">Exotel Telephony Status:</span>
                  <span className="font-bold text-slate-700">{getLightStatusBadge(latestCall.status).label}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-slate-500">Call Time:</span>
                  <span className="text-slate-600">
                    {new Date(latestCall.createdAt || latestCall.startTime).toLocaleString('en-IN', {
                      day: '2-digit', month: 'short', year: 'numeric',
                      hour: '2-digit', minute: '2-digit', hour12: true
                    })}
                  </span>
                </div>
              </div>

              <form onSubmit={handleSaveQuickDisp} className="space-y-4">
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">
                    Follow-up Status <span className="text-rose-500">*</span>
                  </label>
                  <select
                    value={quickDispForm.disposition}
                    onChange={e => setQuickDispForm({ ...quickDispForm, disposition: e.target.value })}
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-xl text-xs font-semibold bg-white text-slate-800 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                    required
                  >
                    <option value="callback_completed">🟢 Callback Completed / Resolved</option>
                    <option value="follow_up_needed">🔵 Follow-up Needed (Call Later)</option>
                    <option value="appointment_booked">🟣 Appointment Booked</option>
                    <option value="not_interested">⚪ Not looking for any Treatment</option>
                    <option value="invalid_number">🔴 Invalid Number</option>
                    <option value="pending">🟡 Pending Callback</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">
                    Call Reason / Inquiry
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Consultation query, pricing details"
                    value={quickDispForm.callReason}
                    onChange={e => setQuickDispForm({ ...quickDispForm, callReason: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs bg-white text-slate-800 outline-none focus:border-violet-500"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">
                    Follow-up Notes & Remarks
                  </label>
                  <textarea
                    rows={3}
                    placeholder="e.g. Spoke to patient. Confirmed appointment details."
                    value={quickDispForm.dispositionNotes}
                    onChange={e => setQuickDispForm({ ...quickDispForm, dispositionNotes: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs bg-white text-slate-800 outline-none focus:border-violet-500 resize-none"
                  />
                </div>

                <div className="flex gap-2 pt-2 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setShowQuickDispModal(false)}
                    className="flex-1 py-2.5 border border-slate-300 text-slate-700 rounded-xl text-xs font-bold hover:bg-slate-50 transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSavingQuickDisp}
                    className="flex-1 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-xl text-xs font-bold transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-1.5 shadow-md shadow-violet-200"
                  >
                    {isSavingQuickDisp ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    <span>{isSavingQuickDisp ? 'Saving…' : 'Save Status'}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ✏️ EDIT PATIENT DETAILS MODAL */}
      <EditPatientModal
        isOpen={showEditPatientModal}
        onClose={() => setShowEditPatientModal(false)}
        leadId={targetLeadId}
        initialData={{
          patientName,
          patientPhone,
          patientEmail: appointment?.leadId?.patientEmail || patientEmail,
          patientAge,
          patientGender,
          bloodGroup,
          dateOfBirth: appointment?.leadId?.dateOfBirth,
          emergencyContact: appointment?.leadId?.emergencyContact,
          abhaId: appointment?.leadId?.abhaId,
          city: appointment?.leadId?.city,
          patientAddress: appointment?.leadId?.patientAddress,
          treatmentRequired: appointment?.leadId?.treatmentRequired || appointment?.medicalReason,
          disease: appointment?.leadId?.disease,
          coverageMode: appointment?.leadId?.coverageMode
        }}
        onSuccess={() => {
          fetchAppointment();
        }}
      />
    </div>
  );
};

export default AppointmentDetail;
